import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import CariDegisiklikGecmisi from '../components/CariDegisiklikGecmisi';
import axiosInstance from '../utils/axiosConfig';
import { isAxiosError } from 'axios';
import { Button, Card, Row, Col, Modal, Form } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';
import { FileUp, StickyNote, Edit, User, Phone, Mail, MapPin, Building2, FileText, File, FileImage, Download, Trash2, Eye, X, Check, MonitorPlay } from 'lucide-react';
import TrDateInput from '../components/TrDateInput';
import IysIzinModal from '../components/IysIzinModal';
import IysKayitListesi, { IysIzinDetay } from '../components/IysKayitListesi';
import IysKilitliAlan from '../components/IysKilitliAlan';
import KayitTakipButonu from '../components/takip/KayitTakipButonu';
import KayitRandevuButonu from '../components/randevu/KayitRandevuButonu';
import { IlgiTalepButonu } from '../components/ilgi/IlgiTalepleri';

interface Not {
    notID: number;
    eklenmeTarihi: string;
    aciklama: string;
    ekleyenID: number;
    ekleyen: {
        kullaniciID: number;
        adSoyad: string;
    };
    aktifMi: boolean;
    takipNo: string;
}

interface Dosya {
    dosyaID: number;
    takipNo: string;
    eklenmeTarihi: string;
    aktifMi: boolean;
    dosyaAdi: string;
    aciklama: string;
}

interface CariPlan {
    cariOdemePlaniID: number;
    cariID: number;
    vadeTarihi: string;
    miktar: number;
    odemeTuru: string;
    aciklama: string | null;
    odenmeDurumu: boolean;
    eklemeTarihi: string;
    aktifMi: boolean;
    gercekOdemeID: number | null;
}

interface Odeme {
    odemeID: number;
    cariID: number;
    islemTarihi: string;
    vadeTarihi: string | null;
    tahsilatTarihi: string | null;
    miktar: number;
    paraBirimiID: number;
    islemTakipNo: string;
    odemeTuru: string;
    odemeYonu: string;
    odemeDurumu: string;
    islemTipi?: string;
    aciklama: string | null;
    hizmetUrunAdi: string | null;
    aktifMi: boolean;
    sozlesmeOdemesi: boolean;
    eklemeTarihi: string;
    projeID: number | null;
    kasaID?: number;
    kasa?: {
        kasaID: number;
        kasaAdi: string;
        kasaTuru: string;
    };
    paraBirimi: {
        paraBirimiID: number;
        birim: string;
        sembol: string;
    };
    ilgiliProje?: {
        projeID: number;
        projeAdi: string;
    } | null;
}

interface Proje {
    projeID: number;
    projeAdi: string;
}

interface NotForm {
    aciklama: string;
}

interface Cari {
    cariID: number;
    takipNo: string;
    eklenmeTarihi: string;
    adiSoyadi: string;
    firmaAdi: string | null;
    telefon1: string;
    telefon2: string | null;
    ePosta: string | null;
    adres: string | null;
    tc: string | null;
    cariVD: string | null;
    cariVN: string | null;
    turu: string;
    turu2: string | null;
    aktifMi: boolean;
    bakiye: number;
}

interface CariForm {
    adiSoyadi: string;
    firmaAdi: string | null;
    telefon1: string;
    telefon2: string | null;
    ePosta: string | null;
    adres: string | null;
    tc: string | null;
    cariVD: string | null;
    cariVN: string | null;
    turu: string;
    turu2: string | null;
    aktifMi: boolean;
}

interface CariAltKategori {
    cariAltKategoriID: number;
    turu: string;
    altKategoriAdi: string;
    aktif: boolean;
}

interface Sozlesme {
    sozlesmeID: number;
    sozlesmeTakipNo: string;
    sozlesmeTarihi: string;
    sozlesmeDurumu: string;
    sozlesmeFiyati: number;
    aktifMi: boolean;
    daire: {
        daireID: number;
        daireNo: string;
        blok?: {
            blokAdi: string;
            proje?: {
                projeID: number;
                projeAdi: string;
            };
        };
    };
}

interface KiraSozlesmeOzet {
    kiraSozlesmeID: number;
    takipNo: string;
    baslangicTarihi: string;
    bitisTarihi: string;
    kiraBedeli: number;
    aktifMi: boolean;
    gayrimenkulBaslik: string | null;
}

interface EmlakSunumOzet {
    sunumID: number;
    takipNo: string;
    baslik: string | null;
    durum: string | null;
    olusturmaTarihi: string;
    aktifMi: boolean;
}

interface AcikFatura {
    tip: 'odeme' | 'sozlesme';
    id: number;
    aciklama: string;
    islemTarihi: string;
    vadeTarihi?: string | null;
    odemeYonu: string;
    tutarToplam: number;
    odenenTutar: number;
    kalanTutar: number;
    durum: string;
}

const getInitials = (name: string): string =>
    name.split(' ').slice(0, 2).map(n => n[0]).join('').toUpperCase();

const AVATAR_STYLES = [
    { background: '#e0e7ff', color: '#4338ca' },
    { background: '#ede9fe', color: '#6d28d9' },
    { background: '#dbeafe', color: '#1d4ed8' },
    { background: '#ccfbf1', color: '#0f766e' },
    { background: '#fef3c7', color: '#b45309' },
    { background: '#ffe4e6', color: '#be123c' },
];

const getAvatarStyle = (id: number) => AVATAR_STYLES[id % AVATAR_STYLES.length];

const formatPhoneNumber = (phone: string): string => {
    const numbers = phone.replace(/\D/g, '');
    if (numbers.length === 10) {
        return `(${numbers.slice(0, 3)})${numbers.slice(3, 6)}-${numbers.slice(6, 8)}-${numbers.slice(8)}`;
    }
    return phone;
};

const unformatPhoneNumber = (phone: string): string => {
    return phone.replace(/\D/g, '');
};

const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('tr-TR');
};

const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('tr-TR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(amount);
};

// UTF-8'in Latin-1 olarak okunmasından kaynaklanan mojibake karakterleri düzeltir
const fixMojibake = (str: string | null | undefined): string | null => {
    if (!str) return str ?? null;
    if (str.split('').some(c => c.charCodeAt(0) > 255)) return str;
    try {
        const bytes = new Uint8Array(str.length);
        for (let i = 0; i < str.length; i++) bytes[i] = str.charCodeAt(i) & 0xFF;
        const decoded = new TextDecoder('utf-8', { fatal: true }).decode(bytes);
        if (decoded.length < str.length) return decoded;
        return str;
    } catch { return str; }
};

const formatMoneyInput = (value: string): string => {
    const numbers = value.replace(/[^\d]/g, '');
    if (numbers === '') return '';
    return numbers.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
};

const parseMoneyInput = (value: string): string => {
    return value.replace(/\./g, '');
};

const getCurrentDate = (): string => {
    return new Date().toISOString().split('T')[0];
};

// gg.aa.yyyy <-> yyyy-mm-dd dönüşüm yardımcıları
const toTRDate = (iso: string): string => {
    if (!iso) return '';
    const [y, m, d] = iso.split('-');
    return `${d}.${m}.${y}`;
};
const fromTRDate = (tr: string): string => {
    if (!tr || tr.length < 10) return '';
    const [d, m, y] = tr.split('.');
    return `${y}-${m}-${d}`;
};
const maskTRDate = (raw: string): string => {
    const digits = raw.replace(/\D/g, '').slice(0, 8);
    if (digits.length <= 2) return digits;
    if (digits.length <= 4) return `${digits.slice(0, 2)}.${digits.slice(2)}`;
    return `${digits.slice(0, 2)}.${digits.slice(2, 4)}.${digits.slice(4)}`;
};

const CariDetay: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const [showDegisiklikGecmisi, setShowDegisiklikGecmisi] = useState(false);
    const navigate = useNavigate();
    const { user } = useAuth();
    const { t } = useTranslation();
    const [cari, setCari] = useState<Cari | null>(null);
    const [showModal, setShowModal] = useState(false);
    const [showNotModal, setShowNotModal] = useState(false);
    const [showIslemlerMenu, setShowIslemlerMenu] = useState(false);
    const [showTopluPlanlaModal, setShowTopluPlanlaModal] = useState(false);
    const [showTekliOdemeModal, setShowTekliOdemeModal] = useState(false);
    const [tekliOdemeFormData, setTekliOdemeFormData] = useState({
        tutar: '',
        vadeTarihi: '',
        odemeTuru: 'Nakit',
        odemeDurumu: 'Bekliyor',
        kasaID: '',
        aciklama: ''
    });
    const [topluPlanlaFormData, setTopluPlanlaFormData] = useState({
        toplamTutar: '',
        baslamaTarihi: '',
        taksitSayisi: '',
        aralikAy: '1',
        odemeTuru: 'Nakit',
        odemeYonu: 'Odeme',
        aciklama: '',
        projeID: ''
    });
    const [selectedOdemeler, setSelectedOdemeler] = useState<Set<number>>(new Set());
    const [topluSilmeLoading, setTopluSilmeLoading] = useState(false);
    const [showDosyaModal, setShowDosyaModal] = useState(false);
    const [showPreviewModal, setShowPreviewModal] = useState(false);
    const [previewFile, setPreviewFile] = useState<{url: string, type: string, name: string} | null>(null);
    const [showAyrintilarModal, setShowAyrintilarModal] = useState(false);
    const [showOdemeModal, setShowOdemeModal] = useState(false);
    const [showEditOdemeModal, setShowEditOdemeModal] = useState(false);
    const [notlar, setNotlar] = useState<Not[]>([]);
    const [dosyalar, setDosyalar] = useState<Dosya[]>([]);
    const [odemeler, setOdemeler] = useState<Odeme[]>([]);
    const [cariOdemePlanlari, setCariOdemePlanlari] = useState<CariPlan[]>([]);
    const [ekstreOzet, setEkstreOzet] = useState<{ toplamBorc: number; toplamAlacak: number; bakiye: number; baYonu: string; bakiyeEtiketi: string } | null>(null);
    const [kasalar, setKasalar] = useState<any[]>([]);
    const [activeHareketTab, setActiveHareketTab] = useState<'ekstre' | 'plan'>('ekstre');
    const [activeMainTab, setActiveMainTab] = useState<'ekstre' | 'plan' | 'sozlesmeler' | 'sunumlar' | 'aktiviteler' | 'dosyalar'>('ekstre');
    // Müşteri sunumları (Projeler > Müşteri Sunumları kayıtları)
    const [musteriSunumlari, setMusteriSunumlari] = useState<any[]>([]);
    const [sunumlarLoading, setSunumlarLoading] = useState(false);
    const [sunumOlusturuluyor, setSunumOlusturuluyor] = useState(false);
    const [selectedTaksitler, setSelectedTaksitler] = useState<Set<number>>(new Set());
    const [taksitTopluSilLoading, setTaksitTopluSilLoading] = useState(false);
    const [showOdemeYapModal, setShowOdemeYapModal] = useState(false);
    const [odemeYapTaksit, setOdemeYapTaksit] = useState<CariPlan | null>(null);
    const [odemeYapFormData, setOdemeYapFormData] = useState({ tarih: '', kasaID: '' });
    const [odemeYapTarihDisplay, setOdemeYapTarihDisplay] = useState('');
    const [showEditCariPlanModal, setShowEditCariPlanModal] = useState(false);
    const [editingCariPlan, setEditingCariPlan] = useState<CariPlan | null>(null);
    const [editCariPlanFormData, setEditCariPlanFormData] = useState({ miktar: '', vadeTarihi: '', aciklama: '' });

    // Bağımsız makbuz + allocation state'leri
    const [showMakbuzModal, setShowMakbuzModal] = useState(false);
    const [makbuzStep, setMakbuzStep] = useState<1 | 2>(1);
    const [makbuzModu, setMakbuzModu] = useState<'tahsilat' | 'odeme'>('tahsilat');
    const [makbuzCDFormData, setMakbuzCDFormData] = useState({ miktar: '', tarih: '', kasaID: '', odemeTuru: 'Nakit', aciklama: '' });
    const [makbuzCDSaving, setMakbuzCDSaving] = useState(false);
    const [savedMakbuzID, setSavedMakbuzID] = useState<number | null>(null);
    const [acikFaturalar, setAcikFaturalar] = useState<AcikFatura[]>([]);
    const [eslesmeTutarlari, setEslesmeTutarlari] = useState<Record<string, string>>({});
    const [eslesmeSaving, setEslesmeSaving] = useState(false);
    const [hareketFilters, setHareketFilters] = useState({
        vadeTarihiBaslangic: '',
        vadeTarihiBitis: '',
        odemeTuru: '',
        odemeDurumu: '',
    });
    const [projeler, setProjeler] = useState<Proje[]>([]);
    const [editingOdeme, setEditingOdeme] = useState<Odeme | null>(null);
    const [saving, setSaving] = useState(false);
    const [odemeFormData, setOdemeFormData] = useState({
        miktar: '',
        vadeTarihi: '',
        odemeTuru: 'Nakit',
        odemeYonu: 'Tahsilat',
        odemeDurumu: 'Bekliyor',
        aciklama: '',
        projeID: '',
        hizmetUrunAdi: '',
        kasaID: ''
    });
    const [editOdemeFormData, setEditOdemeFormData] = useState({
        miktar: '',
        vadeTarihi: '',
        tahsilatTarihi: '',
        odemeDurumu: 'Bekliyor',
        aciklama: '',
        hizmetUrunAdi: '',
        kasaID: ''
    });
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [notFormData, setNotFormData] = useState<NotForm>({
        aciklama: ''
    });

    const [dosyaFormData, setDosyaFormData] = useState({
        dosya: null as File | null,
        aciklama: ''
    });

    const [formData, setFormData] = useState<CariForm>({
        adiSoyadi: '',
        firmaAdi: null,
        telefon1: '',
        telefon2: null,
        ePosta: null,
        adres: null,
        tc: null,
        cariVD: null,
        cariVN: null,
        turu: 'Cari',
        turu2: null,
        aktifMi: true
    });

    const [cariAltKategoriler, setCariAltKategoriler] = useState<CariAltKategori[]>([]);
    const [sozlesmeler, setSozlesmeler] = useState<Sozlesme[]>([]);
    const [relatedRecords, setRelatedRecords] = useState<{ kiraSozlesmeleri: KiraSozlesmeOzet[]; emlakSunumlari: EmlakSunumOzet[] } | null>(null);
    const [loadingRelated, setLoadingRelated] = useState(false);
    const [iysIzinler, setIysIzinler] = useState<IysIzinDetay[]>([]);
    const [iysAktarimAktif, setIysAktarimAktif] = useState(false);
    const [iysModal, setIysModal] = useState<{ iletisimTipi: 'TELEFON1' | 'TELEFON2' | 'EPOSTA'; iletisimVerisi: string } | null>(null);

    const fetchNotlar = async () => {
        if (cari?.takipNo) {
            try {
                const response = await axiosInstance.get(`/api/Notlar/takip/${cari.takipNo}`);
                setNotlar(response.data);
            } catch (error) {
                toast.error(t('msg.islemHatasi'));
            }
        }
    };

    const fetchDosyalar = async () => {
        if (cari?.takipNo && cari.takipNo !== '') {
            try {
                const response = await axiosInstance.get(`/api/Dosyalar/takip/${cari.takipNo}`, {
                    params: { dosyaTuru: 1 }
                });
                setDosyalar(response.data);
            } catch (error: unknown) {
                if (isAxiosError(error) && error.response?.status !== 404) {
                    toast.error(t('msg.islemHatasi'));
                }
            }
        } else {
            setDosyalar([]);
        }
    };

    const fetchCari = async () => {
        if (id === 'yeni') {
            setCari({
                cariID: 0,
                takipNo: '',
                eklenmeTarihi: new Date().toISOString(),
                adiSoyadi: '',
                firmaAdi: null,
                telefon1: '',
                telefon2: null,
                ePosta: null,
                adres: null,
                tc: null,
                cariVD: null,
                cariVN: null,
                turu: 'Cari',
                turu2: null,
                aktifMi: true,
                bakiye: 0
            });
            setFormData({
                adiSoyadi: '',
                firmaAdi: null,
                telefon1: '',
                telefon2: null,
                ePosta: null,
                adres: null,
                tc: null,
                cariVD: null,
                cariVN: null,
                turu: 'Cari',
                turu2: null,
                aktifMi: true
            });
            setShowModal(true);
        } else {
            try {
                const response = await axiosInstance.get(`/api/Cariler/${id}`);
                const fixedAdres = fixMojibake(response.data.adres);
                if (response.data.turu === 'Müşteri') {
                    navigate(`/musteriler/${id}`, { replace: true });
                    return;
                }
                setCari({ ...response.data, adres: fixedAdres });
                setFormData({
                    adiSoyadi: response.data.adiSoyadi,
                    firmaAdi: response.data.firmaAdi,
                    telefon1: response.data.telefon1,
                    telefon2: response.data.telefon2,
                    ePosta: response.data.ePosta,
                    adres: fixedAdres,
                    tc: response.data.tc,
                    cariVD: response.data.cariVD,
                    cariVN: response.data.cariVN,
                    turu: 'Cari',
                    turu2: response.data.turu2,
                    aktifMi: response.data.aktifMi
                });
            } catch (error) {
                toast.error(t('msg.islemHatasi'));
                navigate('/cariler');
            }
        }
    };

    useEffect(() => {
        fetchCari();
    }, [id]);

    useEffect(() => {
        if (cari?.takipNo) {
            fetchNotlar();
            fetchDosyalar();
            fetchOdemeler();
            fetchCariOdemePlani();
            fetchEkstreOzet();
        }
    }, [cari?.takipNo]);

    useEffect(() => {
        if (cari?.cariID && cari.cariID > 0) {
            setLoadingRelated(true);
            axiosInstance.get(`/api/Cariler/${cari.cariID}/related`)
                .then(res => setRelatedRecords(res.data))
                .catch(() => setRelatedRecords(null))
                .finally(() => setLoadingRelated(false));

            setSunumlarLoading(true);
            axiosInstance.get(`/api/MusteriSunumlari?cariID=${cari.cariID}&aktifMi=true`)
                .then(res => setMusteriSunumlari(Array.isArray(res.data) ? res.data : []))
                .catch(() => setMusteriSunumlari([]))
                .finally(() => setSunumlarLoading(false));

            axiosInstance.get(`/api/Sozlesmeler?cariID=${cari.cariID}&aktifMi=true`)
                .then(res => {
                    const data = Array.isArray(res.data) ? res.data : [];
                    setSozlesmeler(data);
                })
                .catch(() => setSozlesmeler([]));

            axiosInstance.get(`/api/CariIysIzinleri/cari/${cari.cariID}`).then(r => setIysIzinler(r.data)).catch(() => {});
            axiosInstance.get('/api/IysAktarimAyarlari/aktif').then(r => setIysAktarimAktif(r.data.aktif)).catch(() => {});
        }
    }, [cari?.cariID]);

    useEffect(() => {
        fetchProjeler();
        fetchKasalar();
        fetchCariAltKategoriler();
    }, []);

    const fetchCariAltKategoriler = async () => {
        try {
            const response = await axiosInstance.get('/api/CariAltKategorileri/aktif?turu=Cari');
            setCariAltKategoriler(response.data);
        } catch {
            // sessiz hata
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            const payload = { ...formData, turu: 'Cari' };
            if (id === 'yeni') {
                const response = await axiosInstance.post('/api/Cariler', payload);
                toast.success(t('msg.islemBasarili'));
                setShowModal(false);
                navigate(`/cariler/${response.data.cariID}`);
            } else {
                await axiosInstance.put(`/api/Cariler/${id}`, payload);
                toast.success(t('msg.islemBasarili'));
                fetchCari();
                setShowModal(false);
            }
        } catch (error: any) {
            const msg = error?.response?.data?.message;
            toast.error(msg || (id === 'yeni' ? 'Cari eklenirken bir hata oluştu' : 'Güncelleme sırasında bir hata oluştu'));
        }
    };

    const handleNotSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!cari?.takipNo || !user) {
            toast.error(t('msg.islemHatasi'));
            return;
        }
        try {
            await axiosInstance.post('/api/Notlar', {
                takipNo: cari.takipNo,
                aciklama: notFormData.aciklama,
                ekleyenID: user.kullaniciID,
                aktifMi: true
            });
            toast.success(t('msg.islemBasarili'));
            setNotFormData({ aciklama: '' });
            fetchNotlar();
        } catch (error) {
            toast.error(t('msg.islemHatasi'));
        }
    };

    const handleDosyaSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!dosyaFormData.dosya || !cari?.takipNo) return;
        try {
            const fd = new FormData();
            fd.append('file', dosyaFormData.dosya);  // Backend 'file' bekliyor
            fd.append('takipNo', cari.takipNo);
            fd.append('aciklama', dosyaFormData.aciklama);
            fd.append('dosyaTuru', '1');
            // Content-Type header'ını manuel ayarlama - Axios otomatik ekler
            await axiosInstance.post('/api/Dosyalar/upload', fd);
            toast.success(t('msg.islemBasarili'));
            setDosyaFormData({ dosya: null, aciklama: '' });
            if (fileInputRef.current) fileInputRef.current.value = '';
            fetchDosyalar();
            setShowDosyaModal(false);
        } catch (error) {
            console.error('Dosya yükleme hatası:', error);
            toast.error(t('msg.islemHatasi'));
        }
    };

    const odemeTuruLabel = (tur: string) => {
        const map: Record<string, string> = {
            '0': 'Nakit', 'Nakit': 'Nakit',
            '1': 'Banka Havale/EFT', 'BankaHavale': 'Banka Havale/EFT',
            '2': t('odeme.cek'), 'Cek': t('odeme.cek'),
            '3': 'Senet', 'Senet': 'Senet',
            '4': 'Takas', 'Takas': 'Takas',
            '5': 'Mahsup', 'Mahsup': 'Mahsup',
        };
        return map[tur] ?? tur;
    };

    const fetchOdemeler = async () => {
        if (cari?.cariID) {
            try {
                const response = await axiosInstance.get(`/api/Odemeler?aktifMi=true&cariID=${cari.cariID}`);
                const filtered = response.data
                    .sort((a: any, b: any) => {
                        if (!a.vadeTarihi && !b.vadeTarihi) return 0;
                        if (!a.vadeTarihi) return 1;
                        if (!b.vadeTarihi) return -1;
                        return new Date(a.vadeTarihi).getTime() - new Date(b.vadeTarihi).getTime();
                    });
                setOdemeler(filtered);
            } catch (error) {
                if (isAxiosError(error) && (error as any).response?.status !== 404) {
                    toast.error(t('msg.islemHatasi'));
                }
            }
            fetchEkstreOzet();
        } else {
            setOdemeler([]);
        }
    };

    const fetchCariOdemePlani = async () => {
        if (cari?.cariID) {
            try {
                const response = await axiosInstance.get(`/api/CariOdemePlani?cariID=${cari.cariID}`);
                setCariOdemePlanlari(response.data || []);
            } catch {
                setCariOdemePlanlari([]);
            }
        } else {
            setCariOdemePlanlari([]);
        }
    };

    const fetchEkstreOzet = async () => {
        if (cari?.cariID) {
            try {
                const res = await axiosInstance.get(`/api/Cariler/${cari.cariID}/ekstre-ozet`);
                setEkstreOzet(res.data);
            } catch {
                // sessiz hata
            }
        } else {
            setEkstreOzet(null);
        }
    };

    const fetchProjeler = async () => {
        try {
            const response = await axiosInstance.get('/api/Projeler');
            setProjeler(response.data);
        } catch {}
    };

    const fetchKasalar = async () => {
        try {
            const response = await axiosInstance.get('/api/Kasalar');
            setKasalar(response.data);
        } catch (error) {
            toast.error(t('msg.islemHatasi'));
        }
    };

    /** Bu müşteri için doğrudan yeni sunum oluşturur.
     *  Taslak/Devam Ediyor durumunda sunum varsa backend 409 döner; yeni sunum açılmaz. */
    const handleSunumOlustur = async () => {
        if (!cari?.cariID) return;
        // Önce elimizdeki listeden hızlı kontrol (backend zaten otoriter kontrolü yapıyor)
        const devamEden = musteriSunumlari.find(s => s.durum === 'Taslak' || s.durum === 'Devam Ediyor');
        if (devamEden) {
            toast.warning(`Bu müşterinin "${devamEden.durum}" durumunda bir sunumu var. Lütfen devam eden sunumu tamamlayınız.`, { autoClose: 6000 });
            navigate(`/musteri-sunumlari/${devamEden.sunumID}`);
            return;
        }
        try {
            setSunumOlusturuluyor(true);
            const res = await axiosInstance.post('/api/MusteriSunumlari', {
                cariID: cari.cariID,
                olusturanKullaniciID: user?.kullaniciID,
                baslik: `${cari.adiSoyadi} Sunumu`,
                daireIDler: [],
            });
            toast.success('Sunum oluşturuldu.');
            navigate(`/musteri-sunumlari/${res.data.sunumID}`);
        } catch (err: any) {
            if (err?.response?.status === 409) {
                const d = err.response.data;
                toast.warning(d?.message || 'Bu müşterinin devam eden bir sunumu var.', { autoClose: 6000 });
                if (d?.mevcutSunumID) navigate(`/musteri-sunumlari/${d.mevcutSunumID}`);
            } else {
                toast.error('Sunum oluşturulamadı.');
            }
        } finally {
            setSunumOlusturuluyor(false);
        }
    };

    const handlePrintRapor = async () => {
        // Kurumsal bilgileri çek
        let firmaAdi = 'YapiCRM';
        let firmaAdres = '';
        try {
            const kr = await axiosInstance.get('/api/Kurumsal/1');
            firmaAdi = kr.data.firmaAdi || firmaAdi;
            firmaAdres = [kr.data.adres, kr.data.sehir].filter(Boolean).join(', ');
        } catch {}

        // Aktif filtreleri uygula (sözleşmeye bağlı taksitler + barter + Bekliyor Tahsilat/Ödeme hariç —
        // bu satırlar aşağıda sozlesmeler'den tek satır olarak, ekrandaki tabloyla aynı şekilde ekleniyor)
        const filtered = odemeler.filter(o => {
            if (o.sozlesmeOdemesi) return false;
            if (isBarterSozlesmeOdeme(o)) return false;
            if ((o.islemTipi === 'Tahsilat' || o.islemTipi === 'Odeme') && o.odemeDurumu === 'Bekliyor') return false;
            if (hareketFilters.odemeTuru && o.odemeTuru !== hareketFilters.odemeTuru) return false;
            if (hareketFilters.odemeDurumu && o.odemeDurumu !== hareketFilters.odemeDurumu) return false;
            if (hareketFilters.vadeTarihiBaslangic && o.vadeTarihi)
                if (new Date(o.vadeTarihi) < new Date(hareketFilters.vadeTarihiBaslangic)) return false;
            if (hareketFilters.vadeTarihiBitis && o.vadeTarihi)
                if (new Date(o.vadeTarihi) > new Date(hareketFilters.vadeTarihiBitis)) return false;
            return true;
        });

        // Tür bazlı hesap: backend ekstre-ozet'ten alınır
        const toplamBorc = ekstreOzet?.toplamBorc ?? 0;
        const toplamAlacak = ekstreOzet?.toplamAlacak ?? 0;
        const bakiye       = ekstreOzet?.bakiye ?? 0;
        const baYonuPrint  = ekstreOzet?.baYonu ?? '';
        const bakiyeEtiketi = ekstreOzet?.bakiyeEtiketi ?? 'Dengede';
        const fmt = (n: number) => new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Math.abs(n));
        const fmtDate = (d: string | null) => d ? new Date(d).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—';
        const today = new Date().toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' });

        let cumBorc = 0;
        let cumAlacak = 0;

        // Aktif sözleşme/taşeron kayıtları — ekrandaki "Cari Hareketleri" tablosuyla aynı şekilde
        // tek satır olarak eklenir (satırın tüm taksitleri değil, sözleşmenin toplam tutarı)
        const aktifSozlesmeler = sozlesmeler.filter(s => s.sozlesmeDurumu === 'Taşeron' || s.sozlesmeDurumu === 'Sözleşme');
        const sozlesmeRows = aktifSozlesmeler.map((s, i) => {
            cumBorc += s.sozlesmeFiyati;
            const satirBakiyesi = Math.abs(cumBorc - cumAlacak);
            const baYonu = cumBorc > cumAlacak ? 'B' : cumAlacak > cumBorc ? 'A' : '';
            const bakiyeClass = cumBorc > cumAlacak ? 'borc' : cumAlacak > cumBorc ? 'alacak' : '';
            const bakiyeStr = fmt(satirBakiyesi) + ' ₺' + (baYonu ? ` (${baYonu})` : '');
            const tipLabel = s.sozlesmeDurumu === 'Taşeron' ? t('cari.taseronSozlesme', 'Taşeron Sözleşmesi') : t('cari.satisSozlesme', 'Satış Sözleşmesi');
            const projeAdi = s.daire?.blok?.proje?.projeAdi;
            const blokAdi = s.daire?.blok?.blokAdi;
            const daireNo = s.daire?.daireNo;
            const aciklama = [tipLabel, projeAdi, blokAdi && daireNo ? `${blokAdi}-${daireNo}` : daireNo, s.sozlesmeTakipNo].filter(Boolean).join(' · ');
            return `
            <tr class="${i % 2 === 0 ? 'even' : ''}">
                <td>${fmtDate(s.sozlesmeTarihi)}</td>
                <td>${t('cari.sozlesme', 'Sözleşme')}</td>
                <td><span class="badge badge-amber">${t('cari.sozlesme', 'Sözleşme')}</span></td>
                <td class="aciklama">${aciklama || '—'}</td>
                <td class="num borc">${fmt(s.sozlesmeFiyati)} ₺</td>
                <td class="num alacak">—</td>
                <td class="num ${bakiyeClass}">${bakiyeStr}</td>
            </tr>
        `;
        }).join('');

        const rows = sozlesmeRows + filtered.map((o, i) => {
            // Tür: Gider → Alacak | Gelir → Borç | Tahsilat+Odendi → Alacak | Ödeme+Odendi → Borç
            const rowBorc = (o.islemTipi === 'Gelir' || (o.islemTipi === 'Odeme' && o.odemeDurumu === 'Odendi')) ? o.miktar : null;
            const rowAlacak = (o.islemTipi === 'Gider' || (o.islemTipi === 'Tahsilat' && o.odemeDurumu === 'Odendi')) ? o.miktar : null;
            cumAlacak += rowAlacak ?? 0;
            cumBorc += rowBorc ?? 0;
            const satirBakiyesi = Math.abs(cumBorc - cumAlacak);
            const baYonu = cumBorc > cumAlacak ? 'B' : cumAlacak > cumBorc ? 'A' : '';
            const bakiyeClass = cumBorc > cumAlacak ? 'borc' : cumAlacak > cumBorc ? 'alacak' : '';
            const bakiyeStr = fmt(satirBakiyesi) + ' ' + (o.paraBirimi?.birim || '₺') + (baYonu ? ` (${baYonu})` : '');
            return `
            <tr class="${i % 2 === 0 ? 'even' : ''}">
                <td>${fmtDate(o.vadeTarihi)}</td>
                <td>${o.odemeTuru}</td>
                <td><span class="badge ${o.odemeDurumu === 'Odendi' ? 'badge-green' : 'badge-amber'}">${o.odemeDurumu}</span></td>
                <td class="aciklama">${[o.ilgiliProje?.projeAdi, o.hizmetUrunAdi, o.aciklama].filter(Boolean).join(' — ') || '—'}</td>
                <td class="num borc">${rowBorc != null ? fmt(rowBorc) + ' ' + (o.paraBirimi?.birim || '₺') : '—'}</td>
                <td class="num alacak">${rowAlacak != null ? fmt(rowAlacak) + ' ' + (o.paraBirimi?.birim || '₺') : '—'}</td>
                <td class="num ${bakiyeClass}">${bakiyeStr}</td>
            </tr>
        `;
        }).join('');

        const totalRowCount = aktifSozlesmeler.length + filtered.length;

        const html = `<!DOCTYPE html>
<html lang="tr">
<head>
<meta charset="UTF-8"/>
<title>Cari Hareket Raporu</title>
<style>
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: Arial, Helvetica, sans-serif; font-size: 12px; color: #1e293b; background: #fff; }
  .page { padding: 28px 32px; max-width: 900px; margin: 0 auto; }

  /* Header */
  .header { display: flex; justify-content: space-between; align-items: flex-start;
            padding-bottom: 18px; border-bottom: 2px solid #1e293b; margin-bottom: 20px; }
  .header-left .firma { font-size: 20px; font-weight: 800; color: #0f172a; letter-spacing: -0.03em; }
  .header-left .firma-sub { font-size: 10px; font-weight: 600;
                             letter-spacing: 0.1em; color: #64748b; margin-top: 2px; }
  .cari-info { margin-top: 12px; }
  .cari-name { font-size: 14px; font-weight: 700; color: #0f172a; }
  .cari-detail { color: #64748b; margin-top: 3px; line-height: 1.6; }
  .header-right { text-align: right; }
  .rapor-label { font-size: 9px; letter-spacing: 0.1em; color: #94a3b8; }
  .rapor-date { font-size: 15px; font-weight: 700; color: #0f172a; margin-top: 2px; }

  /* Finans özeti */
  .summary { display: flex; gap: 12px; margin-bottom: 20px; }
  .sum-box { flex: 1; border-radius: 8px; padding: 14px 16px; }
  .sum-box.green { background: #f0fdf4; border: 1px solid #bbf7d0; }
  .sum-box.red   { background: #fef2f2; border: 1px solid #fecaca; }
  .sum-box.amber { background: #fffbeb; border: 1px solid #fde68a; }
  .sum-label { font-size: 9px; font-weight: 700; letter-spacing: 0.08em; color: #64748b; }
  .sum-val   { font-size: 18px; font-weight: 800; margin-top: 6px; letter-spacing: -0.03em; }
  .sum-box.green .sum-val { color: #15803d; }
  .sum-box.red   .sum-val { color: #dc2626; }
  .sum-box.amber .sum-val { color: #b45309; }
  .sum-sub { font-size: 10px; color: #94a3b8; margin-top: 2px; }

  /* Tablo */
  .section-title { font-size: 10px; font-weight: 700;
                   letter-spacing: 0.08em; color: #94a3b8; margin-bottom: 8px; }
  table { width: 100%; border-collapse: collapse; }
  thead tr { background: #f8fafc; border-top: 1px solid #e2e8f0; border-bottom: 1px solid #e2e8f0; }
  th { padding: 8px 10px; text-align: left; font-size: 9px; font-weight: 700;
       letter-spacing: 0.08em; color: #64748b; }
  td { padding: 9px 10px; border-bottom: 1px solid #f1f5f9; font-size: 11.5px; color: #374151; }
  tr.even td { background: #fafafa; }
  .aciklama { color: #64748b; font-size: 11px; }
  .num { text-align: right; font-variant-numeric: tabular-nums; }
  .alacak { color: #15803d; font-weight: 600; }
  .borc   { color: #dc2626; font-weight: 600; }
  .bakiye { font-weight: 700; border-left: 1px solid #e2e8f0; }
  .badge { display: inline-block; padding: 2px 8px; border-radius: 999px;
           font-size: 10px; font-weight: 700; }
  .badge-green { background: #dcfce7; color: #15803d; }
  .badge-amber { background: #fef3c7; color: #b45309; }

  /* Footer */
  .footer { margin-top: 24px; padding-top: 12px; border-top: 1px solid #e2e8f0;
            display: flex; justify-content: space-between; color: #94a3b8; font-size: 9px; }

  @media print {
    body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .page { padding: 16px 20px; }
  }
</style>
</head>
<body>
<div class="page">

  <!-- HEADER -->
  <div class="header">
    <div class="header-left">
      <div class="firma">${firmaAdi}</div>
      <div class="firma-sub">${t('cari.hareketRaporu', 'Cari Hareket Raporu')}</div>
      <div class="cari-info">
        <div class="cari-name">${cari?.adiSoyadi || ''}${cari?.firmaAdi ? ' &mdash; ' + cari.firmaAdi : ''} <span style="font-size:10px;font-weight:600;color:${cari?.aktifMi ? '#15803d' : '#dc2626'}">(${cari?.aktifMi ? 'Aktif' : 'Pasif'})</span></div>
        <div class="cari-detail">
          ${cari?.telefon1 ? '📞 ' + cari.telefon1 + '<br/>' : ''}
          ${cari?.adres ? '📍 ' + cari.adres : ''}
        </div>
      </div>
    </div>
    <div class="header-right">
      <div class="rapor-label">${t('rapor.tarih', 'Rapor Tarihi')}</div>
      <div class="rapor-date">${today}</div>
      ${(hareketFilters.vadeTarihiBaslangic || hareketFilters.vadeTarihiBitis) ? `<div style="margin-top:8px;font-size:10px;color:#64748b">Vade: ${hareketFilters.vadeTarihiBaslangic ? fmtDate(hareketFilters.vadeTarihiBaslangic) : '...'} – ${hareketFilters.vadeTarihiBitis ? fmtDate(hareketFilters.vadeTarihiBitis) : '...'}</div>` : ''}
    </div>
  </div>

  <!-- SUMMARY -->
  <div class="summary">
    <div class="sum-box red">
      <div class="sum-label">${t('cari.toplamBorc')}</div>
      <div class="sum-val">${fmt(toplamBorc)} ₺</div>
      <div class="sum-sub">${t('cari.gerceklesenOdeme')}</div>
    </div>
    <div class="sum-box green">
      <div class="sum-label">${t('cari.toplamAlacakGiderler', 'Toplam Alacak (Giderler)')}</div>
      <div class="sum-val">${fmt(toplamAlacak)} ₺</div>
      <div class="sum-sub">${t('cari.bekleyenFatura')}</div>
    </div>
    <div class="sum-box amber">
      <div class="sum-label">${t('cari.genelBakiye', 'Genel Bakiye')}${baYonuPrint ? ` (${baYonuPrint})` : ''}</div>
      <div class="sum-val">${fmt(bakiye)} ₺</div>
      <div class="sum-sub">${bakiyeEtiketi}</div>
    </div>
  </div>

  <!-- TABLO -->
  <div class="section-title">${t('cari.hareketDetaylari')} (${totalRowCount} kayıt)</div>
  <table>
    <thead>
      <tr>
        <th>${t('label.vadeTarihi', 'Vade Tarihi')}</th>
        <th>${t('sozlesme.odemeTuru')}</th>
        <th>${t('label.durum', 'Durum')}</th>
        <th>${t('label.aciklama')}</th>
        <th style="text-align:right">${t('label.borc')}</th>
        <th style="text-align:right">${t('label.alacak', 'Alacak')}</th>
        <th style="text-align:right">${t('label.bakiye', 'Bakiye')}</th>
      </tr>
    </thead>
    <tbody>
      ${rows || `<tr><td colspan="7" style="text-align:center;padding:20px;color:#94a3b8">${t('msg.veriYok')}</td></tr>`}
    </tbody>
  </table>

  <!-- FOOTER -->
  <div class="footer">
    <span>${t('msg.otomatikOlusturuldu')}</span>
    <span>${firmaAdi} &copy; ${new Date().getFullYear()}</span>
  </div>

</div>
<script>window.onload = function(){ window.print(); }<\/script>
</body>
</html>`;

        const win = window.open('', '_blank', 'width=960,height=700');
        if (win) {
            win.document.write(html);
            win.document.close();
        }
    };

    // ─── Bağımsız Makbuz + Allocation ─────────────────────────────────────

    const fetchAcikFaturalar = async (cariID: number) => {
        try {
            const res = await axiosInstance.get(`/api/OdemeFaturaEslesmeleri/acik-faturalar/${cariID}`);
            setAcikFaturalar(res.data || []);
        } catch {
            setAcikFaturalar([]);
        }
    };

    const handleMakbuzFormSaveCD = async () => {
        if (!makbuzCDFormData.miktar || parseFloat(parseMoneyInput(makbuzCDFormData.miktar)) <= 0) {
            toast.error(t('msg.zorunluAlanlar')); return;
        }
        if (!makbuzCDFormData.tarih) { toast.error(t('msg.zorunluAlanlar')); return; }
        if (!makbuzCDFormData.kasaID) { toast.error(t('msg.zorunluAlanlar')); return; }
        if (!cari?.cariID) return;
        try {
            setMakbuzCDSaving(true);
            const prefix = makbuzModu === 'tahsilat' ? 'TAH' : 'ODE';
            const res = await axiosInstance.post('/api/Odemeler', {
                cariID: cari.cariID,
                islemTarihi: makbuzCDFormData.tarih,
                miktar: parseFloat(parseMoneyInput(makbuzCDFormData.miktar)),
                paraBirimiID: 1,
                islemTakipNo: `${prefix}-${Date.now()}`,
                odemeTuru: makbuzCDFormData.odemeTuru,
                odemeYonu: makbuzModu === 'tahsilat' ? 'Tahsilat' : 'Odeme',
                odemeDurumu: 'Odendi',
                kasaID: parseInt(makbuzCDFormData.kasaID),
                aciklama: makbuzCDFormData.aciklama || null,
                aktifMi: true,
                sozlesmeOdemesi: false,
            });
            toast.success(t('msg.islemBasarili'));
            setShowMakbuzModal(false);
            fetchOdemeler();
        } catch (error: any) {
            toast.error(error.response?.data?.message || 'Makbuz kaydedilemedi.');
        } finally {
            setMakbuzCDSaving(false);
        }
    };

    const handleEslesmeKaydetCD = async () => {
        if (!savedMakbuzID) { toast.error(t('msg.islemHatasi')); return; }
        const eslesmeler = acikFaturalar
            .map(f => {
                const key = `${f.tip}-${f.id}`;
                const tutar = parseFloat(parseMoneyInput(eslesmeTutarlari[key] || '')) || 0;
                if (tutar <= 0) return null;
                return {
                    faturaOdemeID: f.tip === 'odeme' ? f.id : null,
                    sozlesmeID: f.tip === 'sozlesme' ? f.id : null,
                    odenenTutar: tutar,
                };
            })
            .filter(Boolean);
        if (eslesmeler.length === 0) { toast.info(t('msg.bilgi')); return; }
        try {
            setEslesmeSaving(true);
            await axiosInstance.post('/api/OdemeFaturaEslesmeleri', { makbuzOdemeID: savedMakbuzID, eslesmeler });
            toast.success(t('msg.islemBasarili'));
            setShowMakbuzModal(false);
        } catch (error: any) {
            toast.error(error.response?.data?.message  || t('msg.islemHatasi'));
        } finally {
            setEslesmeSaving(false);
        }
    };

    // ───────────────────────────────────────────────────────────────

    const handleTahsilatEkle = () => {
        const todayIso = new Date().toISOString().split('T')[0];
        setMakbuzModu('tahsilat');
        setMakbuzCDFormData({ miktar: '', tarih: todayIso, kasaID: '', odemeTuru: 'Nakit', aciklama: '' });
        setSavedMakbuzID(null);
        setAcikFaturalar([]);
        setEslesmeTutarlari({});
        setMakbuzStep(1);
        setShowMakbuzModal(true);
    };

    const handleOdemeEkleBtn = () => {
        const todayIso = new Date().toISOString().split('T')[0];
        setMakbuzModu('odeme');
        setMakbuzCDFormData({ miktar: '', tarih: todayIso, kasaID: '', odemeTuru: 'Nakit', aciklama: '' });
        setSavedMakbuzID(null);
        setAcikFaturalar([]);
        setEslesmeTutarlari({});
        setMakbuzStep(1);
        setShowMakbuzModal(true);
    };

    const handleShowTekliOdemeModal = () => {
        setTekliOdemeFormData({
            tutar: '',
            vadeTarihi: toTRDate(getCurrentDate()),
            odemeTuru: 'Nakit',
            odemeDurumu: 'Bekliyor',
            kasaID: '',
            aciklama: ''
        });
        setShowTekliOdemeModal(true);
    };

    const handleSaveTekliOdeme = async () => {
        const { tutar, vadeTarihi: vadeTarihiTR, odemeTuru, odemeDurumu, kasaID, aciklama } = tekliOdemeFormData;
        const vadeTarihiISO = fromTRDate(vadeTarihiTR);
        if (!tutar || !vadeTarihiISO) {
            toast.error(t('msg.zorunluAlanlar'));
            return;
        }
        if (odemeDurumu === 'Odendi' && !kasaID) {
            toast.error(t('msg.zorunluAlanlar'));
            return;
        }
        if (!cari?.cariID) return;
        const isoDateTime = `${vadeTarihiISO}T00:00:00`;
        try {
            setSaving(true);
            await axiosInstance.post('/api/CariOdemePlani', {
                cariID: cari.cariID,
                vadeTarihi: isoDateTime,
                miktar: parseFloat(parseMoneyInput(tutar)),
                odemeTuru: odemeTuru,
                aciklama: aciklama || null
            });
            toast.success(t('msg.islemBasarili'));
            setShowTekliOdemeModal(false);
            await fetchCariOdemePlani();
        } catch (error: any) {
            toast.error(t('msg.islemHatasi'));
            console.error('Tekli ödeme hatası:', error?.response?.data);
        } finally {
            setSaving(false);
        }
    };

    const handleShowTopluPlanlaModal = () => {
        setTopluPlanlaFormData({
            toplamTutar: '',
            baslamaTarihi: toTRDate(getCurrentDate()),
            taksitSayisi: '',
            aralikAy: '1',
            odemeTuru: 'Nakit',
            odemeYonu: 'Odeme',
            aciklama: '',
            projeID: ''
        });
        setShowTopluPlanlaModal(true);
    };

    const handleSaveTopluPlanla = async () => {
        const { toplamTutar, baslamaTarihi: baslamaTarihiTR, taksitSayisi, aralikAy, odemeTuru, aciklama } = topluPlanlaFormData;
        const baslamaTarihi = fromTRDate(baslamaTarihiTR);
        if (!toplamTutar || !baslamaTarihi || !taksitSayisi || !aralikAy) {
            toast.error(t('msg.zorunluAlanlar'));
            return;
        }
        if (!cari?.cariID) {
            toast.error(t('msg.islemHatasi'));
            return;
        }
        const sayi = parseInt(taksitSayisi);
        const tutar = parseFloat(parseMoneyInput(toplamTutar));
        const aralik = parseInt(aralikAy);
        if (sayi <= 0 || tutar <= 0 || aralik <= 0) {
            toast.error(t('msg.zorunluAlanlar'));
            return;
        }
        const taksitTutari = tutar / sayi;
        let kalanTutar = tutar;
        const taksitler = [];
        for (let i = 0; i < sayi; i++) {
            const vade = new Date(baslamaTarihi);
            vade.setMonth(vade.getMonth() + i * aralik);
            const miktarBu = i === sayi - 1
                ? Math.round(kalanTutar * 100) / 100
                : Math.round(taksitTutari * 100) / 100;
            if (i < sayi - 1) kalanTutar -= miktarBu;
            taksitler.push({
                vadeTarihi: vade.toISOString().split('T')[0],
                miktar: miktarBu,
                odemeTuru: odemeTuru,
                aciklama: `${i + 1}. Taksit${aciklama ? ' - ' + aciklama : ''}`
            });
        }
        try {
            setSaving(true);
            await axiosInstance.post('/api/CariOdemePlani/toplu', {
                cariID: cari.cariID,
                taksitler
            });
            toast.success(t('msg.islemBasarili'));
            setShowTopluPlanlaModal(false);
            await fetchCariOdemePlani();
        } catch (error) {
            toast.error(t('msg.islemHatasi'));
            console.error('Toplu planla hatası:', error);
        } finally {
            setSaving(false);
        }
    };

    const handleTopluIptal = async () => {
        if (selectedOdemeler.size === 0) return;
        setTopluSilmeLoading(true);
        try {
            await Promise.all(Array.from(selectedOdemeler).map(id => {
                const odeme = odemeler.find(o => o.odemeID === id);
                if (!odeme) return Promise.resolve();
                return axiosInstance.put(`/api/Odemeler/${id}`, {
                    odemeID: odeme.odemeID,
                    cariID: odeme.cariID,
                    islemTarihi: odeme.islemTarihi,
                    vadeTarihi: odeme.vadeTarihi,
                    tahsilatTarihi: odeme.tahsilatTarihi,
                    miktar: odeme.miktar,
                    paraBirimiID: odeme.paraBirimiID,
                    islemTakipNo: odeme.islemTakipNo,
                    odemeTuru: odeme.odemeTuru,
                    odemeYonu: odeme.odemeYonu,
                    odemeDurumu: odeme.odemeDurumu,
                    aciklama: odeme.aciklama,
                    hizmetUrunAdi: odeme.hizmetUrunAdi,
                    aktifMi: false,
                    sozlesmeOdemesi: odeme.sozlesmeOdemesi,
                    eklemeTarihi: odeme.eklemeTarihi,
                    projeID: odeme.projeID,
                    kasaID: odeme.kasaID ?? null,
                });
            }));
            toast.success(t('msg.islemBasarili'));
            setSelectedOdemeler(new Set());
            await fetchOdemeler();
        } catch {
            toast.error(t('msg.islemHatasi'));
        } finally {
            setTopluSilmeLoading(false);
        }
    };

    const handleOdemeYap = (plan: CariPlan) => {
        setOdemeYapTaksit(plan);
        setOdemeYapFormData({ tarih: getCurrentDate(), kasaID: '' });
        setOdemeYapTarihDisplay(toTRDate(getCurrentDate()));
        setShowOdemeYapModal(true);
    };

    const handleSaveOdemeYap = async () => {
        if (!odemeYapTaksit || !cari?.cariID) return;
        if (!odemeYapFormData.kasaID) {
            toast.error(t('msg.zorunluAlanlar'));
            return;
        }
        try {
            setSaving(true);
            await axiosInstance.post(`/api/CariOdemePlani/${odemeYapTaksit.cariOdemePlaniID}/odeme-yap`, {
                odemeTarihi: odemeYapFormData.tarih,
                miktar: odemeYapTaksit.miktar,
                odemeTuru: odemeYapTaksit.odemeTuru ?? 'Nakit',
                kasaID: parseInt(odemeYapFormData.kasaID),
            });
            toast.success(t('msg.islemBasarili'));
            setShowOdemeYapModal(false);
            setOdemeYapTaksit(null);
            await fetchCariOdemePlani();
            await fetchOdemeler();
        } catch {
            toast.error(t('msg.islemHatasi'));
        } finally {
            setSaving(false);
        }
    };

    const handleOdemeGeriAl = async (plan: CariPlan) => {
        if (!window.confirm(t('confirm.silmekIstiyormusunuz'))) return;
        try {
            setSaving(true);
            await axiosInstance.put(`/api/CariOdemePlani/${plan.cariOdemePlaniID}/geri-al`);
            toast.success(t('msg.islemBasarili'));
            await fetchCariOdemePlani();
            await fetchOdemeler();
        } catch {
            toast.error(t('msg.islemHatasi'));
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteTaksit = async (plan: CariPlan) => {
        if (!window.confirm(t('confirm.silmekIstiyormusunuz'))) return;
        try {
            setSaving(true);
            await axiosInstance.delete(`/api/CariOdemePlani/${plan.cariOdemePlaniID}`);
            toast.success('Taksit silindi.');
            await fetchCariOdemePlani();
        } catch {
            toast.error(t('msg.islemHatasi'));
        } finally {
            setSaving(false);
        }
    };

    const handleTopluSilTaksit = async () => {
        if (selectedTaksitler.size === 0) return;
        if (!window.confirm(t('confirm.silmekIstiyormusunuz'))) return;
        setTaksitTopluSilLoading(true);
        try {
            const taksitIDs = Array.from(selectedTaksitler);
            for (const id of taksitIDs) {
                await axiosInstance.delete(`/api/CariOdemePlani/${id}`);
            }
            toast.success(`${selectedTaksitler.size} taksit silindi.`);
            setSelectedTaksitler(new Set());
            await fetchCariOdemePlani();
        } catch {
            toast.error(t('msg.islemHatasi'));
        } finally {
            setTaksitTopluSilLoading(false);
        }
    };

    const handleOdemeFormChange = (field: string, value: string) => {
        if (field === 'miktar') {
            setOdemeFormData(prev => ({ ...prev, [field]: formatMoneyInput(value) }));
        } else {
            setOdemeFormData(prev => ({ ...prev, [field]: value }));
        }
    };

    const handleSaveOdeme = async () => {
        if (!odemeFormData.miktar || !odemeFormData.vadeTarihi || !cari?.cariID) {
            toast.error(t('msg.zorunluAlanlar'));
            return;
        }
        if (odemeFormData.odemeYonu !== 'Odeme' && odemeFormData.odemeDurumu === 'Odendi' && !odemeFormData.kasaID) {
            toast.error(t('msg.zorunluAlanlar'));
            return;
        }
        const toOdemeTuru = (t: string) => ({ Nakit: 0, BankaHavale: 1, Cek: 2, Senet: 3, Takas: 4, Mahsup: 5 }[t] ?? 0);
        const toOdemeYonu = (y: string) => (y === 'Odeme' ? 1 : 0);
        const toOdemeDurumu = (d: string) => (d === 'Odendi' ? 1 : 0);
        try {
            setSaving(true);
            await axiosInstance.post('/api/Odemeler', {
                cariID: cari.cariID,
                islemTarihi: getCurrentDate(),
                vadeTarihi: odemeFormData.vadeTarihi,
                tahsilatTarihi: odemeFormData.vadeTarihi,
                miktar: parseFloat(parseMoneyInput(odemeFormData.miktar)),
                paraBirimiID: 1,
                islemTakipNo: cari.takipNo,
                odemeTuru: toOdemeTuru(odemeFormData.odemeTuru),
                odemeYonu: toOdemeYonu(odemeFormData.odemeYonu),
                islemTipi: toOdemeYonu(odemeFormData.odemeYonu) === 0 ? 2 : 3,
                odemeDurumu: odemeFormData.odemeYonu === 'Odeme' ? (odemeFormData.kasaID ? 1 : 0) : toOdemeDurumu(odemeFormData.odemeDurumu),
                kasaID: odemeFormData.kasaID ? parseInt(odemeFormData.kasaID) : null,
                aciklama: odemeFormData.aciklama || '',
                hizmetUrunAdi: odemeFormData.hizmetUrunAdi || null,
                projeID: odemeFormData.projeID ? parseInt(odemeFormData.projeID) : null,
                aktifMi: true,
                sozlesmeOdemesi: false,
                eklemeTarihi: new Date().toISOString()
            });
            toast.success(t('msg.islemBasarili'));
            setShowOdemeModal(false);
            await fetchOdemeler();
        } catch {
            toast.error(t('msg.islemHatasi'));
        } finally {
            setSaving(false);
        }
    };

    const handleEditOdeme = (odeme: Odeme) => {
        setEditingOdeme(odeme);
        const fmt = (d: string) => {
            const dt = new Date(d);
            return `${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,'0')}-${String(dt.getDate()).padStart(2,'0')}`;
        };
        setEditOdemeFormData({
            miktar: formatMoneyInput(odeme.miktar.toString()),
            vadeTarihi: odeme.vadeTarihi ? fmt(odeme.vadeTarihi) : '',
            tahsilatTarihi: odeme.tahsilatTarihi ? fmt(odeme.tahsilatTarihi) : '',
            odemeDurumu: odeme.odemeDurumu,
            aciklama: odeme.aciklama || '',
            hizmetUrunAdi: odeme.hizmetUrunAdi || '',
            kasaID: odeme.kasaID ? odeme.kasaID.toString() : ''
        });
        setShowEditOdemeModal(true);
    };

    const handleEditOdemeFormChange = (field: string, value: string) => {
        if (field === 'miktar') {
            setEditOdemeFormData(prev => ({ ...prev, [field]: formatMoneyInput(value) }));
        } else {
            setEditOdemeFormData(prev => ({ ...prev, [field]: value }));
        }
    };

    const handleSaveEditOdeme = async () => {
        if (!editingOdeme || !editOdemeFormData.miktar || !editOdemeFormData.vadeTarihi) {
            toast.error(t('msg.zorunluAlanlar'));
            return;
        }
        if (editingOdeme?.odemeYonu !== 'Odeme' && editOdemeFormData.odemeDurumu === 'Odendi' && !editOdemeFormData.kasaID) {
            toast.error(t('msg.zorunluAlanlar'));
            return;
        }
        const toEnum = (v: string, map: Record<string,number>) => map[v] ?? 0;
        try {
            setSaving(true);
            await axiosInstance.put(`/api/Odemeler/${editingOdeme.odemeID}`, {
                odemeID: editingOdeme.odemeID,
                cariID: editingOdeme.cariID,
                islemTarihi: editingOdeme.islemTarihi,
                vadeTarihi: editOdemeFormData.vadeTarihi,
                tahsilatTarihi: editOdemeFormData.tahsilatTarihi || null,
                miktar: parseFloat(parseMoneyInput(editOdemeFormData.miktar)),
                paraBirimiID: editingOdeme.paraBirimiID,
                islemTakipNo: editingOdeme.islemTakipNo,
                odemeTuru: toEnum(editingOdeme.odemeTuru, { Nakit:0, BankaHavale:1, Cek:2, Senet:3, Takas:4, Mahsup:5 }),
                odemeYonu: editingOdeme.odemeYonu === 'Tahsilat' ? 0 : 1,
                islemTipi: editingOdeme.odemeYonu === 'Tahsilat' ? 2 : 3,
                odemeDurumu: editingOdeme.odemeYonu === 'Odeme' ? (editOdemeFormData.kasaID ? 1 : 0) : toEnum(editOdemeFormData.odemeDurumu, { Bekliyor:0, Odendi:1 }),
                kasaID: editOdemeFormData.kasaID ? parseInt(editOdemeFormData.kasaID) : null,
                aciklama: editOdemeFormData.aciklama || '',
                projeID: editingOdeme.projeID,
                aktifMi: editingOdeme.aktifMi,
                sozlesmeOdemesi: editingOdeme.sozlesmeOdemesi,
                eklemeTarihi: editingOdeme.eklemeTarihi,
                hizmetUrunAdi: editOdemeFormData.hizmetUrunAdi || null
            });
            toast.success(t('msg.islemBasarili'));
            setShowEditOdemeModal(false);
            setEditingOdeme(null);
            await fetchOdemeler();
        } catch {
            toast.error(t('msg.islemHatasi'));
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteOdeme = async () => {
        if (!editingOdeme) return;
        try {
            setSaving(true);
            const toEnum = (v: string, map: Record<string,number>) => map[v] ?? 0;
            await axiosInstance.put(`/api/Odemeler/${editingOdeme.odemeID}`, {
                odemeID: editingOdeme.odemeID,
                cariID: editingOdeme.cariID,
                islemTarihi: editingOdeme.islemTarihi,
                vadeTarihi: editingOdeme.vadeTarihi || null,
                tahsilatTarihi: editingOdeme.tahsilatTarihi || null,
                miktar: editingOdeme.miktar,
                paraBirimiID: editingOdeme.paraBirimiID,
                islemTakipNo: editingOdeme.islemTakipNo,
                odemeTuru: toEnum(editingOdeme.odemeTuru, { Nakit:0, BankaHavale:1, Cek:2, Senet:3, Takas:4, Mahsup:5 }),
                odemeYonu: editingOdeme.odemeYonu === 'Tahsilat' ? 0 : 1,
                islemTipi: editingOdeme.odemeYonu === 'Tahsilat' ? 2 : 3,
                odemeDurumu: toEnum(editingOdeme.odemeDurumu, { Bekliyor:0, Odendi:1 }),
                aciklama: editingOdeme.aciklama || '',
                projeID: editingOdeme.projeID,
                aktifMi: false,
                sozlesmeOdemesi: editingOdeme.sozlesmeOdemesi,
                eklemeTarihi: editingOdeme.eklemeTarihi
            });
            toast.success(t('msg.islemBasarili'));
            setShowEditOdemeModal(false);
            setEditingOdeme(null);
            await fetchOdemeler();
        } catch {
            toast.error(t('msg.islemHatasi'));
        } finally {
            setSaving(false);
        }
    };

    const handleEditCariPlan = (plan: CariPlan) => {
        setEditingCariPlan(plan);
        const fmt = (d: string) => {
            const dt = new Date(d);
            return `${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,'0')}-${String(dt.getDate()).padStart(2,'0')}`;
        };
        setEditCariPlanFormData({
            miktar: formatMoneyInput(plan.miktar.toString()),
            vadeTarihi: plan.vadeTarihi ? fmt(plan.vadeTarihi) : '',
            aciklama: plan.aciklama || ''
        });
        setShowEditCariPlanModal(true);
    };

    const handleEditCariPlanFormChange = (field: string, value: string) => {
        if (field === 'miktar') {
            setEditCariPlanFormData(prev => ({ ...prev, [field]: formatMoneyInput(value) }));
        } else {
            setEditCariPlanFormData(prev => ({ ...prev, [field]: value }));
        }
    };

    const handleSaveEditCariPlan = async () => {
        if (!editingCariPlan || !editCariPlanFormData.miktar || !editCariPlanFormData.vadeTarihi) {
            toast.error(t('msg.zorunluAlanlar'));
            return;
        }
        try {
            setSaving(true);
            await axiosInstance.put(`/api/CariOdemePlani/${editingCariPlan.cariOdemePlaniID}`, {
                vadeTarihi: editCariPlanFormData.vadeTarihi,
                miktar: parseFloat(parseMoneyInput(editCariPlanFormData.miktar)),
                aciklama: editCariPlanFormData.aciklama || ''
            });
            toast.success(t('msg.islemBasarili'));
            setShowEditCariPlanModal(false);
            setEditingCariPlan(null);
            await fetchCariOdemePlani();
        } catch {
            toast.error(t('msg.islemHatasi'));
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteCariPlan = async () => {
        if (!editingCariPlan) return;
        if (!window.confirm(t('confirm.silmekIstiyormusunuz'))) return;
        try {
            setSaving(true);
            await axiosInstance.delete(`/api/CariOdemePlani/${editingCariPlan.cariOdemePlaniID}`);
            toast.success(t('msg.islemBasarili'));
            setShowEditCariPlanModal(false);
            setEditingCariPlan(null);
            await fetchCariOdemePlani();
        } catch {
            toast.error(t('msg.islemHatasi'));
        } finally {
            setSaving(false);
        }
    };

    const handleBekliyenOdemeYap = async () => {
        if (!editingOdeme) return;
        if (!editOdemeFormData.kasaID) {
            toast.error(t('msg.zorunluAlanlar'));
            return;
        }
        const toEnum = (v: string, map: Record<string,number>) => map[v] ?? 0;
        const bugun = new Date().toISOString().split('T')[0];
        try {
            setSaving(true);
            await axiosInstance.put(`/api/Odemeler/${editingOdeme.odemeID}`, {
                odemeID: editingOdeme.odemeID,
                cariID: editingOdeme.cariID,
                islemTarihi: editingOdeme.islemTarihi,
                vadeTarihi: editOdemeFormData.vadeTarihi,
                tahsilatTarihi: editOdemeFormData.tahsilatTarihi || bugun,
                miktar: parseFloat(parseMoneyInput(editOdemeFormData.miktar)),
                paraBirimiID: editingOdeme.paraBirimiID,
                islemTakipNo: editingOdeme.islemTakipNo,
                odemeTuru: toEnum(editingOdeme.odemeTuru, { Nakit:0, BankaHavale:1, Cek:2, Senet:3, Takas:4, Mahsup:5 }),
                odemeYonu: editingOdeme.odemeYonu === 'Tahsilat' ? 0 : 1,
                islemTipi: editingOdeme.odemeYonu === 'Tahsilat' ? 2 : 3,
                odemeDurumu: 1,
                kasaID: parseInt(editOdemeFormData.kasaID),
                aciklama: editOdemeFormData.aciklama || '',
                projeID: editingOdeme.projeID,
                aktifMi: editingOdeme.aktifMi,
                sozlesmeOdemesi: editingOdeme.sozlesmeOdemesi,
                eklemeTarihi: editingOdeme.eklemeTarihi,
                hizmetUrunAdi: editOdemeFormData.hizmetUrunAdi || null
            });
            toast.success(t('msg.islemBasarili'));
            setShowEditOdemeModal(false);
            setEditingOdeme(null);
            await fetchOdemeler();
        } catch {
            toast.error(t('msg.islemHatasi'));
        } finally {
            setSaving(false);
        }
    };

    const handleNotSil = async (notID: number) => {
        if (!window.confirm(t('confirm.silmekIstiyormusunuz'))) return;
        try {
            await axiosInstance.delete(`/api/Notlar/${notID}`);
            toast.success('Not silindi');
            fetchNotlar();
        } catch (error) {
            toast.error(t('msg.islemHatasi'));
        }
    };

    const handleDosyaSil = async (dosyaID: number) => {
        if (!window.confirm(t('confirm.silmekIstiyormusunuz'))) return;
        try {
            await axiosInstance.delete(`/api/Dosyalar/${dosyaID}`);
            toast.success('Dosya silindi');
            fetchDosyalar();
        } catch (error) {
            toast.error(t('msg.islemHatasi'));
        }
    };

    const handleDosyaDownload = async (dosyaID: number) => {
        try {
            const response = await axiosInstance.get(`/api/Dosyalar/download/${dosyaID}`, {
                responseType: 'blob',
                headers: {
                    'Accept': 'application/octet-stream'
                }
            });
            
            // Content-Disposition header'ından dosya adını al
            const contentDisposition = response.headers['content-disposition'];
            let fileName = 'indirilen-dosya';
            
            if (contentDisposition) {
                // RFC 5987 encoding için kontrol
                const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
                const matches = filenameRegex.exec(contentDisposition);
                if (matches != null && matches[1]) {
                    fileName = matches[1].replace(/['"]/g, '');
                    // URL-encoded dosya adını decode et
                    try {
                        fileName = decodeURIComponent(fileName);
                    } catch (e) {
                        console.warn('Dosya adı decode edilemedi:', e);
                    }
                }
            }

            // Dosyayı indir
            const url = window.URL.createObjectURL(new Blob([response.data], { 
                type: response.headers['content-type'] 
            }));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Dosya indirme hatası:', error);
            toast.error(t('msg.islemHatasi'));
        }
    };

    const handlePreview = async (dosyaID: number, dosyaAdi: string) => {
        try {
            const ext = dosyaAdi.toLowerCase().split('.').pop() || '';
            const isPdf = ext === 'pdf';
            const isImage = ['jpg', 'jpeg', 'png', 'gif'].includes(ext);

            if (!isPdf && !isImage) {
                toast.info(t('msg.bilgi'));
                handleDosyaDownload(dosyaID);
                return;
            }

            const response = await axiosInstance.get(`/api/Dosyalar/download/${dosyaID}`, {
                responseType: 'blob',
                headers: {
                    'Accept': isPdf ? 'application/pdf' : 'image/*'
                }
            });

            const blob = new Blob([response.data], { 
                type: response.headers['content-type'] 
            });
            const url = window.URL.createObjectURL(blob);
            
            setPreviewFile({
                url: url,
                type: isPdf ? 'pdf' : 'image',
                name: dosyaAdi
            });
            setShowPreviewModal(true);
        } catch (error) {
            console.error('Dosya önizleme hatası:', error);
            toast.error(t('msg.islemHatasi'));
        }
    };

    const handleClosePreview = () => {
        if (previewFile?.url) {
            window.URL.revokeObjectURL(previewFile.url);
        }
        setPreviewFile(null);
        setShowPreviewModal(false);
    };

    if (!cari) {
        return <div>{t('msg.yukleniyor')}</div>;
    }

    if (id === 'yeni' && showModal) {
        const inputStyle: React.CSSProperties = { width: '100%', padding: '7px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, outline: 'none', background: '#fff', color: '#111827', boxSizing: 'border-box' };
        const inputFocus = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => { e.target.style.borderColor = '#111827'; (e.target as HTMLElement).style.boxShadow = '0 0 0 3px rgba(17,24,39,0.08)'; };
        const inputBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => { e.target.style.borderColor = '#d1d5db'; (e.target as HTMLElement).style.boxShadow = 'none'; };
        const labelStyle: React.CSSProperties = { display: 'block', fontSize: 12, fontWeight: 500, color: '#374151', marginBottom: 4 };

        return (
            <div
                style={{ position: 'fixed', inset: 0, zIndex: 1050, display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                onClick={(e) => { if (e.target === e.currentTarget) { setShowModal(false); navigate('/cariler'); } }}
            >
                <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(2px)' }} />
                <div style={{ position: 'relative', width: '100%', maxWidth: 998, maxHeight: '90vh', background: '#fff', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.2)', borderRadius: 16, overflow: 'hidden' }}>

                    {/* Header */}
                    <div style={{ flexShrink: 0, padding: '20px', borderBottom: '1px solid #e5e7eb', background: '#fff' }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                            <div>
                                <h2 style={{ fontSize: 18, fontWeight: 600, color: '#111827', margin: 0 }}>Yeni Tedarikçi Ekle</h2>
                                <p style={{ fontSize: 13, color: '#6b7280', margin: '4px 0 0' }}>Sisteme yeni bir tedarikçi veya cari hesap tanımlayın.</p>
                            </div>
                            <button
                                onClick={() => { setShowModal(false); navigate('/cariler'); }}
                                style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 4, color: '#9ca3af', borderRadius: 6, display: 'flex', alignItems: 'center', transition: 'color 0.15s' }}
                                onMouseEnter={e => (e.currentTarget.style.color = '#4b5563')}
                                onMouseLeave={e => (e.currentTarget.style.color = '#9ca3af')}
                            >
                                <X size={20} />
                            </button>
                        </div>
                    </div>

                    {/* Body */}
                    <Form onSubmit={handleSubmit} style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
                        <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: 14, flex: 1 }}>

                            {/* Adı Soyadı + TC */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                                <div>
                                    <label style={labelStyle}>{t('label.adSoyad')} <span style={{ color: '#f43f5e' }}>*</span></label>
                                    <input type="text" value={formData.adiSoyadi} onChange={e => setFormData({ ...formData, adiSoyadi: e.target.value })} required maxLength={100} style={inputStyle} onFocus={inputFocus} onBlur={inputBlur} />
                                </div>
                                <div>
                                    <label style={labelStyle}>{t('label.tc')}</label>
                                    <input type="text" value={formData.tc || ''} onChange={e => setFormData({ ...formData, tc: e.target.value || null })} maxLength={11} style={inputStyle} onFocus={inputFocus} onBlur={inputBlur} />
                                </div>
                            </div>

                            {/* Unvan (tam genişlik) */}
                            <div>
                                <label style={labelStyle}>{t('label.firmaAdi')}</label>
                                <input type="text" value={formData.firmaAdi || ''} onChange={e => setFormData({ ...formData, firmaAdi: e.target.value || null })} maxLength={100} style={inputStyle} onFocus={inputFocus} onBlur={inputBlur} />
                            </div>

                            {/* Vergi Dairesi + Vergi No */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                                <div>
                                    <label style={labelStyle}>{t('label.vergiDairesi')}</label>
                                    <input type="text" value={formData.cariVD || ''} onChange={e => setFormData({ ...formData, cariVD: e.target.value || null })} maxLength={100} style={inputStyle} onFocus={inputFocus} onBlur={inputBlur} />
                                </div>
                                <div>
                                    <label style={labelStyle}>{t('label.vergiNo')}</label>
                                    <input type="text" value={formData.cariVN || ''} onChange={e => setFormData({ ...formData, cariVN: e.target.value || null })} maxLength={50} style={inputStyle} onFocus={inputFocus} onBlur={inputBlur} />
                                </div>
                            </div>

                            {/* Telefon + E-Posta */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                                <div>
                                    <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: 6 }}>
                                        Telefon 1 <span style={{ color: '#f43f5e' }}>*</span>
                                        {iysAktarimAktif && (iysIzinler.some(i => i.iletisimTipi === 'TELEFON1' && i.izinDurumu === 'ONAY' && i.aktifMi)
                                            ? <i className="fas fa-check-circle" title={t('cari.iysIzniAlindi')} style={{ color: '#22c55e' }}></i>
                                            : formData.telefon1 && <button type="button" onClick={() => setIysModal({ iletisimTipi: 'TELEFON1', iletisimVerisi: formData.telefon1 })} style={{ background: 'none', border: '1px solid #e67d21', color: '#e67d21', borderRadius: 4, padding: '1px 7px', cursor: 'pointer', fontSize: 11, lineHeight: '18px' }}>IYS İzni Al</button>)}
                                    </label>
                                    <IysKilitliAlan
                                        aktifIzin={iysIzinler.find(i => i.iletisimTipi === 'TELEFON1' && i.izinDurumu === 'ONAY' && i.aktifMi) ?? null}
                                        cariID={cari?.cariID ?? 0}
                                        takipNo={cari?.takipNo ?? ''}
                                        ekleyenID={user?.kullaniciID ?? 0}
                                        iletisimTipiLabel="Telefon 1"
                                        onUnlocked={async () => { try { const r = await axiosInstance.get(`/api/CariIysIzinleri/cari/${cari?.cariID}`); setIysIzinler(r.data); } catch {} }}
                                        disabled={!iysAktarimAktif}
                                    >{(readOnly) => (
                                        <input type="text" value={formatPhoneNumber(formData.telefon1)} onChange={e => setFormData({ ...formData, telefon1: unformatPhoneNumber(e.target.value) })} required maxLength={14} placeholder="(5XX)XXX-XX-XX" readOnly={readOnly} style={{ ...inputStyle, background: readOnly ? '#f9fafb' : undefined, cursor: readOnly ? 'not-allowed' : undefined }} onFocus={readOnly ? undefined : inputFocus} onBlur={inputBlur} />
                                    )}</IysKilitliAlan>
                                </div>
                                <div>
                                    <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: 6 }}>
                                        E-Posta
                                        {iysAktarimAktif && (iysIzinler.some(i => i.iletisimTipi === 'EPOSTA' && i.izinDurumu === 'ONAY' && i.aktifMi)
                                            ? <i className="fas fa-check-circle" title={t('cari.iysIzniAlindi')} style={{ color: '#22c55e' }}></i>
                                            : formData.ePosta && <button type="button" onClick={() => setIysModal({ iletisimTipi: 'EPOSTA', iletisimVerisi: formData.ePosta! })} style={{ background: 'none', border: '1px solid #e67d21', color: '#e67d21', borderRadius: 4, padding: '1px 7px', cursor: 'pointer', fontSize: 11, lineHeight: '18px' }}>IYS İzni Al</button>)}
                                    </label>
                                    <IysKilitliAlan
                                        aktifIzin={iysIzinler.find(i => i.iletisimTipi === 'EPOSTA' && i.izinDurumu === 'ONAY' && i.aktifMi) ?? null}
                                        cariID={cari?.cariID ?? 0}
                                        takipNo={cari?.takipNo ?? ''}
                                        ekleyenID={user?.kullaniciID ?? 0}
                                        iletisimTipiLabel="E-Posta"
                                        onUnlocked={async () => { try { const r = await axiosInstance.get(`/api/CariIysIzinleri/cari/${cari?.cariID}`); setIysIzinler(r.data); } catch {} }}
                                        disabled={!iysAktarimAktif}
                                    >{(readOnly) => (
                                        <input type="email" value={formData.ePosta || ''} onChange={e => setFormData({ ...formData, ePosta: e.target.value || null })} maxLength={100} readOnly={readOnly} style={{ ...inputStyle, background: readOnly ? '#f9fafb' : undefined, cursor: readOnly ? 'not-allowed' : undefined }} onFocus={readOnly ? undefined : inputFocus} onBlur={inputBlur} />
                                    )}</IysKilitliAlan>
                                </div>
                            </div>

                            {/* Telefon 2 + Alt Kategori */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                                <div>
                                    <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: 6 }}>
                                        Telefon 2
                                        {iysAktarimAktif && (iysIzinler.some(i => i.iletisimTipi === 'TELEFON2' && i.izinDurumu === 'ONAY' && i.aktifMi)
                                            ? <i className="fas fa-check-circle" title={t('cari.iysIzniAlindi')} style={{ color: '#22c55e' }}></i>
                                            : formData.telefon2 && <button type="button" onClick={() => setIysModal({ iletisimTipi: 'TELEFON2', iletisimVerisi: formData.telefon2! })} style={{ background: 'none', border: '1px solid #e67d21', color: '#e67d21', borderRadius: 4, padding: '1px 7px', cursor: 'pointer', fontSize: 11, lineHeight: '18px' }}>IYS İzni Al</button>)}
                                    </label>
                                    <IysKilitliAlan
                                        aktifIzin={iysIzinler.find(i => i.iletisimTipi === 'TELEFON2' && i.izinDurumu === 'ONAY' && i.aktifMi) ?? null}
                                        cariID={cari?.cariID ?? 0}
                                        takipNo={cari?.takipNo ?? ''}
                                        ekleyenID={user?.kullaniciID ?? 0}
                                        iletisimTipiLabel="Telefon 2"
                                        onUnlocked={async () => { try { const r = await axiosInstance.get(`/api/CariIysIzinleri/cari/${cari?.cariID}`); setIysIzinler(r.data); } catch {} }}
                                        disabled={!iysAktarimAktif}
                                    >{(readOnly) => (
                                        <input type="text" value={formData.telefon2 ? formatPhoneNumber(formData.telefon2) : ''} onChange={e => setFormData({ ...formData, telefon2: e.target.value ? unformatPhoneNumber(e.target.value) : null })} maxLength={14} placeholder="(5XX)XXX-XX-XX" readOnly={readOnly} style={{ ...inputStyle, background: readOnly ? '#f9fafb' : undefined, cursor: readOnly ? 'not-allowed' : undefined }} onFocus={readOnly ? undefined : inputFocus} onBlur={inputBlur} />
                                    )}</IysKilitliAlan>
                                </div>
                                <div>
                                    <label style={labelStyle}>Alt Kategori</label>
                                    <select value={formData.turu2 || ''} onChange={e => setFormData({ ...formData, turu2: e.target.value || null })} style={{ ...inputStyle }} onFocus={inputFocus} onBlur={inputBlur}>
                                        <option value="">{t('label.seciniz')}</option>
                                        {cariAltKategoriler.map(k => (
                                            <option key={k.cariAltKategoriID} value={k.altKategoriAdi}>{k.altKategoriAdi}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {/* IYS Kayıtları */}
                            <IysKayitListesi izinler={iysIzinler} />

                            {/* Adres (tam genişlik) */}
                            <div>
                                <label style={labelStyle}>{t('label.adres')}</label>
                                <textarea rows={2} value={formData.adres || ''} onChange={e => setFormData({ ...formData, adres: e.target.value || null })} maxLength={500} style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }} onFocus={inputFocus} onBlur={inputBlur} />
                            </div>

                            {/* Aktif Toggle */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                <div
                                    onClick={() => setFormData({ ...formData, aktifMi: !formData.aktifMi })}
                                    style={{ width: 44, height: 24, borderRadius: 12, background: formData.aktifMi ? '#cd6906' : '#d1d5db', cursor: 'pointer', position: 'relative', transition: 'background 0.2s', flexShrink: 0 }}
                                >
                                    <div style={{ position: 'absolute', top: 3, left: formData.aktifMi ? 23 : 3, width: 18, height: 18, borderRadius: '50%', background: '#fff', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
                                </div>
                                <span style={{ fontSize: 13, fontWeight: 500, color: '#374151' }}>Aktif</span>
                            </div>

                        </div>

                        {/* Footer */}
                        <div style={{ flexShrink: 0, padding: '20px', borderTop: '1px solid #e5e7eb', background: '#fff', display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
                            <button
                                type="button"
                                onClick={() => { setShowModal(false); navigate('/cariler'); }}
                                style={{ padding: '10px 20px', background: '#fff', border: '1px solid #d1d5db', color: '#374151', borderRadius: 8, fontSize: 14, fontWeight: 500, cursor: 'pointer', transition: 'background 0.15s' }}
                                onMouseEnter={e => (e.currentTarget.style.background = '#f9fafb')}
                                onMouseLeave={e => (e.currentTarget.style.background = '#fff')}
                            >{t('btn.iptal')}</button>
                            <button
                                type="submit"
                                style={{ padding: '10px 24px', background: '#cd6906', border: 'none', color: '#fff', borderRadius: 8, fontSize: 14, fontWeight: 500, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, boxShadow: '0 1px 3px rgba(205,105,6,0.3)', transition: 'background 0.15s, box-shadow 0.15s' }}
                                onMouseEnter={e => { e.currentTarget.style.background = '#a85505'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(205,105,6,0.4)'; }}
                                onMouseLeave={e => { e.currentTarget.style.background = '#cd6906'; e.currentTarget.style.boxShadow = '0 1px 3px rgba(205,105,6,0.3)'; }}
                            ><Check size={16} />{t('btn.kaydet')}</button>
                        </div>
                    </Form>
                </div>
            </div>
        );
    }

    // Barter/Mahsup ödeme kaydı: cari ekstreden gizle, sadece İşlemler'de görünsün
    const isBarterSozlesmeOdeme = (o: Odeme): boolean => o.islemTakipNo.startsWith('ODE-SOZ-');
    const genelToplamBorc = ekstreOzet?.toplamBorc ?? 0;
    const genelToplamAlacak = ekstreOzet?.toplamAlacak ?? 0;
    const genelBakiye = ekstreOzet?.bakiye ?? 0;
    const genelBaYonu = ekstreOzet?.baYonu ?? '';

    return (
        <div>
            <style>{`
                @media (max-width: 768px) {
                    .cari-hareketler-tablo { display: none !important; }
                    .cari-hareketler-kartlar { display: flex !important; }
                }
                @media (min-width: 769px) {
                    .cari-hareketler-tablo { display: block !important; }
                    .cari-hareketler-kartlar { display: none !important; }
                }
                .cari-hareket-kart {
                    background: #fff;
                    border: 1px solid #e5e7eb;
                    border-radius: 12px;
                    padding: 14px 16px;
                    display: flex;
                    flex-direction: column;
                    gap: 10px;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.04);
                }
                .cari-hareket-kart-row {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    gap: 8px;
                }
                .cari-hareket-kart-label {
                    font-size: 11px;
                    font-weight: 600;
                    color: #9ca3af;
                    letter-spacing: 0.04em;
                    text-transform: uppercase;
                    min-width: 80px;
                }
                .cari-hareket-kart-value {
                    font-size: 13px;
                    color: #374151;
                    font-weight: 500;
                    text-align: right;
                }
            `}</style>
            <section className="content" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                <div className="container-fluid">
                    {/* Geri Dön */}
                    <div style={{ marginBottom: 16 }}>
                        <button
                            onClick={() => navigate(-1)}
                            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '12px', fontWeight: 600, color: '#94a3b8', background: 'none', border: 'none', cursor: 'pointer', padding: 0, transition: 'color 0.15s', letterSpacing: '0.01em' }}
                            onMouseEnter={e => (e.currentTarget.style.color = '#0f172a')}
                            onMouseLeave={e => (e.currentTarget.style.color = '#94a3b8')}
                        >
                            <i className="fas fa-arrow-left" style={{ fontSize: '10px' }}></i> {t('btn.geriDon')}
                        </button>
                    </div>
                    {/* Hero Card - Birleşik Kart */}
                    <Row className="mb-4">
                        <Col md={12}>
                            <div style={{
                                background: 'white',
                                border: '1px solid #f1f5f9',
                                borderRadius: '1.5rem',
                                boxShadow: '0 1px 3px rgba(15,23,42,0.08), 0 4px 20px rgba(15,23,42,0.04)',
                                overflow: 'hidden'
                            }}>
                                {/* Üst Kısım - Avatar ve Bilgiler */}
                                <div style={{
                                    padding: '2rem 2rem 1.5rem',
                                    borderBottom: '1px solid #f1f5f9'
                                }}>
                                    <div className="d-flex align-items-start justify-content-between gap-4 flex-wrap">
                                        <div className="d-flex align-items-start gap-4 flex-1">
                                            
                                            {/* İsim ve Durum */}
                                            <div style={{ flex: 1 }}>
                                                <div className="d-flex align-items-center gap-2 flex-wrap mb-2">
                                                    <h1 style={{
                                                        fontSize: '1.25rem',
                                                        fontWeight: '700',
                                                        color: '#0f172a',
                                                        margin: 0,
                                                        lineHeight: 1.2,
                                                        letterSpacing: '-0.02em'
                                                    }}>
                                                        {cari?.adiSoyadi || t('msg.yukleniyor')}
                                                    </h1>
                                                    
                                                    <span
                                                        style={{
                                                            padding: '0.375rem 0.875rem',
                                                            borderRadius: '9999px',
                                                            fontSize: '0.8125rem',
                                                            fontWeight: '700',
                                                            background: cari?.aktifMi ? '#dcfce7' : '#fef2f2',
                                                            color: cari?.aktifMi ? '#15803d' : '#dc2626',
                                                            border: '1px solid',
                                                            borderColor: cari?.aktifMi ? '#a7f3d0' : '#fecaca',
                                                            lineHeight: 1,
                                                            letterSpacing: '0.02em'
                                                        }}
                                                    >
                                                        {cari?.aktifMi ? t('durum.aktif') : t('durum.pasif')}
                                                    </span>
                                                </div>
                                                
                                                {cari?.firmaAdi && (
                                                    <div style={{
                                                        fontSize: '1rem',
                                                        color: '#64748b',
                                                        fontWeight: '500',
                                                        marginTop: '0.25rem'
                                                    }}>
                                                        {cari.firmaAdi}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        
                                        {/* Action Buttons */}
                                        <div className="d-flex gap-2" style={{ flexShrink: 0 }}>
                                            <IlgiTalepButonu cariID={cari?.cariID} ad={cari?.adiSoyadi ?? ''} />
                                            <KayitRandevuButonu tur="CARI" id={cari?.cariID} ad={cari?.adiSoyadi ?? ''} />
                                            <KayitTakipButonu tur="CARI" anahtar={cari?.cariID} baslik={cari?.adiSoyadi ?? ''} />
                                            <button
                                                onClick={() => setShowDosyaModal(true)}
                                                style={{
                                                    padding: '0.625rem',
                                                    background: 'white',
                                                    border: '1.5px solid #e2e8f0',
                                                    borderRadius: '0.5rem',
                                                    color: '#64748b',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.2s',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontSize: '0.875rem',
                                                    fontWeight: '500'
                                                }}
                                                onMouseOver={(e) => {
                                                    e.currentTarget.style.background = '#f8fafc';
                                                    e.currentTarget.style.borderColor = '#cd6906';
                                                    e.currentTarget.style.color = '#cd6906';
                                                }}
                                                onMouseOut={(e) => {
                                                    e.currentTarget.style.background = 'white';
                                                    e.currentTarget.style.borderColor = '#e2e8f0';
                                                    e.currentTarget.style.color = '#64748b';
                                                }}
                                                title="Dosya Ekle"
                                            >
                                                <FileUp size={18} />
                                            </button>
                                            <button
                                                onClick={() => setShowNotModal(true)}
                                                style={{
                                                    padding: '0.625rem',
                                                    background: 'white',
                                                    border: '1.5px solid #e2e8f0',
                                                    borderRadius: '0.5rem',
                                                    color: '#64748b',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.2s',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontSize: '0.875rem',
                                                    fontWeight: '500'
                                                }}
                                                onMouseOver={(e) => {
                                                    e.currentTarget.style.background = '#f8fafc';
                                                    e.currentTarget.style.borderColor = '#cd6906';
                                                    e.currentTarget.style.color = '#cd6906';
                                                }}
                                                onMouseOut={(e) => {
                                                    e.currentTarget.style.background = 'white';
                                                    e.currentTarget.style.borderColor = '#e2e8f0';
                                                    e.currentTarget.style.color = '#64748b';
                                                }}
                                                title="Not Ekle"
                                            >
                                                <StickyNote size={18} />
                                            </button>
                                            <button
                                                onClick={handleSunumOlustur}
                                                disabled={sunumOlusturuluyor}
                                                style={{
                                                    padding: '0.625rem',
                                                    background: 'white',
                                                    border: '1.5px solid #e2e8f0',
                                                    borderRadius: '0.5rem',
                                                    color: '#64748b',
                                                    cursor: sunumOlusturuluyor ? 'not-allowed' : 'pointer',
                                                    transition: 'all 0.2s',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontSize: '0.875rem',
                                                    fontWeight: '500'
                                                }}
                                                onMouseOver={(e) => {
                                                    e.currentTarget.style.background = '#f8fafc';
                                                    e.currentTarget.style.borderColor = '#cd6906';
                                                    e.currentTarget.style.color = '#cd6906';
                                                }}
                                                onMouseOut={(e) => {
                                                    e.currentTarget.style.background = 'white';
                                                    e.currentTarget.style.borderColor = '#e2e8f0';
                                                    e.currentTarget.style.color = '#64748b';
                                                }}
                                                title="Sunum Oluştur"
                                            >
                                                <MonitorPlay size={18} />
                                            </button>
                                            <button
                                                onClick={() => setShowModal(true)}
                                                style={{
                                                    padding: '0.625rem 1.125rem',
                                                    background: '#cd6906',
                                                    border: 'none',
                                                    borderRadius: '0.5rem',
                                                    color: 'white',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.2s',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '0.5rem',
                                                    fontWeight: '600',
                                                    fontSize: '0.875rem',
                                                    boxShadow: '0 2px 4px rgba(205, 105, 6, 0.2)'
                                                }}
                                                onMouseOver={(e) => {
                                                    e.currentTarget.style.background = '#b85d05';
                                                    e.currentTarget.style.boxShadow = '0 4px 8px rgba(205, 105, 6, 0.3)';
                                                }}
                                                onMouseOut={(e) => {
                                                    e.currentTarget.style.background = '#cd6906';
                                                    e.currentTarget.style.boxShadow = '0 2px 4px rgba(205, 105, 6, 0.2)';
                                                }}
                                                title={t('btn.duzenle')}
                                            >
                                                <Edit size={16} />
                                                {t('btn.duzenle')}
                                            </button>
                                            {id && id !== 'yeni' && id !== '0' && (
                                            <button
                                                onClick={() => setShowDegisiklikGecmisi(true)}
                                                title="Bilgi değişiklik geçmişi"
                                                style={{ padding: '0.625rem 0.9rem', background: '#fff', color: '#475569', border: '1px solid #e2e8f0', borderRadius: '0.5rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.4rem', fontWeight: 600, fontSize: '0.875rem' }}
                                            >
                                                <i className="fas fa-clock-rotate-left"></i> Geçmiş
                                            </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                
                                {/* Alt Kısım - İletişim Bilgileri */}
                                <div style={{ padding: '1.75rem 2rem' }}>
                                    <h3 style={{
                                        fontSize: '0.8125rem',
                                        fontWeight: '700',
                                        color: '#94a3b8',
                                        letterSpacing: '0.1em',
                                        marginBottom: '1.25rem',
                                        textTransform: 'uppercase'
                                    }}>{t('label.iletisimBilgileri')}</h3>
                                    <Row>
                                        {/* Telefon 1 - 1/4 */}
                                        <Col md={3}>
                                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                                                <div style={{
                                                    width: '2rem',
                                                    height: '2rem',
                                                    borderRadius: '0.5rem',
                                                    background: '#fff8f0',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    flexShrink: 0
                                                }}>
                                                    <Phone size={16} style={{ color: '#cd6906' }} />
                                                </div>
                                                <div style={{ flex: 1 }}>
                                                    <div style={{ fontSize: '0.6875rem', color: '#94a3b8', marginBottom: '0.375rem', fontWeight: '600', letterSpacing: '0.05em' }}>{t('label.telefon', 'Telefon').toUpperCase()} 1</div>
                                                    <div style={{ fontSize: '0.9375rem', color: '#0f172a', fontWeight: '600' }}>
                                                        {formatPhoneNumber(cari.telefon1)}
                                                    </div>
                                                </div>
                                            </div>
                                            {/* Telefon 2 */}
                                            {cari.telefon2 && (
                                                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', marginTop: '1.25rem' }}>
                                                    <div style={{
                                                        width: '2rem',
                                                        height: '2rem',
                                                        borderRadius: '0.5rem',
                                                        background: '#fff8f0',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        flexShrink: 0
                                                    }}>
                                                        <Phone size={16} style={{ color: '#cd6906' }} />
                                                    </div>
                                                    <div style={{ flex: 1 }}>
                                                        <div style={{ fontSize: '0.6875rem', color: '#94a3b8', marginBottom: '0.375rem', fontWeight: '600', letterSpacing: '0.05em' }}>{t('label.telefon', 'Telefon').toUpperCase()} 2</div>
                                                        <div style={{ fontSize: '0.9375rem', color: '#0f172a', fontWeight: '600' }}>
                                                            {formatPhoneNumber(cari.telefon2)}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </Col>
                                        
                                        {/* E-Posta - 1/4 */}
                                        <Col md={3}>
                                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                                                <div style={{
                                                    width: '2rem',
                                                    height: '2rem',
                                                    borderRadius: '0.5rem',
                                                    background: '#fff8f0',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    flexShrink: 0
                                                }}>
                                                    <Mail size={16} style={{ color: '#cd6906' }} />
                                                </div>
                                                <div style={{ flex: 1 }}>
                                                    <div style={{ fontSize: '0.6875rem', color: '#94a3b8', marginBottom: '0.375rem', fontWeight: '600', letterSpacing: '0.05em' }}>{t('label.eposta', 'E-Posta').toUpperCase()}</div>
                                                    <div style={{ fontSize: '0.9375rem', color: '#0f172a', fontWeight: '600', wordBreak: 'break-word' }}>
                                                        {cari.ePosta || '-'}
                                                    </div>
                                                </div>
                                            </div>
                                        </Col>
                                        
                                        {/* Adres - 1/2 */}
                                        <Col md={6}>
                                            <div style={{ display: 'flex', alignItems: 'flex-start', gap:'0.75rem' }}>
                                                <div style={{
                                                    width: '2rem',
                                                    height: '2rem',
                                                    borderRadius: '0.5rem',
                                                    background: '#fff8f0',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    flexShrink: 0
                                                }}>
                                                    <MapPin size={16} style={{ color: '#cd6906' }} />
                                                </div>
                                                <div style={{ flex: 1 }}>
                                                    <div style={{ fontSize: '0.6875rem', color: '#94a3b8', marginBottom: '0.375rem', fontWeight: '600', letterSpacing: '0.05em' }}>{t('label.adres', 'Adres').toUpperCase()}</div>
                                                    <div style={{ fontSize: '0.9375rem', color: '#0f172a', lineHeight: '1.6', fontWeight: '500' }}>
                                                        {cari.adres || '-'}
                                                    </div>
                                                </div>
                                            </div>
                                        </Col>
                                    </Row>
                                </div>
                            </div>
                        </Col>
                    </Row>

                    {/* ---- Ana Sekme Navigasyonu ---- */}
                    <div style={{ display: 'flex', borderBottom: '2px solid #f1f5f9', background: '#ffffff', margin: '1.5rem 0 0 0', borderRadius: '0.875rem 0.875rem 0 0', overflow: 'hidden' }}>
                        {([
                            { key: 'ekstre', label: t('label.finansalIslemler'), icon: 'fas fa-exchange-alt' },
                            { key: 'plan', label: t('cari.odemePlani', 'Ödeme Planı'), icon: 'fas fa-calendar-check' },
                            { key: 'sozlesmeler', label: t('label.sozlesmeler'), icon: 'fas fa-file-contract' },
                            { key: 'sunumlar', label: t('menu.sunumTeklif', 'Sunum & Teklif'), icon: 'fas fa-desktop' },
                            { key: 'aktiviteler', label: t('label.notlar'), icon: 'fas fa-clock' },
                            { key: 'dosyalar', label: t('label.dosyalar'), icon: 'fas fa-paperclip' },
                        ] as { key: 'ekstre' | 'plan' | 'sozlesmeler' | 'sunumlar' | 'aktiviteler' | 'dosyalar'; label: string; icon: string }[]).map(tab => (
                            <button
                                key={tab.key}
                                onClick={() => setActiveMainTab(tab.key)}
                                style={{
                                    padding: '0.875rem 1.375rem',
                                    background: 'transparent',
                                    border: 'none',
                                    borderBottom: activeMainTab === tab.key ? '2.5px solid #cd6906' : '2.5px solid transparent',
                                    color: activeMainTab === tab.key ? '#0f172a' : '#94a3b8',
                                    fontWeight: activeMainTab === tab.key ? '700' : '500',
                                    fontSize: '0.875rem',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.4rem',
                                    transition: 'color 0.15s, border-color 0.15s',
                                    marginBottom: '-2px',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                <i className={tab.icon} style={{ fontSize: '0.75rem' }}></i>
                                {tab.label}
                                {tab.key === 'plan' && cariOdemePlanlari.length > 0 && (
                                    <span style={{ background: '#fef3c7', color: '#92400e', borderRadius: '9999px', fontSize: '0.65rem', fontWeight: 700, padding: '1px 6px', border: '1px solid #fde68a' }}>
                                        {cariOdemePlanlari.length}
                                    </span>
                                )}
                                {tab.key === 'sozlesmeler' && (sozlesmeler.length + (relatedRecords?.kiraSozlesmeleri.length ?? 0)) > 0 && (
                                    <span style={{ background: '#dbeafe', color: '#1d4ed8', borderRadius: '9999px', fontSize: '0.65rem', fontWeight: 700, padding: '1px 6px', border: '1px solid #bfdbfe' }}>
                                        {sozlesmeler.length + (relatedRecords?.kiraSozlesmeleri.length ?? 0)}
                                    </span>
                                )}
                                {tab.key === 'sunumlar' && musteriSunumlari.length > 0 && (
                                    <span style={{ background: '#ede9fe', color: '#6d28d9', borderRadius: '9999px', fontSize: '0.65rem', fontWeight: 700, padding: '1px 6px', border: '1px solid #ddd6fe' }}>
                                        {musteriSunumlari.length}
                                    </span>
                                )}
                            </button>
                        ))}
                    </div>

                    {/* ---- Sunumlar Sekmesi ---- */}
                    {activeMainTab === 'sunumlar' && (
                        <div style={{ background: '#fff', borderRadius: '0 0 0.875rem 0.875rem', padding: '1.25rem 1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
                                <div style={{ fontSize: '0.95rem', fontWeight: 700, color: '#0f172a' }}>
                                    <i className="fas fa-desktop" style={{ marginRight: 8, color: '#cd6906' }} />
                                    {t('menu.sunumTeklif', 'Sunum & Teklif')}
                                </div>
                                <button
                                    onClick={handleSunumOlustur}
                                    disabled={sunumOlusturuluyor}
                                    style={{
                                        padding: '0.45rem 0.9rem', background: '#cd6906', border: 'none', borderRadius: '0.5rem',
                                        color: '#fff', fontSize: '0.8rem', fontWeight: 600,
                                        cursor: sunumOlusturuluyor ? 'not-allowed' : 'pointer', opacity: sunumOlusturuluyor ? 0.6 : 1,
                                    }}>
                                    <i className="fas fa-plus" style={{ marginRight: 6 }} />
                                    {sunumOlusturuluyor ? 'Oluşturuluyor...' : 'Sunum Oluştur'}
                                </button>
                            </div>

                            {sunumlarLoading ? (
                                <div style={{ padding: '2rem', textAlign: 'center', color: '#94a3b8', fontSize: '0.85rem' }}>
                                    <i className="fas fa-spinner fa-spin" style={{ marginRight: 8 }} />Yükleniyor...
                                </div>
                            ) : musteriSunumlari.length === 0 ? (
                                <div style={{ padding: '2rem', textAlign: 'center', color: '#94a3b8', fontSize: '0.85rem' }}>
                                    <i className="fas fa-desktop" style={{ fontSize: 26, display: 'block', marginBottom: 10, opacity: 0.5 }} />
                                    Bu müşteri için henüz sunum oluşturulmamış.
                                </div>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                    {musteriSunumlari.map((s: any) => {
                                        const devamEdiyor = s.durum === 'Taslak' || s.durum === 'Devam Ediyor';
                                        const durumRenk = s.durum === 'Olumlu Sonuçlandı' ? { bg: '#f0fdf4', color: '#15803d' }
                                            : s.durum === 'Olumsuz Sonuçlandı' || s.durum === 'İptal Edildi' ? { bg: '#fff1f2', color: '#be123c' }
                                            : s.durum === 'Tamamlandı' ? { bg: '#eff6ff', color: '#1d4ed8' }
                                            : { bg: '#fef3c7', color: '#92400e' };
                                        return (
                                            <div key={s.sunumID}
                                                onClick={() => navigate(`/musteri-sunumlari/${s.sunumID}`)}
                                                style={{
                                                    display: 'flex', alignItems: 'center', gap: 14, padding: '0.75rem 1rem',
                                                    border: '1px solid #e2e8f0', borderRadius: '0.625rem', cursor: 'pointer',
                                                    transition: 'all 0.15s', background: '#fff',
                                                }}
                                                onMouseOver={e => { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.borderColor = '#cd6906'; }}
                                                onMouseOut={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.borderColor = '#e2e8f0'; }}>
                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                                        <span style={{ fontSize: '0.875rem', fontWeight: 600, color: '#0f172a' }}>
                                                            {s.baslik || 'Sunum'}
                                                        </span>
                                                        <span style={{ fontSize: '0.68rem', fontWeight: 700, padding: '2px 9px', borderRadius: '9999px', background: durumRenk.bg, color: durumRenk.color }}>
                                                            {s.durum}
                                                        </span>
                                                        {devamEdiyor && (
                                                            <span style={{ fontSize: '0.68rem', color: '#92400e' }}>
                                                                <i className="fas fa-circle-exclamation" style={{ marginRight: 4 }} />devam ediyor
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: 3 }}>
                                                        {s.takipNo} · {new Date(s.olusturmaTarihi).toLocaleDateString('tr-TR')}
                                                        {typeof s.daireAdet === 'number' ? ` · ${s.daireAdet} öğe` : ''}
                                                        {s.olusturanAdSoyad ? ` · ${s.olusturanAdSoyad}` : ''}
                                                    </div>
                                                </div>
                                                <i className="fas fa-chevron-right" style={{ color: '#cbd5e1', fontSize: '0.8rem' }} />
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Finansal Ekstre / Ödeme Planı Sekmeleri */}
                    {(activeMainTab === 'ekstre' || activeMainTab === 'plan') && (
                    <Row className="mt-3">
                        <Col md={12}>
                            <div style={{
                                background: 'white',
                                border: '1px solid #f1f5f9',
                                borderRadius: '1.25rem',
                                boxShadow: '0 1px 3px rgba(15,23,42,0.07), 0 4px 16px rgba(15,23,42,0.03)',
                                overflow: 'hidden'
                            }}>
                                <div style={{
                                    padding: '1.25rem 1.5rem',
                                    borderBottom: '1px solid #f1f5f9',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    flexWrap: 'wrap',
                                    gap: '1rem'
                                }}>
                                    <h3 style={{
                                        fontSize: '1.125rem',
                                        fontWeight: '700',
                                        color: '#0f172a',
                                        margin: 0,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.5rem'
                                    }}>
                                        <i className="fas fa-exchange-alt" style={{ color: '#cd6906', fontSize: '1rem' }}></i>
                                        {t('cari.hareketleri', 'Cari Hareketleri')}
                                    </h3>
                                    <div style={{ position: 'relative' }}>
                                        <button
                                            onClick={handlePrintRapor}
                                            style={{
                                                padding: '0.625rem 1.125rem',
                                                background: '#cd6906',
                                                border: 'none',
                                                borderRadius: '0.5rem',
                                                color: 'white',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '0.5rem',
                                                fontWeight: '600',
                                                fontSize: '0.875rem',
                                                boxShadow: '0 2px 8px rgba(205,105,6,0.35), 0 1px 2px rgba(205,105,6,0.2)'
                                            }}
                                        >
                                            <i className="fas fa-print" style={{ fontSize: '0.75rem' }}></i>
                                            {t('btn.rapor', 'Rapor Al')}
                                        </button>
                                    </div>
                                </div>
                                {activeMainTab === 'ekstre' && (
                                <div style={{ padding: '1.5rem' }}>
                                    {odemeler.length === 0 && sozlesmeler.filter(s => s.sozlesmeDurumu === 'Taşeron' || s.sozlesmeDurumu === 'Sözleşme').length === 0 ? (
                                        <div style={{
                                            textAlign: 'center',
                                            padding: '4rem 2rem',
                                            color: '#94a3b8',
                                            fontSize: '0.9375rem'
                                        }}>
                                            <i className="fas fa-info-circle" style={{ fontSize: '2.5rem', marginBottom: '1rem', opacity: 0.3 }}></i>
                                            <div style={{ fontWeight: '500' }}>
                                                Bu cariye ait hareket bulunmamaktadır.
                                            </div>
                                        </div>
                                    ) : (
                                        <>
                                            {/* Özet Bilgiler */}
                                            <Row className="mb-4">
                                                <Col md={4}>
                                                    <div style={{
                                                        background: '#ffffff',
                                                        border: '1px solid #fee2e2',
                                                        padding: '1.375rem 1.5rem',
                                                        borderRadius: '16px',
                                                        boxShadow: '0 1px 3px rgba(15,23,42,0.06), 0 1px 2px rgba(15,23,42,0.03)'
                                                    }}>
                                                        <div style={{ fontSize: '0.6875rem', color: '#94a3b8', fontWeight: '600', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '0.625rem' }}>
                                                            {t('cari.toplamBorcOdemeler', 'Toplam Borç (Ödemeler)')}
                                                        </div>
                                                        <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.25rem' }}>
                                                            <div style={{
                                                                fontSize: '1.1rem',
                                                                fontWeight: '800',
                                                                color: '#dc2626',
                                                                fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                                                                lineHeight: 1.1,
                                                                letterSpacing: '-0.02em'
                                                            }}>
                                                                {formatCurrency(genelToplamBorc)}
                                                            </div>
                                                            <span style={{ fontSize: '0.875rem', fontWeight: '500', color: '#9ca3af', marginLeft: '0.125rem' }}>₺</span>
                                                        </div>
                                                    </div>
                                                </Col>
                                                <Col md={4}>
                                                    <div style={{
                                                        background: '#ffffff',
                                                        border: '1px solid #d1fae5',
                                                        padding: '1.375rem 1.5rem',
                                                        borderRadius: '16px',
                                                        boxShadow: '0 1px 3px rgba(15,23,42,0.06), 0 1px 2px rgba(15,23,42,0.03)'
                                                    }}>
                                                        <div style={{ fontSize: '0.6875rem', color: '#94a3b8', fontWeight: '600', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '0.625rem' }}>
                                                            {t('cari.toplamAlacakGiderler', 'Toplam Alacak (Giderler)')}
                                                        </div>
                                                        <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.25rem' }}>
                                                            <div style={{
                                                                fontSize: '1.1rem',
                                                                fontWeight: '800',
                                                                color: '#059669',
                                                                fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                                                                lineHeight: 1.1,
                                                                letterSpacing: '-0.02em'
                                                            }}>
                                                                {formatCurrency(genelToplamAlacak)}
                                                            </div>
                                                            <span style={{ fontSize: '0.875rem', fontWeight: '500', color: '#9ca3af', marginLeft: '0.125rem' }}>₺</span>
                                                        </div>
                                                    </div>
                                                </Col>
                                                <Col md={4}>
                                                    <div style={{
                                                        background: '#ffffff',
                                                        border: genelBaYonu === 'B' ? '1px solid #fee2e2' : genelBaYonu === 'A' ? '1px solid #d1fae5' : '1px solid #e2e8f0',
                                                        padding: '1.375rem 1.5rem',
                                                        borderRadius: '16px',
                                                        boxShadow: '0 1px 3px rgba(15,23,42,0.06), 0 1px 2px rgba(15,23,42,0.03)'
                                                    }}>
                                                        <div style={{ fontSize: '0.6875rem', color: '#94a3b8', fontWeight: '600', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '0.625rem' }}>
                                                            {`${t('label.bakiye', 'Bakiye')}${genelBaYonu ? ' (' + genelBaYonu + ')' : ''}`}
                                                        </div>
                                                        <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.25rem' }}>
                                                            <div style={{
                                                                fontSize: '1.1rem',
                                                                fontWeight: '800',
                                                                color: genelBaYonu === 'B' ? '#dc2626' : genelBaYonu === 'A' ? '#16a34a' : '#6b7280',
                                                                fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                                                                lineHeight: 1.1,
                                                                letterSpacing: '-0.02em'
                                                            }}>
                                                                {formatCurrency(genelBakiye)}
                                                            </div>
                                                            <span style={{ fontSize: '0.875rem', fontWeight: '500', color: '#9ca3af', marginLeft: '0.125rem' }}>₺</span>
                                                        </div>
                                                    </div>
                                                </Col>
                                            </Row>

                                            <div className="cari-hareketler-tablo" style={{
                                                background: 'white',
                                                borderRadius: '14px',
                                                border: '1px solid #f1f5f9',
                                                overflowX: 'auto',
                                                boxShadow: '0 1px 4px rgba(15,23,42,0.06)'
                                            }}>
                                            {/* ── Filtre Barı ── */}
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', borderBottom: '1px solid #e5e7eb', background: '#f8fafc', borderRadius: '12px 12px 0 0', flexWrap: 'wrap' }}>
                                                <span style={{ fontSize: '0.7rem', fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em', marginRight: 4 }}>{t('label.filtre', 'Filtre')}:</span>
                                                {/* Tarih aralığı */}
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                                                    <TrDateInput
                                                        value={hareketFilters.vadeTarihiBaslangic}
                                                        onChange={e => setHareketFilters(f => ({ ...f, vadeTarihiBaslangic: e.target.value }))}
                                                        title={t('label.baslangicTarihi')}
                                                        style={{ padding: '3px 6px', fontSize: '11px', border: '1px solid #e2e8f0', borderRadius: '6px', background: '#fff', outline: 'none', color: '#374151' }}
                                                    />
                                                    <span style={{ color: '#d1d5db', fontSize: '12px' }}>–</span>
                                                    <TrDateInput
                                                        value={hareketFilters.vadeTarihiBitis}
                                                        onChange={e => setHareketFilters(f => ({ ...f, vadeTarihiBitis: e.target.value }))}
                                                        title={t('label.bitisTarihi')}
                                                        style={{ padding: '3px 6px', fontSize: '11px', border: '1px solid #e2e8f0', borderRadius: '6px', background: '#fff', outline: 'none', color: '#374151' }}
                                                    />
                                                </div>
                                                {/* Durum */}
                                                <div style={{ display: 'flex', gap: '4px' }}>
                                                    {(['', 'Bekliyor', 'Odendi'] as const).map(d => (
                                                        <button
                                                            key={d}
                                                            onClick={() => setHareketFilters(f => ({ ...f, odemeDurumu: d }))}
                                                            style={{
                                                                padding: '3px 10px',
                                                                fontSize: '11px',
                                                                fontWeight: 500,
                                                                borderRadius: '20px',
                                                                cursor: 'pointer',
                                                                border: '1px solid',
                                                                transition: 'all 0.15s',
                                                                borderColor: hareketFilters.odemeDurumu === d ? (d === 'Odendi' ? '#a7f3d0' : d === 'Bekliyor' ? '#fde68a' : '#c7d2fe') : '#e2e8f0',
                                                                background: hareketFilters.odemeDurumu === d ? (d === 'Odendi' ? '#ecfdf5' : d === 'Bekliyor' ? '#fef3c7' : '#eef2ff') : '#fff',
                                                                color: hareketFilters.odemeDurumu === d ? (d === 'Odendi' ? '#065f46' : d === 'Bekliyor' ? '#92400e' : '#3730a3') : '#6b7280',
                                                            }}
                                                        >
                                                            {d === '' ? t('label.tumu', 'Tümü') : d === 'Odendi' ? t('durum.odendi', 'Ödendi') : t('durum.bekliyor', 'Bekliyor')}
                                                        </button>
                                                    ))}
                                                </div>
                                                {/* Temizle */}
                                                {(hareketFilters.vadeTarihiBaslangic || hareketFilters.vadeTarihiBitis || hareketFilters.odemeDurumu) && (
                                                    <button
                                                        onClick={() => setHareketFilters({ vadeTarihiBaslangic: '', vadeTarihiBitis: '', odemeTuru: '', odemeDurumu: '' })}
                                                        style={{ marginLeft: 'auto', padding: '3px 10px', fontSize: '11px', fontWeight: 600, border: '1px solid #fca5a5', borderRadius: '6px', background: '#fff', color: '#dc2626', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                                                    >
                                                        <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                                                        {t('btn.filtreTemizle', 'Filtreyi Temizle')}
                                                    </button>
                                                )}
                                            </div>
                                            <table style={{
                                                width: '100%',
                                                borderCollapse: 'collapse',
                                                fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                                                minWidth: 0
                                            }}>
                                                <thead>
                                                    {/* --- Başlık satırı --- */}
                                                    <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                                                        <th style={{ padding: '0.5rem 0.75rem', width: 40 }}>
                                                            {(() => {
                                                                const bekleyenler = odemeler.filter(o => o.odemeDurumu === 'Bekliyor' && !o.sozlesmeOdemesi);
                                                                const tumSecili = bekleyenler.length > 0 && bekleyenler.every(o => selectedOdemeler.has(o.odemeID));
                                                                return (
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={tumSecili}
                                                                        onChange={() => {
                                                                            if (tumSecili) {
                                                                                setSelectedOdemeler(new Set());
                                                                            } else {
                                                                                setSelectedOdemeler(new Set(bekleyenler.map(o => o.odemeID)));
                                                                            }
                                                                        }}
                                                                        title={t('label.tumunuSec')}
                                                                        style={{ cursor: 'pointer', width: 14, height: 14 }}
                                                                    />
                                                                );
                                                            })()}
                                                        </th>
                                                        {[t('label.islemler'), t('label.tarih'), t('label.vadeTarihi', 'Vade Tarihi'), t('label.tur', 'Tür'), t('label.aciklama'), t('label.borc', 'Borç'), t('label.alacak', 'Alacak'), t('label.bakiye', 'Bakiye')].map((h, i) => (
                                                            <th key={i} style={{
                                                                padding: '0.4rem 0.75rem',
                                                                textAlign: i >= 5 ? 'right' : 'left',
                                                                fontSize: '0.6875rem',
                                                                fontWeight: '700',
                                                                color: '#6b7280',
                                                                letterSpacing: '0.05em',
                                                                whiteSpace: 'nowrap',
                                                            }}>{h}</th>
                                                        ))}
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {(() => {
                                                        let cumAlacak = 0, cumBorc = 0;
                                                        type SozlesmeRow = { rowKind: 'sozlesme'; sozlesme: Sozlesme; rowAlacak: null; rowBorc: number };
                                                        type OdemeRow = { rowKind: 'fatura' | 'makbuz'; odeme: Odeme; rowAlacak: number | null; rowBorc: number | null };
                                                        type BekleyenRow = { rowKind: 'bekliyor'; odeme: Odeme; rowAlacak: null; rowBorc: null };
                                                        const expandedRows: Array<SozlesmeRow | OdemeRow | BekleyenRow> = [];
                                                        // Sözleşme ve Taşeron satırları (salt okunur, en başta) — Rezerve hariç
                                                        sozlesmeler
                                                            .filter(s => s.sozlesmeDurumu === 'Taşeron' || s.sozlesmeDurumu === 'Sözleşme')
                                                            .forEach(s => {
                                                                expandedRows.push({ rowKind: 'sozlesme', sozlesme: s, rowAlacak: null, rowBorc: s.sozlesmeFiyati });
                                                            });
                                                        odemeler.filter(o => {
                                                            if (o.sozlesmeOdemesi) return false;
                                                            if (isBarterSozlesmeOdeme(o)) return false;
                                                            if (hareketFilters.odemeTuru && o.odemeTuru !== hareketFilters.odemeTuru) return false;
                                                            if (hareketFilters.odemeDurumu && o.odemeDurumu !== hareketFilters.odemeDurumu) return false;
                                                            if (hareketFilters.vadeTarihiBaslangic && o.vadeTarihi) {
                                                                if (new Date(o.vadeTarihi) < new Date(hareketFilters.vadeTarihiBaslangic)) return false;
                                                            }
                                                            if (hareketFilters.vadeTarihiBitis && o.vadeTarihi) {
                                                                if (new Date(o.vadeTarihi) > new Date(hareketFilters.vadeTarihiBitis)) return false;
                                                            }
                                                            return true;
                                                        }).forEach(odeme => {
                                                            if (odeme.islemTipi === 'Gider') {
                                                                // Gider → Alacak
                                                                expandedRows.push({ rowKind: 'fatura', odeme, rowAlacak: odeme.miktar, rowBorc: null });
                                                            } else if (odeme.islemTipi === 'Gelir') {
                                                                // Gelir → Borç
                                                                expandedRows.push({ rowKind: 'fatura', odeme, rowAlacak: null, rowBorc: odeme.miktar });
                                                            } else if (odeme.islemTipi === 'Tahsilat' && odeme.odemeDurumu === 'Odendi') {
                                                                // Tahsilat + Odendi → Alacak
                                                                expandedRows.push({ rowKind: 'makbuz', odeme, rowAlacak: odeme.miktar, rowBorc: null });
                                                            } else if (odeme.islemTipi === 'Odeme' && odeme.odemeDurumu === 'Odendi') {
                                                                // Ödeme + Odendi → Borç
                                                                expandedRows.push({ rowKind: 'makbuz', odeme, rowAlacak: null, rowBorc: odeme.miktar });
                                                            } else if ((odeme.islemTipi === 'Tahsilat' || odeme.islemTipi === 'Odeme') && odeme.odemeDurumu === 'Bekliyor') {
                                                                // Bekleyen tahsilat/ödeme — bakiyeyi etkilemez, düzenlenebilir
                                                                expandedRows.push({ rowKind: 'bekliyor', odeme, rowAlacak: null, rowBorc: null });
                                                            }
                                                        });
                                                        return expandedRows.map((row) => {
                                                            cumAlacak += row.rowAlacak ?? 0;
                                                            cumBorc += row.rowBorc ?? 0;
                                                            const satirBakiyesi = Math.abs(cumBorc - cumAlacak);
                                                            const baYonu = cumBorc > cumAlacak ? 'B' : cumAlacak > cumBorc ? 'A' : '';
                                                            const bakiyeRengi = cumBorc > cumAlacak ? '#dc2626' : cumAlacak > cumBorc ? '#059669' : '#6b7280';
                                                            if (row.rowKind === 'sozlesme') {
                                                                const s = row.sozlesme;
                                                                const projeAdi = s.daire?.blok?.proje?.projeAdi;
                                                                const blokAdi = s.daire?.blok?.blokAdi;
                                                                const daireNo = s.daire?.daireNo;
                                                                const aciklama = [s.sozlesmeDurumu === 'Taşeron' ? t('cari.taseronSozlesme', 'Taşeron Sözleşmesi') : t('cari.satisSozlesme', 'Satış Sözleşmesi'), projeAdi, blokAdi && daireNo ? `${blokAdi}-${daireNo}` : daireNo, s.sozlesmeTakipNo].filter(Boolean).join(' · ');
                                                                return (
                                                            <tr key={`sozlesme-${s.sozlesmeID}`} style={{ borderBottom: '1px solid #fde68a', background: '#fffbeb' }}>
                                                                <td style={{ padding: '0.4rem 0.4rem', width: 36 }}></td>
                                                                <td style={{ padding: '0.4rem 0.5rem', whiteSpace: 'nowrap' }}>
                                                                    <span style={{ fontSize: '0.65rem', color: '#92400e', fontWeight: 600, padding: '2px 9px', background: '#fef3c7', borderRadius: '9999px', border: '1px solid #fde68a', whiteSpace: 'nowrap' }}>🔒 {t('cari.sozlesme', 'Sözleşme')}</span>
                                                                </td>
                                                                <td style={{ padding: '0.4rem 0.5rem', whiteSpace: 'nowrap' }}>
                                                                    <span style={{ fontSize: '0.8125rem', color: '#374151', fontWeight: '500' }}>
                                                                        {s.sozlesmeTarihi ? new Date(s.sozlesmeTarihi).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—'}
                                                                    </span>
                                                                </td>
                                                                <td style={{ padding: '0.4rem 0.5rem' }}><span style={{ fontSize: '0.8125rem', color: '#9ca3af' }}>—</span></td>
                                                                <td style={{ padding: '0.4rem 0.5rem' }}><span style={{ fontSize: '0.8125rem', color: '#9ca3af' }}>—</span></td>
                                                                <td style={{ padding: '0.4rem 0.5rem', maxWidth: '160px', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                                                                    <span style={{ fontSize: '0.8125rem', color: '#374151' }} title={aciklama}>{aciklama}</span>
                                                                </td>
                                                                <td style={{ padding: '0.4rem 0.5rem', textAlign: 'right', whiteSpace: 'nowrap' }}>
                                                                    <span style={{ fontSize: '0.8125rem', fontWeight: '600', color: '#dc2626' }}>
                                                                        {formatCurrency(row.rowBorc)} TL
                                                                    </span>
                                                                </td>
                                                                <td style={{ padding: '0.4rem 0.5rem', textAlign: 'right', whiteSpace: 'nowrap' }}>
                                                                    <span style={{ fontSize: '0.8125rem', color: '#9ca3af' }}>—</span>
                                                                </td>
                                                                <td style={{ padding: '0.4rem 0.5rem', textAlign: 'right', whiteSpace: 'nowrap' }}>
                                                                    <span style={{ fontSize: '0.8125rem', fontWeight: '700', color: bakiyeRengi }}>
                                                                        {formatCurrency(satirBakiyesi)} TL
                                                                        {baYonu && <span style={{ fontSize: '0.65rem', marginLeft: 2 }}>({baYonu})</span>}
                                                                    </span>
                                                                </td>
                                                            </tr>
                                                                );
                                                            }
                                                            if (row.rowKind === 'bekliyor') {
                                                                const o = row.odeme;
                                                                const isTahsilat = o.islemTipi === 'Tahsilat';
                                                                const aciklamaB = [o.ilgiliProje?.projeAdi, o.hizmetUrunAdi, o.aciklama].filter(Boolean).join(' · ') || (isTahsilat ? 'Tahsilat' : 'Ödeme');
                                                                const isOverdueB = o.vadeTarihi && new Date(o.vadeTarihi) < new Date();
                                                                return (
                                                            <tr key={`bekliyor-${o.odemeID}`} style={{ borderBottom: '1px solid #fde68a', background: isOverdueB ? '#fff7ed' : '#fefce8' }}>
                                                                <td style={{ padding: '0.4rem 0.4rem', width: 36 }}>
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={selectedOdemeler.has(o.odemeID)}
                                                                        onChange={(e) => {
                                                                            e.stopPropagation();
                                                                            setSelectedOdemeler(prev => {
                                                                                const next = new Set(prev);
                                                                                if (next.has(o.odemeID)) next.delete(o.odemeID);
                                                                                else next.add(o.odemeID);
                                                                                return next;
                                                                            });
                                                                        }}
                                                                        style={{ cursor: 'pointer', width: 13, height: 13 }}
                                                                    />
                                                                </td>
                                                                <td style={{ padding: '0.4rem 0.5rem', whiteSpace: 'nowrap' }}>
                                                                    <button
                                                                        onClick={() => handleEditOdeme(o)}
                                                                        style={{ background: '#fef3c7', border: '1px solid #fde68a', color: '#92400e', cursor: 'pointer', padding: '2px 10px', borderRadius: '5px', fontSize: '0.7rem', fontWeight: '600', transition: 'all 0.15s ease', whiteSpace: 'nowrap' }}
                                                                        onMouseEnter={(e) => { e.currentTarget.style.background = '#fde68a'; }}
                                                                        onMouseLeave={(e) => { e.currentTarget.style.background = '#fef3c7'; }}
                                                                    >⏳ {t('durum.bekliyor', 'Bekliyor')}</button>
                                                                </td>
                                                                <td style={{ padding: '0.4rem 0.5rem', whiteSpace: 'nowrap' }}>
                                                                    <span style={{ fontSize: '0.8125rem', color: '#374151', fontWeight: '500' }}>
                                                                        {o.islemTarihi ? new Date(o.islemTarihi).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—'}
                                                                    </span>
                                                                </td>
                                                                <td style={{ padding: '0.4rem 0.5rem', whiteSpace: 'nowrap' }}>
                                                                    <span style={{ fontSize: '0.8125rem', color: isOverdueB ? '#dc2626' : '#374151', fontWeight: isOverdueB ? '600' : '500' }}>
                                                                        {o.vadeTarihi ? new Date(o.vadeTarihi).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—'}
                                                                    </span>
                                                                </td>
                                                                <td style={{ padding: '0.4rem 0.5rem', whiteSpace: 'nowrap' }}>
                                                                    <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: '999px', fontSize: '0.6875rem', fontWeight: 700, background: '#fef9c3', color: '#854d0e', border: '1px solid #fde68a' }}>
                                                                        {isTahsilat ? t('cari.tahsilat', 'Tahsilat') : t('cari.odeme', 'Ödeme')}
                                                                    </span>
                                                                </td>
                                                                <td style={{ padding: '0.4rem 0.5rem', maxWidth: '160px', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                                                                    <span style={{ fontSize: '0.8125rem', color: '#374151' }} title={aciklamaB}>{aciklamaB}</span>
                                                                </td>
                                                                <td style={{ padding: '0.4rem 0.5rem', textAlign: 'right', whiteSpace: 'nowrap' }}>
                                                                    {!isTahsilat
                                                                        ? <span style={{ fontSize: '0.8125rem', fontWeight: '600', color: '#b45309', fontStyle: 'italic' }}>{formatCurrency(o.miktar)} TL</span>
                                                                        : <span style={{ fontSize: '0.8125rem', color: '#9ca3af' }}>—</span>}
                                                                </td>
                                                                <td style={{ padding: '0.4rem 0.5rem', textAlign: 'right', whiteSpace: 'nowrap' }}>
                                                                    {isTahsilat
                                                                        ? <span style={{ fontSize: '0.8125rem', fontWeight: '600', color: '#b45309', fontStyle: 'italic' }}>{formatCurrency(o.miktar)} TL</span>
                                                                        : <span style={{ fontSize: '0.8125rem', color: '#9ca3af' }}>—</span>}
                                                                </td>
                                                                <td style={{ padding: '0.4rem 0.5rem', textAlign: 'right', whiteSpace: 'nowrap' }}>
                                                                    <span style={{ fontSize: '0.75rem', color: '#9ca3af', fontStyle: 'italic' }}>{t('durum.bekliyor', 'Bekliyor')}</span>
                                                                </td>
                                                            </tr>
                                                                );
                                                            }
                                                            const { odeme, rowAlacak, rowBorc } = row;
                                                            const isMakbuz = row.rowKind === 'makbuz';
                                                            const aciklamaParts = [
                                                                isMakbuz ? t('cari.odemeMakbuzu', 'Ödeme Makbuzu') : null,
                                                                odeme.ilgiliProje?.projeAdi,
                                                                odeme.hizmetUrunAdi,
                                                                odeme.aciklama
                                                            ].filter(Boolean).join(' · ') || t('cari.odeme', 'Ödeme');
                                                            return (
                                                        <tr key={`${odeme.odemeID}-${row.rowKind}`} style={{
                                                            borderBottom: '1px solid #e5e7eb',
                                                            background: isMakbuz ? '#f0fdf4' : (selectedOdemeler.has(odeme.odemeID) ? '#fef3c7' : 'transparent')
                                                        }}
                                                        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f0f9ff'; }}
                                                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = isMakbuz ? '#f0fdf4' : (selectedOdemeler.has(odeme.odemeID) ? '#fef3c7' : 'transparent'); }}
                                                        >
                                                            <td style={{ padding: '0.4rem 0.4rem', width: 36 }}>
                                                                {!isMakbuz && odeme.odemeDurumu === 'Bekliyor' && (
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={selectedOdemeler.has(odeme.odemeID)}
                                                                        onChange={(e) => {
                                                                            e.stopPropagation();
                                                                            setSelectedOdemeler(prev => {
                                                                                const next = new Set(prev);
                                                                                if (next.has(odeme.odemeID)) next.delete(odeme.odemeID);
                                                                                else next.add(odeme.odemeID);
                                                                                return next;
                                                                            });
                                                                        }}
                                                                        style={{ cursor: 'pointer', width: 13, height: 13 }}
                                                                    />
                                                                )}
                                                            </td>
                                                            <td style={{ padding: '0.4rem 0.5rem', whiteSpace: 'nowrap' }}>
                                                                {!isMakbuz && odeme.odemeDurumu === 'Bekliyor' ? (
                                                                    <button
                                                                        onClick={() => handleEditOdeme(odeme)}
                                                                        style={{ background: '#f8fafc', border: '1px solid #e2e8f0', color: '#374151', cursor: 'pointer', padding: '2px 10px', borderRadius: '5px', fontSize: '0.7rem', fontWeight: '600', transition: 'all 0.15s ease', whiteSpace: 'nowrap' }}
                                                                        onMouseEnter={(e) => { e.currentTarget.style.background = '#fff8f0'; e.currentTarget.style.color = '#cd6906'; e.currentTarget.style.borderColor = '#f5d7b0'; }}
                                                                        onMouseLeave={(e) => { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.color = '#374151'; e.currentTarget.style.borderColor = '#e2e8f0'; }}
                                                                    >{t('btn.detay')}</button>
                                                                ) : (
                                                                    <span style={{ fontSize: '0.65rem', color: isMakbuz ? '#059669' : '#9ca3af', fontWeight: 600, padding: isMakbuz ? '2px 9px' : '0', background: isMakbuz ? '#d1fae5' : 'transparent', borderRadius: '9999px', border: isMakbuz ? '1px solid #a7f3d0' : 'none', whiteSpace: 'nowrap' }}>
                                                                        {isMakbuz ? t('cari.makbuz', 'Makbuz') : '—'}
                                                                    </span>
                                                                )}
                                                            </td>
                                                            <td style={{ padding: '0.4rem 0.5rem', whiteSpace: 'nowrap' }}>
                                                                <span style={{ fontSize: '0.8125rem', color: '#374151', fontWeight: '500' }}>
                                                                    {odeme.islemTarihi ? new Date(odeme.islemTarihi).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—'}
                                                                </span>
                                                            </td>
                                                            <td style={{ padding: '0.4rem 0.5rem', whiteSpace: 'nowrap' }}>
                                                                <span style={{ fontSize: '0.8125rem', color: '#374151', fontWeight: '500' }}>
                                                                    {odeme.vadeTarihi ? new Date(odeme.vadeTarihi).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—'}
                                                                </span>
                                                            </td>
                                                            <td style={{ padding: '0.4rem 0.5rem', whiteSpace: 'nowrap' }}>
                                                                {(() => {
                                                                    const tur = odeme.islemTipi || (odeme.odemeYonu === 'Tahsilat' ? 'Tahsilat' : 'Odeme');
                                                                    const turColors: Record<string, { bg: string; color: string; border: string }> = {
                                                                        'Gelir':    { bg: '#dcfce7', color: '#15803d', border: '#bbf7d0' },
                                                                        'Tahsilat': { bg: '#dbeafe', color: '#1d4ed8', border: '#bfdbfe' },
                                                                        'Gider':    { bg: '#fee2e2', color: '#dc2626', border: '#fca5a5' },
                                                                        'Ödeme':    { bg: '#fef9c3', color: '#854d0e', border: '#fde68a' },
                                                                    };
                                                                    const turLabel: Record<string, string> = {
                                                                        'Gelir':    t('cari.gelir', 'Gelir'),
                                                                        'Tahsilat': t('cari.tahsilat', 'Tahsilat'),
                                                                        'Gider':    t('cari.gider', 'Gider'),
                                                                        'Ödeme':    t('cari.odeme', 'Ödeme'),
                                                                        'Odeme':    t('cari.odeme', 'Ödeme'),
                                                                    };
                                                                    const c = turColors[tur] || { bg: '#f1f5f9', color: '#374151', border: '#e2e8f0' };
                                                                    return <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: '999px', fontSize: '0.6875rem', fontWeight: 700, background: c.bg, color: c.color, border: `1px solid ${c.border}` }}>{turLabel[tur] || tur}</span>;
                                                                })()}
                                                            </td>
                                                            <td style={{ padding: '0.4rem 0.5rem', maxWidth: '160px', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                                                                <span style={{ fontSize: '0.8125rem', color: isMakbuz ? '#065f46' : '#374151' }} title={aciklamaParts}>
                                                                    {aciklamaParts}
                                                                </span>
                                                            </td>
                                                            <td style={{ padding: '0.4rem 0.5rem', textAlign: 'right', whiteSpace: 'nowrap' }}>
                                                                <span style={{ fontSize: '0.8125rem', fontWeight: '600', color: '#dc2626' }}>
                                                                    {rowBorc != null ? `${formatCurrency(rowBorc)} ${odeme.paraBirimi?.birim || 'TL'}` : '—'}
                                                                </span>
                                                            </td>
                                                            <td style={{ padding: '0.4rem 0.5rem', textAlign: 'right', whiteSpace: 'nowrap' }}>
                                                                <span style={{ fontSize: '0.8125rem', fontWeight: '600', color: '#059669' }}>
                                                                    {rowAlacak != null ? `${formatCurrency(rowAlacak)} ${odeme.paraBirimi?.birim || 'TL'}` : '—'}
                                                                </span>
                                                            </td>
                                                            <td style={{ padding: '0.4rem 0.5rem', textAlign: 'right', whiteSpace: 'nowrap' }}>
                                                                <span style={{ fontSize: '0.8125rem', fontWeight: '700', color: bakiyeRengi }}>
                                                                    {formatCurrency(satirBakiyesi)} {odeme.paraBirimi?.birim || 'TL'}
                                                                    {baYonu && <span style={{ fontSize: '0.65rem', marginLeft: 2 }}>({baYonu})</span>}
                                                                </span>
                                                            </td>
                                                        </tr>
                                                            );
                                                        });
                                                    })()}
                                                </tbody>
                                                <tfoot>
                                                    <tr style={{ borderTop: '1px solid #e2e8f0', background: '#f8fafc' }}>
                                                        <td colSpan={6} style={{ padding: '0.6rem 0.5rem', fontSize: '0.7rem', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Genel Toplam</td>
                                                        <td style={{ padding: '0.6rem 0.5rem', textAlign: 'right', whiteSpace: 'nowrap' }}>
                                                            <span style={{ fontSize: '0.875rem', fontWeight: '700', color: '#dc2626' }}>{formatCurrency(genelToplamBorc)} TL</span>
                                                        </td>
                                                        <td style={{ padding: '0.6rem 0.5rem', textAlign: 'right', whiteSpace: 'nowrap' }}>
                                                            <span style={{ fontSize: '0.875rem', fontWeight: '700', color: '#059669' }}>{formatCurrency(genelToplamAlacak)} TL</span>
                                                        </td>
                                                        <td style={{ padding: '0.6rem 0.5rem', textAlign: 'right', whiteSpace: 'nowrap' }}>
                                                            <span style={{ fontSize: '0.8125rem', fontWeight: '700', color: genelBaYonu === 'B' ? '#dc2626' : genelBaYonu === 'A' ? '#059669' : '#6b7280' }}>
                                                                {formatCurrency(genelBakiye)} TL
                                                                {genelBaYonu && <span style={{ fontSize: '0.65rem', marginLeft: 2 }}>({genelBaYonu})</span>}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                </tfoot>
                                            </table>
                                        </div>
                                        {/* ── Mobil Kart Görünümü ── */}
                                        <div className="cari-hareketler-kartlar" style={{ flexDirection: 'column', gap: 10, padding: '12px' }}>
                                            {odemeler.filter(o => {
                                                if (hareketFilters.odemeTuru && o.odemeTuru !== hareketFilters.odemeTuru) return false;
                                                if (hareketFilters.odemeDurumu && o.odemeDurumu !== hareketFilters.odemeDurumu) return false;
                                                if (hareketFilters.vadeTarihiBaslangic && o.vadeTarihi) {
                                                    if (new Date(o.vadeTarihi) < new Date(hareketFilters.vadeTarihiBaslangic)) return false;
                                                }
                                                if (hareketFilters.vadeTarihiBitis && o.vadeTarihi) {
                                                    if (new Date(o.vadeTarihi) > new Date(hareketFilters.vadeTarihiBitis)) return false;
                                                }
                                                return true;
                                            }).map(odeme => (
                                                <div key={odeme.odemeID} className="cari-hareket-kart" style={{ background: selectedOdemeler.has(odeme.odemeID) ? '#fef3c7' : '#fff' }}>
                                                    <div className="cari-hareket-kart-row">
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                            {odeme.odemeDurumu === 'Bekliyor' && (
                                                                <input type="checkbox" checked={selectedOdemeler.has(odeme.odemeID)}
                                                                    onChange={e => { e.stopPropagation(); setSelectedOdemeler(prev => { const next = new Set(prev); if (next.has(odeme.odemeID)) next.delete(odeme.odemeID); else next.add(odeme.odemeID); return next; }); }}
                                                                    style={{ cursor: 'pointer', width: 14, height: 14 }} />
                                                            )}
                                                            <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: 6, fontSize: 12, fontWeight: 500, background: odeme.odemeDurumu === 'Odendi' ? '#ecfdf5' : '#fef3c7', color: odeme.odemeDurumu === 'Odendi' ? '#065f46' : '#92400e', border: odeme.odemeDurumu === 'Odendi' ? '1px solid #d1fae5' : '1px solid #fde68a' }}>{odeme.odemeDurumu}</span>
                                                            <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: 6, fontSize: 12, fontWeight: 500, background: '#eff6ff', color: '#1e40af', border: '1px solid #dbeafe' }}>{odemeTuruLabel(odeme.odemeTuru)}</span>
                                                        </div>
                                                        {odeme.odemeDurumu === 'Bekliyor' ? (
                                                            <button onClick={() => handleEditOdeme(odeme)} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', color: '#374151', cursor: 'pointer', padding: '4px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600 }}>{t('btn.detay')}</button>
                                                        ) : (
                                                            <span style={{ fontSize: '0.65rem', color: '#059669', fontWeight: 600, padding: '2px 9px', background: '#d1fae5', borderRadius: '9999px', border: '1px solid #a7f3d0', whiteSpace: 'nowrap' }}>Makbuz</span>
                                                        )}
                                                    </div>
                                                    {odeme.vadeTarihi && (
                                                        <div className="cari-hareket-kart-row">
                                                            <span className="cari-hareket-kart-label">{t('label.vadeTarihi', 'Vade Tarihi')}</span>
                                                            <span className="cari-hareket-kart-value">{new Date(odeme.vadeTarihi).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' })}</span>
                                                        </div>
                                                    )}
                                                    {(odeme.ilgiliProje || odeme.hizmetUrunAdi || odeme.aciklama) && (
                                                        <div className="cari-hareket-kart-row" style={{ alignItems: 'flex-start' }}>
                                                            <span className="cari-hareket-kart-label">{t('label.aciklama')}</span>
                                                            <div style={{ textAlign: 'right', display: 'flex', flexDirection: 'column', gap: 4 }}>
                                                                {odeme.ilgiliProje && <span style={{ fontSize: 12, fontWeight: 500, background: '#f3f4f6', color: '#4b5563', border: '1px solid #e5e7eb', borderRadius: 4, padding: '2px 8px' }}>{odeme.ilgiliProje.projeAdi}</span>}
                                                                {odeme.hizmetUrunAdi && <span style={{ fontSize: 12, fontWeight: 500, background: '#f3f4f6', color: '#4b5563', border: '1px solid #e5e7eb', borderRadius: 4, padding: '2px 8px' }}>{odeme.hizmetUrunAdi}</span>}
                                                                {odeme.aciklama && <span style={{ fontSize: 13, color: '#6b7280' }}>{odeme.aciklama}</span>}
                                                            </div>
                                                        </div>
                                                    )}
                                                    <div className="cari-hareket-kart-row">
                                                        <span className="cari-hareket-kart-label">Tutar</span>
                                                        <span style={{ fontSize: 15, fontWeight: 700, color: '#334155' }}>{formatCurrency(odeme.miktar)} <span style={{ fontSize: 12, color: '#9ca3af', fontWeight: 500 }}>{odeme.paraBirimi?.birim || 'TL'}</span></span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                        {selectedOdemeler.size > 0 && (
                                            <div style={{ margin: '12px 16px', padding: '12px 16px', background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                                                <span style={{ fontSize: 13, fontWeight: 600, color: '#92400e' }}>
                                                    <i className="fas fa-check-square me-2"></i>
                                                    {selectedOdemeler.size} ödeme seçildi
                                                </span>
                                                <div style={{ display: 'flex', gap: 8 }}>
                                                    <button
                                                        onClick={() => setSelectedOdemeler(new Set())}
                                                        style={{ padding: '6px 14px', borderRadius: 6, border: '1px solid #d97706', background: '#fff', fontSize: 12, fontWeight: 500, color: '#92400e', cursor: 'pointer' }}
                                                    >
                                                        Seçimi Temizle
                                                    </button>
                                                    <button
                                                        onClick={handleTopluIptal}
                                                        disabled={topluSilmeLoading}
                                                        style={{ padding: '6px 14px', borderRadius: 6, border: 'none', background: '#dc2626', fontSize: 12, fontWeight: 600, color: '#fff', cursor: topluSilmeLoading ? 'not-allowed' : 'pointer', opacity: topluSilmeLoading ? 0.7 : 1 }}
                                                    >
                                                        {topluSilmeLoading ? <><i className="fas fa-spinner fa-spin me-1"></i>İptal ediliyor...</> : <><i className="fas fa-ban me-1"></i>Ödemeleri İptal Et</>}
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                        </>
                                    )}
                                </div>
                                )} 

                                {/* -- Sekme 2: Odeme Plani -- */}
                                {activeMainTab === 'plan' && (() => {
                                    const taksitler = [...cariOdemePlanlari].sort((a, b) => {
                                        if (!a.vadeTarihi && !b.vadeTarihi) return 0;
                                        if (!a.vadeTarihi) return 1;
                                        if (!b.vadeTarihi) return -1;
                                        return new Date(a.vadeTarihi).getTime() - new Date(b.vadeTarihi).getTime();
                                    });
                                    const toplamPlanlanan = taksitler.reduce((s, o) => s + (o.miktar || 0), 0);
                                    const odenen = taksitler.filter(o => o.odenmeDurumu === true).reduce((s, o) => s + (o.miktar || 0), 0);
                                    const kalan = toplamPlanlanan - odenen;
                                    return (
                                    <div style={{ padding: '1.5rem' }}>
                                        <Row className="mb-3">
                                            <Col md={4}>
                                                <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', padding: '1.25rem', borderRadius: '12px' }}>
                                                    <div style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t('cari.toplamPlanlanan', 'Toplam Planlanan')}</div>
                                                    <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#374151', marginTop: '0.5rem' }}>{formatCurrency(toplamPlanlanan)} <span style={{ fontSize: '0.875rem', color: '#9ca3af' }}>₺</span></div>
                                                    <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '0.25rem' }}>{taksitler.length} {t('cari.taksit', 'taksit')}</div>
                                                </div>
                                            </Col>
                                            <Col md={4}>
                                                <div style={{ background: 'rgba(240,253,244,0.6)', border: '1px solid rgba(22,163,74,0.15)', padding: '1.25rem', borderRadius: '12px' }}>
                                                    <div style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t('sozlesmeDetay.odenen', 'Ödenen')}</div>
                                                    <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#059669', marginTop: '0.5rem' }}>{formatCurrency(odenen)} <span style={{ fontSize: '0.875rem', color: '#9ca3af' }}>₺</span></div>
                                                    <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '0.25rem' }}>{taksitler.filter(o => o.odenmeDurumu === true).length} {t('cari.taksit', 'taksit')}</div>
                                                </div>
                                            </Col>
                                            <Col md={4}>
                                                <div style={{ background: 'rgba(254,242,242,0.6)', border: '1px solid rgba(220,38,38,0.1)', padding: '1.25rem', borderRadius: '12px' }}>
                                                    <div style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t('cari.kalanBekliyor', 'Kalan (Bekliyor)')}</div>
                                                    <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#dc2626', marginTop: '0.5rem' }}>{formatCurrency(kalan)} <span style={{ fontSize: '0.875rem', color: '#9ca3af' }}>₺</span></div>
                                                    <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '0.25rem' }}>{taksitler.filter(o => !o.odenmeDurumu).length} {t('cari.taksit', 'taksit')}</div>
                                                </div>
                                            </Col>
                                        </Row>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                                            {selectedTaksitler.size > 0 ? (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', background: '#fef3c7', border: '1px solid #fde68a', borderRadius: '8px', padding: '0.5rem 0.875rem' }}>
                                                    <span style={{ fontSize: '0.8125rem', fontWeight: 600, color: '#92400e' }}>{selectedTaksitler.size} {t('cari.taksitSecildi', 'taksit seçildi')}</span>
                                                    <button onClick={handleTopluSilTaksit} disabled={taksitTopluSilLoading} style={{ padding: '4px 12px', background: '#dc2626', border: 'none', borderRadius: '6px', color: 'white', cursor: taksitTopluSilLoading ? 'not-allowed' : 'pointer', fontWeight: 600, fontSize: '0.75rem', display: 'inline-flex', alignItems: 'center', gap: '4px', opacity: taksitTopluSilLoading ? 0.7 : 1 }}>
                                                        {taksitTopluSilLoading ? <><i className="fas fa-spinner fa-spin"></i> {t('cari.siliniyor', 'Siliniyor...')}</> : <><i className="fas fa-trash"></i> {t('cari.topluSil', 'Toplu Sil')}</>}
                                                    </button>
                                                    <button onClick={() => setSelectedTaksitler(new Set())} style={{ padding: '4px 10px', background: 'transparent', border: '1px solid #d97706', borderRadius: '6px', color: '#92400e', cursor: 'pointer', fontSize: '0.75rem' }}>{t('btn.temizle')}</button>
                                                </div>
                                            ) : <div />}
                                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                                                <button onClick={handleShowTekliOdemeModal} style={{ padding: '0.5rem 1rem', background: '#2563eb', border: 'none', borderRadius: '0.5rem', color: 'white', cursor: 'pointer', fontWeight: 600, fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                                                    <i className="fas fa-plus"></i> {t('cari.odemeEkle', 'Ödeme Ekle')}
                                                </button>
                                                <button onClick={handleShowTopluPlanlaModal} style={{ padding: '0.5rem 1rem', background: '#059669', border: 'none', borderRadius: '0.5rem', color: 'white', cursor: 'pointer', fontWeight: 600, fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                                                    <i className="fas fa-plus"></i> {t('cari.topluOdemePlanla', 'Toplu Ödeme Planla')}
                                                </button>
                                            </div>
                                        </div>
                                        {taksitler.length === 0 ? (
                                            <div style={{ textAlign: 'center', padding: '3rem 2rem', color: '#94a3b8' }}>
                                                <i className="fas fa-calendar-alt" style={{ fontSize: '2.5rem', opacity: 0.3, display: 'block', marginBottom: '0.75rem' }}></i>
                                                <div style={{ fontWeight: 500 }}>{t('cari.odemePlaniYok', 'Henüz ödeme planı oluşturulmamış.')}</div>
                                            </div>
                                        ) : (
                                            <div style={{ background: 'white', borderRadius: '14px', border: '1px solid #f1f5f9', overflowX: 'auto', boxShadow: '0 1px 4px rgba(15,23,42,0.06)' }}>
                                                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 580 }}>
                                                    <thead>
                                                        <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                                                            <th style={{ padding: '0.5rem 0.75rem', width: 36 }}>
                                                                <input type="checkbox"
                                                                    checked={taksitler.length > 0 && taksitler.every(o => selectedTaksitler.has(o.cariOdemePlaniID))}
                                                                    onChange={e => setSelectedTaksitler(e.target.checked ? new Set(taksitler.map(o => o.cariOdemePlaniID)) : new Set())}
                                                                    style={{ cursor: 'pointer', width: 15, height: 15 }}
                                                                />
                                                            </th>
                                                            {[t('label.vadeTarihi', 'Vade Tarihi'), t('cari.taksit', 'Taksit'), t('label.tur', 'Tür'), t('label.tutar', 'Tutar'), t('label.durum', 'Durum'), t('label.islemler', 'İşlemler')].map((h, i) => (
                                                                <th key={i} style={{ padding: '0.5rem 0.75rem', textAlign: i === 3 ? 'right' : 'left', fontSize: '0.6875rem', fontWeight: 700, color: '#6b7280', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>{h}</th>
                                                            ))}
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {taksitler.map((odeme, idx) => (
                                                            <tr key={odeme.cariOdemePlaniID} style={{ borderBottom: '1px solid #f1f5f9', background: selectedTaksitler.has(odeme.cariOdemePlaniID) ? '#fffbeb' : idx % 2 === 0 ? '#fff' : '#fafafa' }}>
                                                                <td style={{ padding: '0.5rem 0.75rem' }}>
                                                                    <input type="checkbox"
                                                                        checked={selectedTaksitler.has(odeme.cariOdemePlaniID)}
                                                                        onChange={e => setSelectedTaksitler(prev => {
                                                                            const next = new Set(prev);
                                                                            e.target.checked ? next.add(odeme.cariOdemePlaniID) : next.delete(odeme.cariOdemePlaniID);
                                                                            return next;
                                                                        })}
                                                                        style={{ cursor: 'pointer', width: 15, height: 15 }}
                                                                    />
                                                                </td>
                                                                <td style={{ padding: '0.5rem 0.75rem', fontSize: '0.8125rem', color: '#374151', whiteSpace: 'nowrap' }}>
                                                                    {odeme.vadeTarihi ? new Date(odeme.vadeTarihi).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—'}
                                                                </td>
                                                                <td style={{ padding: '0.5rem 0.75rem', fontSize: '0.8125rem', fontWeight: 500, color: '#374151' }}>{odeme.aciklama}</td>
                                                                <td style={{ padding: '0.5rem 0.75rem' }}>
                                                                    <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: '999px', fontSize: '0.6875rem', fontWeight: 600, background: '#f1f5f9', color: '#374151', border: '1px solid #e2e8f0' }}>
                                                                        {odeme.odemeTuru}
                                                                    </span>
                                                                </td>
                                                                <td style={{ padding: '0.5rem 0.75rem', fontSize: '0.8125rem', fontWeight: 600, color: '#374151', textAlign: 'right', whiteSpace: 'nowrap' }}>
                                                                    {formatCurrency(odeme.miktar)} <span style={{ fontWeight: 400, color: '#9ca3af' }}>TL</span>
                                                                </td>
                                                                <td style={{ padding: '0.5rem 0.75rem' }}>
                                                                    <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: '999px', fontSize: '0.6875rem', fontWeight: 700, background: odeme.odenmeDurumu === true ? '#dcfce7' : '#fef3c7', color: odeme.odenmeDurumu === true ? '#15803d' : '#92400e', border: odeme.odenmeDurumu === true ? '1px solid #bbf7d0' : '1px solid #fde68a' }}>
                                                                        {odeme.odenmeDurumu === true ? t('durum.odendi', 'Ödendi') : t('durum.bekliyor', 'Bekliyor')}
                                                                    </span>
                                                                </td>
                                                                <td style={{ padding: '0.5rem 0.75rem', whiteSpace: 'nowrap' }}>
                                                                    {!odeme.odenmeDurumu && (
                                                                        <>
                                                                            <button onClick={() => handleEditCariPlan(odeme)} style={{ padding: '4px 10px', background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0', borderRadius: '6px', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600, marginRight: 6, display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                                                                <i className="fas fa-edit" style={{ fontSize: '0.65rem' }}></i> {t('btn.duzenle', 'Düzenle')}
                                                                            </button>
                                                                            <button onClick={() => handleOdemeYap(odeme)} style={{ padding: '4px 10px', background: '#059669', border: 'none', borderRadius: '6px', color: 'white', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600, marginRight: 6, display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                                                                <i className="fas fa-file-invoice-dollar" style={{ fontSize: '0.7rem' }}></i> {t('cari.odemeYap', 'Ödeme Yap')}
                                                                            </button>
                                                                        </>
                                                                    )}
                                                                    {odeme.odenmeDurumu === true && (
                                                                        <button onClick={() => handleOdemeGeriAl(odeme)} style={{ padding: '4px 10px', background: '#fef3c7', border: '1px solid #fde68a', borderRadius: '6px', color: '#92400e', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600, marginRight: 6, display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                                                            <i className="fas fa-undo" style={{ fontSize: '0.7rem' }}></i> {t('cari.odemeGeriAl', 'Ödeme Geri Al')}
                                                                        </button>
                                                                    )}
                                                                    <button onClick={() => handleDeleteTaksit(odeme)} style={{ padding: '4px 10px', background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: '6px', color: '#dc2626', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                                                        <i className="fas fa-trash" style={{ fontSize: '0.65rem' }}></i> {t('btn.sil', 'Sil')}
                                                                    </button>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                    <tfoot>
                                                        <tr style={{ borderTop: '1px solid #e2e8f0', background: '#f8fafc' }}>
                                                            <td style={{ padding: '0.75rem 0.75rem' }}></td>
                                                            <td colSpan={3} style={{ padding: '0.75rem 0.75rem', fontSize: '0.7rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Toplam</td>
                                                            <td style={{ padding: '0.75rem 0.75rem', textAlign: 'right', fontSize: '0.875rem', fontWeight: 800, color: '#0f172a', whiteSpace: 'nowrap' }}>{formatCurrency(toplamPlanlanan)} TL</td>
                                                            <td colSpan={2} style={{ padding: '0.75rem 0.75rem', fontSize: '0.75rem', color: '#6b7280' }}>
                                                                <span style={{ color: '#059669', fontWeight: 600 }}>{formatCurrency(odenen)} TL ödendi</span>
                                                                {' · '}
                                                                <span style={{ color: '#dc2626', fontWeight: 600 }}>{formatCurrency(kalan)} TL kalan</span>
                                                            </td>
                                                        </tr>
                                                    </tfoot>
                                                </table>
                                            </div>
                                        )}
                                    </div>
                                    );
                                })()}
                            </div>
                        </Col>
                    </Row>

                    )} {/* end Finansal Ekstre / Ödeme Planı sekmesi */}

                    {/* Aktiviteler Sekmesi */}
                    {activeMainTab === 'aktiviteler' && (
                    <Row className="mb-3">
                        <Col md={12}>
                            {/* Activity Feed - Notlar */}
                            <div style={{
                                background: 'white',
                                border: '1px solid #f1f5f9',
                                borderRadius: '1rem',
                                padding: '1.5rem',
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
                            }}>
                                <h3 style={{
                                    fontSize: '1.125rem',
                                    fontWeight: '600',
                                    color: '#1e293b',
                                    marginBottom: '1.5rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem'
                                }}>
                                    <StickyNote size={20} style={{ color: '#64748b' }} />
                                    Aktivite Akışı
                                </h3>
                                <div style={{ 
                                    flex: 1, 
                                    overflowY: 'auto', 
                                    position: 'relative',
                                    paddingLeft: '2rem',
                                    maxHeight: '500px'
                                }}>
                                    {notlar.length === 0 ? (
                                        <div style={{ 
                                            textAlign: 'center', 
                                            color: '#94a3b8', 
                                            padding: '2rem 0',
                                            fontSize: '0.875rem'
                                        }}>
                                            Not bulunmamaktadır
                                        </div>
                                    ) : (
                                        <div style={{ position: 'relative' }}>
                                            {/* Timeline Line */}
                                            <div style={{
                                                position: 'absolute',
                                                left: '0.25rem',
                                                top: '0.5rem',
                                                bottom: '0.5rem',
                                                width: '1px',
                                                background: '#e2e8f0'
                                            }} />
                                            
                                            {notlar.map((not, index) => (
                                                <div key={not.notID} style={{
                                                    position: 'relative',
                                                    paddingBottom: index === notlar.length - 1 ? 0 : '1rem'
                                                }}>
                                                    {/* Timeline Dot - Hollow Ring */}
                                                    <div style={{
                                                        position: 'absolute',
                                                        left: '-1.5rem',
                                                        top: '0.375rem',
                                                        width: '0.5rem',
                                                        height: '0.5rem',
                                                        borderRadius: '50%',
                                                        background: 'white',
                                                        border: '2px solid #4f46e5',
                                                        boxShadow: '0 0 0 2px white'
                                                    }} />
                                                    
                                                    {/* Note Content */}
                                                    <div style={{
                                                        marginLeft: '0.5rem'
                                                    }}>
                                                        <div style={{
                                                            fontSize: '0.625rem',
                                                            letterSpacing: '0.02em',
                                                            color: '#94a3b8',
                                                            fontWeight: '500',
                                                            marginBottom: '0.25rem'
                                                        }}>
                                                            {not.ekleyen?.adSoyad} • {new Date(not.eklenmeTarihi).toLocaleString('tr-TR')}
                                                        </div>
                                                        <div style={{
                                                            background: '#f8fafc',
                                                            padding: '0.5rem 0.75rem',
                                                            borderRadius: '0.375rem',
                                                            borderLeft: '3px solid #e2e8f0',
                                                            fontSize: '0.875rem',
                                                            color: '#475569',
                                                            lineHeight: '1.5'
                                                        }}>
                                                            {not.aciklama}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </Col>
                    </Row>
                    )} {/* end aktiviteler tab */}

                    {/* Dosyalar Sekmesi */}
                    {activeMainTab === 'dosyalar' && (
                    <Row className="mb-3">
                        <Col md={12}>
                            {/* File Cards - Dosyalar */}
                            <div style={{
                                background: 'white',
                                border: '1px solid #f1f5f9',
                                borderRadius: '1rem',
                                padding: '1.5rem',
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
                            }}>
                                <h3 style={{
                                    fontSize: '1.125rem',
                                    fontWeight: '600',
                                    color: '#1e293b',
                                    marginBottom: '1.5rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem'
                                }}>
                                    <FileText size={20} style={{ color: '#64748b' }} />
                                    Ekli Dosyalar
                                </h3>
                                <div style={{ 
                                    flex: 1, 
                                    overflowY: 'auto',
                                    maxHeight: '500px'
                                }}>
                                    {dosyalar.length === 0 ? (
                                        <div style={{ 
                                            textAlign: 'center', 
                                            color: '#94a3b8', 
                                            padding: '2rem 0',
                                            fontSize: '0.875rem'
                                        }}>
                                            Dosya bulunmamaktadır
                                        </div>
                                    ) : (
                                        <div style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: '0.5rem'
                                        }}>
                                            {dosyalar.map((dosya) => {
                                                const ext = dosya.dosyaAdi.toLowerCase().split('.').pop() || '';
                                                const isPdf = ext === 'pdf';
                                                const isImage = ['jpg', 'jpeg', 'png', 'gif'].includes(ext);
                                                
                                                return (
                                                    <div 
                                                        key={dosya.dosyaID} 
                                                        className="file-list-item"
                                                        style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'space-between',
                                                            padding: '0.75rem',
                                                            borderRadius: '0.5rem',
                                                            transition: 'background 0.2s'
                                                        }}
                                                        onMouseEnter={(e) => {
                                                            e.currentTarget.style.background = '#f8fafc';
                                                            const actions = e.currentTarget.querySelector('.file-actions') as HTMLElement;
                                                            if (actions) actions.style.opacity = '1';
                                                        }}
                                                        onMouseLeave={(e) => {
                                                            e.currentTarget.style.background = 'transparent';
                                                            const actions = e.currentTarget.querySelector('.file-actions') as HTMLElement;
                                                            if (actions) actions.style.opacity = '0';
                                                        }}
                                                    >
                                                        {/* Sol taraf: İkon + Dosya Bilgisi */}
                                                        <div style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: '0.75rem',
                                                            flex: 1,
                                                            minWidth: 0
                                                        }}>
                                                            {/* Küçük İkon */}
                                                            <div style={{ flexShrink: 0 }}>
                                                                {isPdf ? (
                                                                    <FileText size={20} style={{ color: '#dc2626' }} />
                                                                ) : isImage ? (
                                                                    <FileImage size={20} style={{ color: '#10b981' }} />
                                                                ) : (
                                                                    <File size={20} style={{ color: '#64748b' }} />
                                                                )}
                                                            </div>
                                                            
                                                            {/* Dosya Adı ve Tarih */}
                                                            <div style={{ 
                                                                flex: 1,
                                                                minWidth: 0,
                                                                overflow: 'hidden'
                                                            }}>
                                                                <div style={{
                                                                    fontSize: '0.875rem',
                                                                    color: '#0f172a',
                                                                    fontWeight: '500',
                                                                    whiteSpace: 'nowrap',
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis'
                                                                }}>
                                                                    {dosya.aciklama}
                                                                </div>
                                                                <div style={{
                                                                    fontSize: '0.625rem',
                                                                    color: '#94a3b8',
                                                                    marginTop: '0.125rem'
                                                                }}>
                                                                    {new Date(dosya.eklenmeTarihi).toLocaleDateString('tr-TR')} · {ext.toUpperCase()}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        
                                                        {/* Sağ taraf: Hover'da görünen butonlar */}
                                                        <div 
                                                            className="file-actions"
                                                            style={{
                                                                display: 'flex',
                                                                gap: '0.5rem',
                                                                opacity: 0,
                                                                transition: 'opacity 0.2s',
                                                                flexShrink: 0
                                                            }}
                                                        >
                                                            {(isPdf || isImage) && (
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handlePreview(dosya.dosyaID, dosya.dosyaAdi);
                                                                    }}
                                                                    style={{
                                                                        padding: '0.375rem',
                                                                        background: 'transparent',
                                                                        border: 'none',
                                                                        borderRadius: '0.375rem',
                                                                        color: '#10b981',
                                                                        cursor: 'pointer',
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                        transition: 'background 0.2s'
                                                                    }}
                                                                    onMouseOver={(e) => e.currentTarget.style.background = '#f0fdf4'}
                                                                    onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                                                                    title="Önizle"
                                                                >
                                                                    <Eye size={16} />
                                                                </button>
                                                            )}
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleDosyaDownload(dosya.dosyaID);
                                                                }}
                                                                style={{
                                                                    padding: '0.375rem',
                                                                    background: 'transparent',
                                                                    border: 'none',
                                                                    borderRadius: '0.375rem',
                                                                    color: '#4f46e5',
                                                                    cursor: 'pointer',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    transition: 'background 0.2s'
                                                                }}
                                                                onMouseOver={(e) => e.currentTarget.style.background = '#eef2ff'}
                                                                onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                                                                title={t('btn.indir')}
                                                            >
                                                                <Download size={16} />
                                                            </button>
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleDosyaSil(dosya.dosyaID);
                                                                }}
                                                                style={{
                                                                    padding: '0.375rem',
                                                                    background: 'transparent',
                                                                    border: 'none',
                                                                    borderRadius: '0.375rem',
                                                                    color: '#ef4444',
                                                                    cursor: 'pointer',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    transition: 'background 0.2s'
                                                                }}
                                                                onMouseOver={(e) => e.currentTarget.style.background = '#fef2f2'}
                                                                onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                                                                title={t('btn.sil')}
                                                            >
                                                                <Trash2 size={16} />
                                                            </button>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </Col>
                    </Row>

                    )} {/* end dosyalar sekmesi */}

                    {/* Satış Sözleşmeleri */}
                    {activeMainTab === 'sozlesmeler' && sozlesmeler.length > 0 && (
                        <Row className="mb-3">
                            <Col md={12}>
                                <div style={{ background: 'white', border: '1px solid #f1f5f9', borderRadius: '1rem', padding: '1.5rem', boxShadow: '0 1px 3px 0 rgba(0,0,0,0.1)' }}>
                                    <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#1e293b', marginBottom: '1.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                        <i className="fas fa-file-contract" style={{ color: '#cd6906' }}></i>
                                        {t('cari.satisSozlesmeleri', 'Satış Sözleşmeleri')}
                                        <span style={{ marginLeft: 8, fontSize: 12, fontWeight: 600, background: '#fff7ed', color: '#cd6906', border: '1px solid #fed7aa', borderRadius: 20, padding: '2px 10px' }}>{sozlesmeler.length}</span>
                                    </h3>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                        {sozlesmeler.map((s) => {
                                            const durumConfig: Record<string, { border: string; badgeBg: string; badgeColor: string }> = {
                                                'Sözleşme':   { border: '#3b82f6', badgeBg: '#dbeafe', badgeColor: '#1d4ed8' },
                                                'Rezerve':    { border: '#f59e0b', badgeBg: '#fef9c3', badgeColor: '#ca8a04' },
                                                'ArsaSahibi': { border: '#8b5cf6', badgeBg: '#ede9fe', badgeColor: '#6d28d9' },
                                                'Taşeron':    { border: '#0ea5e9', badgeBg: '#e0f2fe', badgeColor: '#0369a1' },
                                            };
                                            const cfg = durumConfig[s.sozlesmeDurumu] || { border: '#94a3b8', badgeBg: '#f1f5f9', badgeColor: '#475569' };
                                            return (
                                                <div
                                                    key={s.sozlesmeID}
                                                    onClick={() => navigate(`/sozlesme-detay/${s.sozlesmeTakipNo}`)}
                                                    style={{ display: 'flex', alignItems: 'center', padding: '0.75rem 1rem', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '0.5rem', cursor: 'pointer', transition: 'border-color 0.15s', gap: 12 }}
                                                    onMouseOver={e => (e.currentTarget.style.borderColor = cfg.border)}
                                                    onMouseOut={e => (e.currentTarget.style.borderColor = '#e2e8f0')}
                                                >
                                                    <div style={{ width: 4, alignSelf: 'stretch', borderRadius: 4, background: cfg.border, flexShrink: 0 }} />
                                                    <div style={{ flex: 1, minWidth: 0 }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                                            <span style={{ fontWeight: 600, fontSize: '0.875rem', color: '#1e293b' }}>
                                                                {s.daire?.blok?.proje?.projeAdi || '—'}
                                                            </span>
                                                            {s.daire?.daireNo && (
                                                                <span style={{ fontSize: '0.75rem', color: '#64748b' }}>/ {t('label.daire', 'Daire')} {s.daire.daireNo}</span>
                                                            )}
                                                            <span style={{ fontSize: '0.7rem', padding: '2px 8px', borderRadius: 999, background: cfg.badgeBg, color: cfg.badgeColor, fontWeight: 600 }}>
                                                                {s.sozlesmeDurumu}
                                                            </span>
                                                        </div>
                                                        <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: 3 }}>
                                                            {new Date(s.sozlesmeTarihi).toLocaleDateString('tr-TR')}
                                                            &nbsp;·&nbsp;
                                                            {new Intl.NumberFormat('tr-TR').format(Number(s.sozlesmeFiyati))} ₺
                                                            &nbsp;·&nbsp;
                                                            <span style={{ color: '#94a3b8' }}>{s.sozlesmeTakipNo}</span>
                                                        </div>
                                                    </div>
                                                    <i className="fas fa-chevron-right" style={{ color: '#cbd5e1', fontSize: 12 }} />
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    )}

                    {/* Sözleşme yok mesajı */}
                    {activeMainTab === 'sozlesmeler' && !loadingRelated && sozlesmeler.length === 0 && (!relatedRecords || (relatedRecords.kiraSozlesmeleri.length === 0 && relatedRecords.emlakSunumlari.length === 0)) && (
                        <Row className="mb-3">
                            <Col md={12}>
                                <div style={{ background: 'white', border: '1px solid #f1f5f9', borderRadius: '1rem', padding: '2rem', textAlign: 'center', boxShadow: '0 1px 3px 0 rgba(0,0,0,0.06)' }}>
                                    <i className="fas fa-file-contract" style={{ fontSize: '2rem', color: '#e2e8f0', marginBottom: '0.75rem' }}></i>
                                    <p style={{ color: '#94a3b8', fontSize: '0.875rem', margin: 0 }}>{t('cari.sozlesmeYok', 'Bu cari ile ilişkili sözleşme bulunamadı.')}</p>
                                </div>
                            </Col>
                        </Row>
                    )}

                    {/* İlişkili Kayıtlar (Kira Sözleşmeleri + Emlak Sunumları) */}
                    {activeMainTab === 'sozlesmeler' && (loadingRelated || (relatedRecords && (relatedRecords.kiraSozlesmeleri.length > 0 || relatedRecords.emlakSunumlari.length > 0))) && (
                        <Row className="mb-3">
                            <Col md={12}>
                                <div style={{
                                    background: 'white',
                                    border: '1px solid #f1f5f9',
                                    borderRadius: '1rem',
                                    padding: '1.5rem',
                                    boxShadow: '0 1px 3px 0 rgba(0,0,0,0.1)'
                                }}>
                                    <h3 style={{
                                        fontSize: '1.125rem',
                                        fontWeight: '600',
                                        color: '#1e293b',
                                        marginBottom: '1.25rem',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.5rem'
                                    }}>
                                        <i className="fas fa-link" style={{ color: '#1a2340' }}></i>
                                        {t('cari.iliskiliKayitlar', 'İlişkili Kayıtlar')}
                                    </h3>
                                    {loadingRelated ? (
                                        <p style={{ color: '#94a3b8', fontSize: '0.875rem' }}>{t('msg.yukleniyor')}</p>
                                    ) : (
                                        <Row>
                                            {relatedRecords && relatedRecords.kiraSozlesmeleri.length > 0 && (
                                                <Col md={6}>
                                                    <div style={{ marginBottom: '0.75rem', fontWeight: '600', fontSize: '0.8rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                                        <i className="fas fa-file-contract" style={{ marginRight: 6, color: '#1a2340' }}></i>
                                                        {t('cari.kiraSozlesmeleri', 'Kira Sözleşmeleri')}
                                                    </div>
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                                        {relatedRecords.kiraSozlesmeleri.map(s => (
                                                            <div
                                                                key={s.kiraSozlesmeID}
                                                                onClick={() => navigate(`/kiralik-gayrimenkul/sozlesme-detay/${s.takipNo}`)}
                                                                style={{
                                                                    padding: '0.75rem 1rem',
                                                                    background: '#f8fafc',
                                                                    border: '1px solid #e2e8f0',
                                                                    borderRadius: '0.5rem',
                                                                    cursor: 'pointer',
                                                                    transition: 'border-color 0.15s'
                                                                }}
                                                                onMouseOver={e => (e.currentTarget.style.borderColor = '#1a2340')}
                                                                onMouseOut={e => (e.currentTarget.style.borderColor = '#e2e8f0')}
                                                            >
                                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                    <span style={{ fontWeight: '500', fontSize: '0.875rem', color: '#1e293b' }}>
                                                                        {s.gayrimenkulBaslik || s.takipNo}
                                                                    </span>
                                                                    <span style={{
                                                                        fontSize: '0.7rem',
                                                                        padding: '0.15rem 0.5rem',
                                                                        borderRadius: '999px',
                                                                        background: s.aktifMi ? '#dcfce7' : '#fee2e2',
                                                                        color: s.aktifMi ? '#166534' : '#991b1b',
                                                                        fontWeight: '600'
                                                                    }}>
                                                                        {s.aktifMi ? t('durum.aktif', 'Aktif') : t('durum.pasif', 'Pasif')}
                                                                    </span>
                                                                </div>
                                                                <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '0.25rem' }}>
                                                                    {new Date(s.baslangicTarihi).toLocaleDateString('tr-TR')} – {new Date(s.bitisTarihi).toLocaleDateString('tr-TR')}
                                                                    &nbsp;·&nbsp;
                                                                    {new Intl.NumberFormat('tr-TR').format(Number(s.kiraBedeli))} ₺/ay
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </Col>
                                            )}
                                            {relatedRecords && relatedRecords.emlakSunumlari.length > 0 && (
                                                <Col md={6}>
                                                    <div style={{ marginBottom: '0.75rem', fontWeight: '600', fontSize: '0.8rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                                        <i className="fas fa-home" style={{ marginRight: 6, color: '#e67e22' }}></i>
                                                        {t('cari.emlakSunumlari', 'Emlak Sunumları')}
                                                    </div>
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                                        {relatedRecords.emlakSunumlari.map(s => (
                                                            <div
                                                                key={s.sunumID}
                                                                onClick={() => navigate(`/emlak-sunumlari/${s.sunumID}`)}
                                                                style={{
                                                                    padding: '0.75rem 1rem',
                                                                    background: '#f8fafc',
                                                                    border: '1px solid #e2e8f0',
                                                                    borderRadius: '0.5rem',
                                                                    cursor: 'pointer',
                                                                    transition: 'border-color 0.15s'
                                                                }}
                                                                onMouseOver={e => (e.currentTarget.style.borderColor = '#e67e22')}
                                                                onMouseOut={e => (e.currentTarget.style.borderColor = '#e2e8f0')}
                                                            >
                                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                    <span style={{ fontWeight: '500', fontSize: '0.875rem', color: '#1e293b' }}>
                                                                        {s.baslik || s.takipNo}
                                                                    </span>
                                                                    <span style={{
                                                                        fontSize: '0.7rem',
                                                                        padding: '0.15rem 0.5rem',
                                                                        borderRadius: '999px',
                                                                        background: s.aktifMi ? '#dcfce7' : '#fee2e2',
                                                                        color: s.aktifMi ? '#166534' : '#991b1b',
                                                                        fontWeight: '600'
                                                                    }}>
                                                                        {s.aktifMi ? t('durum.aktif', 'Aktif') : t('durum.pasif', 'Pasif')}
                                                                    </span>
                                                                </div>
                                                                <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '0.25rem' }}>
                                                                    {s.durum && <span style={{ marginRight: 8 }}>{s.durum}</span>}
                                                                    {new Date(s.olusturmaTarihi).toLocaleDateString('tr-TR')}
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </Col>
                                            )}
                                        </Row>
                                    )}
                                </div>
                            </Col>
                        </Row>
                    )}
                </div>
            </section>

            {/* Düzenle Modal */}
            <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>{t('cari.duzenle', 'Cari Düzenle')}</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSubmit}>
                    <Modal.Body>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('label.adSoyad', 'Adı Soyadı')}</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={formData.adiSoyadi}
                                        onChange={(e) => setFormData({ ...formData, adiSoyadi: e.target.value })}
                                        required
                                        maxLength={100}
                                    />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('label.firmaAdi', 'Firma Adı')}</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={formData.firmaAdi || ''}
                                        onChange={(e) => setFormData({ ...formData, firmaAdi: e.target.value || null })}
                                        maxLength={100}
                                    />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                        {t('label.telefon', 'Telefon')} 1
                                        {iysAktarimAktif && (iysIzinler.some(i => i.iletisimTipi === 'TELEFON1' && i.izinDurumu === 'ONAY' && i.aktifMi)
                                            ? <i className="fas fa-check-circle" title={t('cari.iysIzniAlindi')} style={{ color: '#22c55e' }}></i>
                                            : formData.telefon1 && <button type="button" onClick={() => setIysModal({ iletisimTipi: 'TELEFON1', iletisimVerisi: formData.telefon1 })} style={{ background: 'none', border: '1px solid #e67d21', color: '#e67d21', borderRadius: 4, padding: '1px 7px', cursor: 'pointer', fontSize: 11, lineHeight: '18px' }}>IYS İzni Al</button>)}
                                    </Form.Label>
                                    <IysKilitliAlan
                                        aktifIzin={iysIzinler.find(i => i.iletisimTipi === 'TELEFON1' && i.izinDurumu === 'ONAY' && i.aktifMi) ?? null}
                                        cariID={cari?.cariID ?? 0}
                                        takipNo={cari?.takipNo ?? ''}
                                        ekleyenID={user?.kullaniciID ?? 0}
                                        iletisimTipiLabel="Telefon 1"
                                        onUnlocked={async () => { try { const r = await axiosInstance.get(`/api/CariIysIzinleri/cari/${cari?.cariID}`); setIysIzinler(r.data); } catch {} }}
                                        disabled={!iysAktarimAktif}
                                    >{(readOnly) => (
                                        <Form.Control type="text" value={formatPhoneNumber(formData.telefon1)} onChange={(e) => setFormData({ ...formData, telefon1: unformatPhoneNumber(e.target.value) })} required maxLength={14} placeholder="(5XX)XXX-XX-XX" readOnly={readOnly} style={{ background: readOnly ? '#f9fafb' : undefined }} />
                                    )}</IysKilitliAlan>
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                        {t('label.telefon', 'Telefon')} 2
                                        {iysAktarimAktif && (iysIzinler.some(i => i.iletisimTipi === 'TELEFON2' && i.izinDurumu === 'ONAY' && i.aktifMi)
                                            ? <i className="fas fa-check-circle" title={t('cari.iysIzniAlindi')} style={{ color: '#22c55e' }}></i>
                                            : formData.telefon2 && <button type="button" onClick={() => setIysModal({ iletisimTipi: 'TELEFON2', iletisimVerisi: formData.telefon2! })} style={{ background: 'none', border: '1px solid #e67d21', color: '#e67d21', borderRadius: 4, padding: '1px 7px', cursor: 'pointer', fontSize: 11, lineHeight: '18px' }}>IYS İzni Al</button>)}
                                    </Form.Label>
                                    <IysKilitliAlan
                                        aktifIzin={iysIzinler.find(i => i.iletisimTipi === 'TELEFON2' && i.izinDurumu === 'ONAY' && i.aktifMi) ?? null}
                                        cariID={cari?.cariID ?? 0}
                                        takipNo={cari?.takipNo ?? ''}
                                        ekleyenID={user?.kullaniciID ?? 0}
                                        iletisimTipiLabel="Telefon 2"
                                        onUnlocked={async () => { try { const r = await axiosInstance.get(`/api/CariIysIzinleri/cari/${cari?.cariID}`); setIysIzinler(r.data); } catch {} }}
                                        disabled={!iysAktarimAktif}
                                    >{(readOnly) => (
                                        <Form.Control type="text" value={formData.telefon2 ? formatPhoneNumber(formData.telefon2) : ''} onChange={(e) => setFormData({ ...formData, telefon2: e.target.value ? unformatPhoneNumber(e.target.value) : null })} maxLength={14} placeholder="(5XX)XXX-XX-XX" readOnly={readOnly} style={{ background: readOnly ? '#f9fafb' : undefined }} />
                                    )}</IysKilitliAlan>
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                        {t('label.eposta', 'E-Posta')}
                                        {iysAktarimAktif && (iysIzinler.some(i => i.iletisimTipi === 'EPOSTA' && i.izinDurumu === 'ONAY' && i.aktifMi)
                                            ? <i className="fas fa-check-circle" title={t('cari.iysIzniAlindi')} style={{ color: '#22c55e' }}></i>
                                            : formData.ePosta && <button type="button" onClick={() => setIysModal({ iletisimTipi: 'EPOSTA', iletisimVerisi: formData.ePosta! })} style={{ background: 'none', border: '1px solid #e67d21', color: '#e67d21', borderRadius: 4, padding: '1px 7px', cursor: 'pointer', fontSize: 11, lineHeight: '18px' }}>IYS İzni Al</button>)}
                                    </Form.Label>
                                    <IysKilitliAlan
                                        aktifIzin={iysIzinler.find(i => i.iletisimTipi === 'EPOSTA' && i.izinDurumu === 'ONAY' && i.aktifMi) ?? null}
                                        cariID={cari?.cariID ?? 0}
                                        takipNo={cari?.takipNo ?? ''}
                                        ekleyenID={user?.kullaniciID ?? 0}
                                        iletisimTipiLabel="E-Posta"
                                        onUnlocked={async () => { try { const r = await axiosInstance.get(`/api/CariIysIzinleri/cari/${cari?.cariID}`); setIysIzinler(r.data); } catch {} }}
                                        disabled={!iysAktarimAktif}
                                    >{(readOnly) => (
                                        <Form.Control type="email" value={formData.ePosta || ''} onChange={(e) => setFormData({ ...formData, ePosta: e.target.value || null })} maxLength={100} readOnly={readOnly} style={{ background: readOnly ? '#f9fafb' : undefined }} />
                                    )}</IysKilitliAlan>
                                </Form.Group>
                                {/* IYS Kayıtları */}
                                <IysKayitListesi izinler={iysIzinler} />
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>TC</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={formData.tc || ''}
                                        onChange={(e) => setFormData({ ...formData, tc: e.target.value || null })}
                                        maxLength={11}
                                    />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('label.vergiDairesi', 'Vergi Dairesi')}</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={formData.cariVD || ''}
                                        onChange={(e) => setFormData({ ...formData, cariVD: e.target.value || null })}
                                        maxLength={100}
                                    />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('label.vergiNo', 'Vergi No')}</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={formData.cariVN || ''}
                                        onChange={(e) => setFormData({ ...formData, cariVN: e.target.value || null })}
                                        maxLength={50}
                                    />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('label.altKategori', 'Alt Kategori')}</Form.Label>
                                    <Form.Select
                                        value={formData.turu2 || ''}
                                        onChange={(e) => setFormData({ ...formData, turu2: e.target.value || null })}
                                    >
                                        <option value="">{t('label.seciniz')}</option>
                                        {cariAltKategoriler.map(k => (
                                            <option key={k.cariAltKategoriID} value={k.altKategoriAdi}>{k.altKategoriAdi}</option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Check
                                        type="switch"
                                        id="aktif-switch"
                                        label={t('label.aktif', 'Aktif')}
                                        checked={formData.aktifMi}
                                        onChange={(e) => setFormData({ ...formData, aktifMi: e.target.checked })}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={12}>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('label.adres', 'Adres')}</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={3}
                                        value={formData.adres || ''}
                                        onChange={(e) => setFormData({ ...formData, adres: e.target.value || null })}
                                        maxLength={500}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowModal(false)}>
                            {t('btn.iptal', 'İptal')}
                        </Button>
                        <Button variant="primary" type="submit">
                            {t('btn.kaydet', 'Kaydet')}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Notlar Modal */}
            <Modal show={showNotModal} onHide={() => setShowNotModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>{t('label.notlar')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {notlar.length === 0 ? (
                        <p className="text-muted text-center">{t('sozlesmeDetay.notYok', 'Henüz not eklenmemiş.')}</p>
                    ) : (
                        <div style={{ maxHeight: '350px', overflowY: 'auto' }} className="mb-3">
                            {notlar.map((not) => (
                                <div key={not.notID} className="d-flex justify-content-between align-items-start mb-2 p-2 border rounded">
                                    <div>
                                        <small className="text-muted">
                                            {formatDate(not.eklenmeTarihi)} - {not.ekleyen?.adSoyad}
                                        </small>
                                        <p className="mb-0">{not.aciklama}</p>
                                    </div>
                                    <Button
                                        variant="link"
                                        size="sm"
                                        className="text-danger p-0 ms-2"
                                        onClick={() => handleNotSil(not.notID)}
                                    >
                                        <i className="fas fa-trash"></i>
                                    </Button>
                                </div>
                            ))}
                        </div>
                    )}
                    <hr />
                    <h6 className="mb-3">{t('sozlesmeDetay.notEkle', 'Not Ekle')}</h6>
                    <Form onSubmit={handleNotSubmit}>
                        <Form.Group className="mb-3">
                            <Form.Control
                                as="textarea"
                                rows={3}
                                placeholder={t('label.aciklama')}
                                value={notFormData.aciklama}
                                onChange={(e) => setNotFormData({ aciklama: e.target.value })}
                                required
                                maxLength={1000}
                            />
                        </Form.Group>
                        <div className="d-flex justify-content-end">
                            <Button variant="primary" type="submit">
                                <i className="fas fa-plus"></i> Ekle
                            </Button>
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>

            {/* Dosyalar Modal */}
            <Modal show={showDosyaModal} onHide={() => setShowDosyaModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>{t('label.dosyalar')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {dosyalar.length === 0 ? (
                        <p className="text-muted text-center">Henüz dosya eklenmemiş.</p>
                    ) : (
                        <div style={{ maxHeight: '300px', overflowY: 'auto' }} className="mb-3">
                            {dosyalar.map((dosya) => (
                                <div key={dosya.dosyaID} className="d-flex justify-content-between align-items-center mb-1 p-2 border rounded">
                                    <div>
                                        <i className="fas fa-file me-2"></i>
                                        <span>{dosya.dosyaAdi}</span>
                                        {dosya.aciklama && <small className="text-muted ms-2">({dosya.aciklama})</small>}
                                    </div>
                                    <Button
                                        variant="link"
                                        size="sm"
                                        className="text-danger p-0 ms-2"
                                        onClick={() => handleDosyaSil(dosya.dosyaID)}
                                    >
                                        <i className="fas fa-trash"></i>
                                    </Button>
                                </div>
                            ))}
                        </div>
                    )}
                    <hr />
                    <h6 className="mb-3">Dosya Ekle</h6>
                    <Form onSubmit={handleDosyaSubmit}>
                        <Form.Group className="mb-3">
                            <Form.Label>Dosya</Form.Label>
                            <Form.Control
                                type="file"
                                ref={fileInputRef}
                                onChange={(e) => {
                                    const target = e.target as HTMLInputElement;
                                    setDosyaFormData({ ...dosyaFormData, dosya: target.files?.[0] || null });
                                }}
                                required
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>{t('label.aciklama')}</Form.Label>
                            <Form.Control
                                type="text"
                                value={dosyaFormData.aciklama}
                                onChange={(e) => setDosyaFormData({ ...dosyaFormData, aciklama: e.target.value })}
                                maxLength={200}
                            />
                        </Form.Group>
                        <div className="d-flex justify-content-end">
                            <Button variant="success" type="submit">
                                <i className="fas fa-upload"></i> Yükle
                            </Button>
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>

            {/* Dosya Önizleme Modal */}
            <Modal 
                show={showPreviewModal} 
                onHide={handleClosePreview} 
                size="xl"
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        {previewFile?.name || 'Dosya Önizleme'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ 
                    padding: 0, 
                    minHeight: '70vh', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    background: '#f8fafc'
                }}>
                    {previewFile?.type === 'pdf' ? (
                        <iframe
                            src={previewFile.url}
                            style={{
                                width: '100%',
                                height: '70vh',
                                border: 'none'
                            }}
                            title="PDF Önizleme"
                        />
                    ) : previewFile?.type === 'image' ? (
                        <div style={{
                            width: '100%',
                            height: '70vh',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '1rem'
                        }}>
                            <img
                                src={previewFile.url}
                                alt="Önizleme"
                                style={{
                                    maxWidth: '100%',
                                    maxHeight: '100%',
                                    objectFit: 'contain'
                                }}
                            />
                        </div>
                    ) : null}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleClosePreview}>
                        Kapat
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Hareket Ekleme Modal */}
            <Modal show={showOdemeModal} onHide={() => setShowOdemeModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>
                        {(odemeFormData.odemeTuru === 'Urun' || odemeFormData.odemeTuru === 'Hizmet')
                            ? 'Hizmet/Ürün Ekle'
                            : odemeFormData.odemeYonu === 'Odeme' ? 'Ödeme Ekle' : 'Tahsilat Ekle'}
                    </Modal.Title>
                </Modal.Header>
                <Form onSubmit={(e) => { e.preventDefault(); handleSaveOdeme(); }}>
                    <Modal.Body>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('label.tutar', 'Tutar')} <span className="text-danger">*</span></Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={odemeFormData.miktar}
                                        onChange={(e) => handleOdemeFormChange('miktar', e.target.value)}
                                        placeholder="0"
                                        required
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>
                                        {(odemeFormData.odemeTuru === 'Urun' || odemeFormData.odemeTuru === 'Hizmet') ? t('label.tur', 'Türü') : t('label.odemeTuru', 'Ödeme Türü')}
                                        {' '}<span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Select
                                        value={odemeFormData.odemeTuru}
                                        onChange={(e) => handleOdemeFormChange('odemeTuru', e.target.value)}
                                        required
                                    >
                                        <option value="Nakit">Nakit</option>
                                        <option value="Senet">Senet</option>
                                        <option value="Cek">{'\u00c7ek'}</option>
                                        <option value="Takas">Takas</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('label.vadeTarihi', 'Vade Tarihi')} <span className="text-danger">*</span></Form.Label>
                                    <TrDateInput className="form-control"
                                        value={odemeFormData.vadeTarihi}
                                        onChange={(e) => handleOdemeFormChange('vadeTarihi', e.target.value)}
                                        required
                                    />
                                </Form.Group>
                            </Col>
                            {odemeFormData.odemeYonu !== 'Odeme' && (
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('sozlesmeDetay.odemeDurumu', 'Ödeme Durumu')} <span className="text-danger">*</span></Form.Label>
                                    <Form.Select
                                        value={odemeFormData.odemeDurumu}
                                        onChange={(e) => handleOdemeFormChange('odemeDurumu', e.target.value)}
                                        required
                                    >
                                        <option value="Bekliyor">{t('durum.bekliyor', 'Bekliyor')}</option>
                                        <option value="Odendi">{t('durum.odendi', 'Ödendi')}</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            )}
                        </Row>

                        {(odemeFormData.odemeYonu === 'Odeme' || odemeFormData.odemeDurumu === 'Odendi') && (
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('label.kasa', 'Kasa')}{odemeFormData.odemeYonu !== 'Odeme' && <span className="text-danger"> *</span>}</Form.Label>
                                    <Form.Select
                                        value={odemeFormData.kasaID}
                                        onChange={(e) => handleOdemeFormChange('kasaID', e.target.value)}
                                        required={odemeFormData.odemeYonu !== 'Odeme'}
                                    >
                                        <option value="">{t('sozlesmeDetay.kasaSecin', 'Kasa seçin')}</option>
                                        {kasalar.map(kasa => (
                                            <option key={kasa.kasaID} value={kasa.kasaID}>
                                                {kasa.kasaAdi} ({kasa.kasaTuru === 'Nakit' ? '💵' : '🏦'})
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                        </Row>
                        )}

                        <Row>
                            <Col md={12}>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('label.proje', 'İlgili Proje')}</Form.Label>
                                    <Form.Select
                                        value={odemeFormData.projeID}
                                        onChange={(e) => handleOdemeFormChange('projeID', e.target.value)}
                                    >
                                        <option value="">{t('cari.projeOpsiyonel', 'Proje Seçiniz (Opsiyonel)')}</option>
                                        {projeler.map((proje) => (
                                            <option key={proje.projeID} value={proje.projeID}>{proje.projeAdi}</option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={12}>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('label.aciklama')}</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={3}
                                        value={odemeFormData.aciklama}
                                        onChange={(e) => handleOdemeFormChange('aciklama', e.target.value)}
                                        placeholder={t('label.aciklama')}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowOdemeModal(false)}>{t('btn.iptal', 'İptal')}</Button>
                        <Button variant="primary" type="submit" disabled={saving}>
                            {saving ? t('msg.kaydediliyor', 'Kaydediliyor...') : t('btn.kaydet', 'Kaydet')}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Hareket Düzenleme Modal */}
            <Modal show={showEditOdemeModal} onHide={() => setShowEditOdemeModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>
                        {editingOdeme?.odemeDurumu === 'Bekliyor'
                            ? <><i className="fas fa-clock me-2 text-warning"></i>{t('cari.bekleyenHareketDuzenle', 'Bekleyen Hareketi Düzenle')}</>
                            : t('cari.hareketDuzenle', 'Hareketi Düzenle')}
                    </Modal.Title>
                </Modal.Header>
                <Form onSubmit={(e) => { e.preventDefault(); handleSaveEditOdeme(); }}>
                    <Modal.Body>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('label.tutar', 'Tutar')} <span className="text-danger">*</span></Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={editOdemeFormData.miktar}
                                        onChange={(e) => handleEditOdemeFormChange('miktar', e.target.value)}
                                        placeholder="0"
                                        required
                                    />
                                </Form.Group>
                            </Col>
                            {editingOdeme?.odemeYonu !== 'Odeme' && (
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('sozlesmeDetay.odemeDurumu', 'Ödeme Durumu')} <span className="text-danger">*</span></Form.Label>
                                    <Form.Select
                                        value={editOdemeFormData.odemeDurumu}
                                        onChange={(e) => handleEditOdemeFormChange('odemeDurumu', e.target.value)}
                                        required
                                        disabled={editingOdeme?.islemTipi === 'Gelir' || editingOdeme?.islemTipi === 'Gider'}
                                        style={editingOdeme?.islemTipi === 'Gelir' || editingOdeme?.islemTipi === 'Gider' ? { backgroundColor: '#e9ecef', cursor: 'not-allowed' } : {}}
                                    >
                                        <option value="Bekliyor">{t('durum.bekliyor', 'Bekliyor')}</option>
                                        <option value="Odendi">{t('durum.odendi', 'Ödendi')}</option>
                                    </Form.Select>
                                    {(editingOdeme?.islemTipi === 'Gelir' || editingOdeme?.islemTipi === 'Gider') && (
                                        <Form.Text className="text-muted">Gelir/Gider türünde durum değiştirilemeez</Form.Text>
                                    )}
                                </Form.Group>
                            </Col>
                            )}
                        </Row>
                        {(editingOdeme?.odemeYonu === 'Odeme' || editOdemeFormData.odemeDurumu === 'Odendi' || editingOdeme?.odemeDurumu === 'Bekliyor') && editingOdeme?.islemTipi !== 'Gelir' && editingOdeme?.islemTipi !== 'Gider' && (
                            <Row>
                                <Col md={editingOdeme?.odemeDurumu === 'Bekliyor' ? 12 : 12}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>
                                            {t('label.kasa', 'Kasa')}
                                            {editingOdeme?.odemeDurumu === 'Bekliyor'
                                                ? <span className="text-muted ms-1" style={{ fontSize: '0.8rem' }}>(ödeme yapılacaksa seçin)</span>
                                                : editingOdeme?.odemeYonu !== 'Odeme' && <span className="text-danger"> *</span>}
                                        </Form.Label>
                                        <Form.Select
                                            value={editOdemeFormData.kasaID}
                                            onChange={(e) => handleEditOdemeFormChange('kasaID', e.target.value)}
                                            required={editingOdeme?.odemeYonu !== 'Odeme' && editingOdeme?.odemeDurumu !== 'Bekliyor'}
                                        >
                                            <option value="">{t('sozlesmeDetay.kasaSecin', 'Kasa seçin')}</option>
                                            {kasalar.map(kasa => (
                                                <option key={kasa.kasaID} value={kasa.kasaID}>
                                                    {kasa.kasaAdi} ({kasa.kasaTuru === 'Nakit' ? '💵' : '🏦'})
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                            </Row>
                        )}
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('label.vadeTarihi', 'Vade Tarihi')} <span className="text-danger">*</span></Form.Label>
                                    <TrDateInput className="form-control"
                                        value={editOdemeFormData.vadeTarihi}
                                        onChange={(e) => handleEditOdemeFormChange('vadeTarihi', e.target.value)}
                                        required
                                    />
                                </Form.Group>
                            </Col>
                            {(editOdemeFormData.odemeDurumu === 'Odendi' || editingOdeme?.odemeDurumu === 'Bekliyor') && (
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>
                                        {editingOdeme?.odemeDurumu === 'Bekliyor' ? t('cari.odemeTarihi', 'Ödeme Tarihi') : t('sozlesmeDetay.tahsilatTarihi', 'Tahsilat Tarihi')}
                                        {editingOdeme?.odemeDurumu === 'Bekliyor' && <span className="text-muted ms-1" style={{ fontSize: '0.8rem' }}>(boş bırakılırsa bugün)</span>}
                                    </Form.Label>
                                    <TrDateInput className="form-control"
                                        value={editOdemeFormData.tahsilatTarihi}
                                        onChange={(e) => handleEditOdemeFormChange('tahsilatTarihi', e.target.value)}
                                    />
                                </Form.Group>
                            </Col>
                            )}
                        </Row>
                        {editingOdeme && editingOdeme.ilgiliProje && (
                            <Row>
                                <Col md={12}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>{t('label.proje', 'İlgili Proje')}</Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={editingOdeme.ilgiliProje.projeAdi}
                                            disabled
                                            readOnly
                                            style={{ backgroundColor: '#e9ecef', cursor: 'not-allowed' }}
                                        />
                                        <Form.Text className="text-muted">İlgili proje bilgisi düzenlenemez</Form.Text>
                                    </Form.Group>
                                </Col>
                            </Row>
                        )}
                        <Row>
                            <Col md={12}>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('label.aciklama')}</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={2}
                                        value={editOdemeFormData.aciklama}
                                        onChange={(e) => handleEditOdemeFormChange('aciklama', e.target.value)}
                                        placeholder={t('label.aciklama')}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="danger" onClick={handleDeleteOdeme} disabled={saving}>
                            <i className="fas fa-trash me-1"></i> {t('btn.sil', 'Sil')}
                        </Button>
                        <Button variant="secondary" onClick={() => setShowEditOdemeModal(false)}>{t('btn.iptal', 'İptal')}</Button>
                        {editingOdeme?.odemeDurumu === 'Bekliyor' && (editingOdeme?.islemTipi === 'Tahsilat' || editingOdeme?.islemTipi === 'Odeme') && (
                            <Button
                                variant="success"
                                onClick={handleBekliyenOdemeYap}
                                disabled={saving}
                                style={{ fontWeight: 600 }}
                            >
                                <i className="fas fa-check-circle me-1"></i>
                                {saving ? t('cari.isleniyor', 'İşleniyor...') : t('cari.odemeYap', 'Ödeme Yap')}
                            </Button>
                        )}
                        <Button variant="primary" type="submit" disabled={saving}>
                            {saving ? t('cari.guncelleniyor', 'Güncelleniyor...') : t('btn.guncelle', 'Güncelle')}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Cari Ödeme Planı Düzenleme Modal */}
            <Modal show={showEditCariPlanModal} onHide={() => setShowEditCariPlanModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title><i className="fas fa-edit me-2" style={{ color: '#cd6906' }}></i>{t('cari.odemePlaniDuzenle', 'Ödeme Planı Düzenle')}</Modal.Title>
                </Modal.Header>
                <Form onSubmit={(e) => { e.preventDefault(); handleSaveEditCariPlan(); }}>
                    <Modal.Body>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('label.tutar', 'Tutar')} (₺) <span className="text-danger">*</span></Form.Label>
                                    <Form.Control type="text"
                                        value={editCariPlanFormData.miktar}
                                        onChange={(e) => handleEditCariPlanFormChange('miktar', e.target.value)}
                                        required
                                        placeholder="0,00"
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Vade Tarihi <span className="text-danger">*</span></Form.Label>
                                    <TrDateInput className="form-control"
                                        value={editCariPlanFormData.vadeTarihi}
                                        onChange={(e) => handleEditCariPlanFormChange('vadeTarihi', e.target.value)}
                                        required
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={12}>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('label.aciklama')}</Form.Label>
                                    <Form.Control type="text"
                                        value={editCariPlanFormData.aciklama}
                                        onChange={(e) => handleEditCariPlanFormChange('aciklama', e.target.value)}
                                        placeholder={t('label.aciklama')}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                    </Modal.Body>
                    <Modal.Footer>
                        {editingCariPlan && (
                            <Button variant="danger" onClick={handleDeleteCariPlan} disabled={saving}>
                                <i className="fas fa-trash me-2"></i>{t('btn.sil', 'Sil')}
                            </Button>
                        )}
                        <Button variant="secondary" onClick={() => setShowEditCariPlanModal(false)}>{t('btn.iptal', 'İptal')}</Button>
                        <Button variant="primary" type="submit" disabled={saving}>
                            {saving ? t('cari.guncelleniyor', 'Güncelleniyor...') : t('btn.guncelle', 'Güncelle')}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Tekli Ödeme Ekle Modal */}
            <Modal show={showTekliOdemeModal} onHide={() => setShowTekliOdemeModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title><i className="fas fa-plus-circle me-2" style={{ color: '#2563eb' }}></i>{t('btn.odemeEkle')}</Modal.Title>
                </Modal.Header>
                <Form onSubmit={(e) => { e.preventDefault(); handleSaveTekliOdeme(); }}>
                    <Modal.Body>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('label.tutar', 'Tutar')} (₺) <span className="text-danger">*</span></Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="0"
                                        value={tekliOdemeFormData.tutar}
                                        onChange={e => setTekliOdemeFormData(p => ({ ...p, tutar: e.target.value }))}
                                        required
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('label.vadeTarihi', 'Vade Tarihi')} <span className="text-danger">*</span></Form.Label>
                                    <TrDateInput
                                        className="form-control"
                                        value={tekliOdemeFormData.vadeTarihi}
                                        onChange={e => setTekliOdemeFormData(p => ({ ...p, vadeTarihi: e.target.value }))}
                                        required
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('label.odemeTuru', 'Ödeme Türü')}</Form.Label>
                                    <Form.Select
                                        value={tekliOdemeFormData.odemeTuru}
                                        onChange={e => setTekliOdemeFormData(p => ({ ...p, odemeTuru: e.target.value }))}
                                    >
                                        <option value="Nakit">Nakit</option>
                                        <option value="Senet">Senet</option>
                                        <option value="Cek">{t('odeme.cek')}</option>
                                        <option value="Takas">Takas</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('label.durum', 'Durum')} <span className="text-danger">*</span></Form.Label>
                                    <Form.Select
                                        value={tekliOdemeFormData.odemeDurumu}
                                        onChange={e => setTekliOdemeFormData(p => ({ ...p, odemeDurumu: e.target.value, kasaID: e.target.value === 'Bekliyor' ? '' : p.kasaID }))}
                                        required
                                    >
                                        <option value="Bekliyor">{t('durum.bekliyor', 'Bekliyor')}</option>
                                        <option value="Odendi">{t('durum.odendi', 'Ödendi')}</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                        </Row>
                        {tekliOdemeFormData.odemeDurumu === 'Odendi' && (
                            <Row>
                                <Col md={12}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>{t('label.kasa', 'Kasa')} <span className="text-danger">*</span></Form.Label>
                                        <Form.Select
                                            value={tekliOdemeFormData.kasaID}
                                            onChange={e => setTekliOdemeFormData(p => ({ ...p, kasaID: e.target.value }))}
                                            required
                                        >
                                            <option value="">{t('sozlesmeDetay.kasaSecin', 'Kasa seçin')}</option>
                                            {kasalar.map(kasa => (
                                                <option key={kasa.kasaID} value={kasa.kasaID}>
                                                    {kasa.kasaAdi} ({kasa.kasaTuru === 'Nakit' ? '💵' : '🏦'})
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                            </Row>
                        )}
                        <Row>
                            <Col md={12}>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('label.aciklama')}</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={2}
                                        placeholder={t('label.aciklama')}
                                        value={tekliOdemeFormData.aciklama}
                                        onChange={e => setTekliOdemeFormData(p => ({ ...p, aciklama: e.target.value }))}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowTekliOdemeModal(false)}>{t('btn.iptal', 'İptal')}</Button>
                        <Button variant="primary" type="submit" disabled={saving}>
                            {saving ? t('msg.kaydediliyor', 'Kaydediliyor...') : t('btn.kaydet', 'Kaydet')}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Toplu Ödeme Planla Modal */}
            <Modal show={showTopluPlanlaModal} onHide={() => setShowTopluPlanlaModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title><i className="fas fa-calendar-alt me-2" style={{ color: '#059669' }}></i>{t('cari.topluOdemePlanla', 'Toplu Ödeme Planla')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>{t('sozlesmeDetay.taksitlendirilecekTutar', 'Toplam Tutar')} (₺) *</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="0"
                                    value={topluPlanlaFormData.toplamTutar}
                                    onChange={e => {
                                        const v = formatMoneyInput(e.target.value);
                                        setTopluPlanlaFormData(prev => ({ ...prev, toplamTutar: v }));
                                    }}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>{t('sozlesmeDetay.taksitSayisi', 'Taksit Sayısı')} *</Form.Label>
                                <Form.Control
                                    type="number"
                                    min={1}
                                    placeholder={t('label.ara')}
                                    value={topluPlanlaFormData.taksitSayisi}
                                    onChange={e => setTopluPlanlaFormData(prev => ({ ...prev, taksitSayisi: e.target.value }))}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>{t('sozlesmeDetay.baslamaTarihi', 'Başlama Tarihi')} *</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="gg.aa.yyyy"
                                    maxLength={10}
                                    value={topluPlanlaFormData.baslamaTarihi}
                                    onChange={e => setTopluPlanlaFormData(prev => ({ ...prev, baslamaTarihi: maskTRDate(e.target.value) }))}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>{t('cari.taksitAralikAy', 'Taksit Aralığı (Ay)')} *</Form.Label>
                                <Form.Control
                                    type="number"
                                    min={1}
                                    placeholder={t('label.ara')}
                                    value={topluPlanlaFormData.aralikAy}
                                    onChange={e => setTopluPlanlaFormData(prev => ({ ...prev, aralikAy: e.target.value }))}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>{t('label.odemeTuru', 'Ödeme Türü')} *</Form.Label>
                                <Form.Select
                                    value={topluPlanlaFormData.odemeTuru}
                                    onChange={e => setTopluPlanlaFormData(prev => ({ ...prev, odemeTuru: e.target.value }))}
                                >
                                    <option value="Nakit">Nakit</option>
                                    <option value="Senet">Senet</option>
                                    <option value="Cek">{t('odeme.cek')}</option>
                                    <option value="Takas">Takas</option>
                                </Form.Select>
                            </Form.Group>
                        </Col>

                        <Col md={12}>
                            <Form.Group className="mb-3">
                                <Form.Label>{t('label.proje', 'İlgili Proje')}</Form.Label>
                                <Form.Select
                                    value={topluPlanlaFormData.projeID}
                                    onChange={e => setTopluPlanlaFormData(prev => ({ ...prev, projeID: e.target.value }))}
                                >
                                    <option value="">{t('cari.projeOpsiyonel', '— Proje seçin (isteğe bağlı) —')}</option>
                                    {projeler.map(p => (
                                        <option key={p.projeID} value={String(p.projeID)}>{p.projeAdi}</option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={12}>
                            <Form.Group className="mb-3">
                                <Form.Label>{t('label.aciklama')}</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder={t('label.aciklama')}
                                    value={topluPlanlaFormData.aciklama}
                                    onChange={e => setTopluPlanlaFormData(prev => ({ ...prev, aciklama: e.target.value }))}
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                    {topluPlanlaFormData.toplamTutar && topluPlanlaFormData.taksitSayisi && parseInt(topluPlanlaFormData.taksitSayisi) > 0 && (
                        <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '12px 16px', fontSize: 14, color: '#166534' }}>
                            <i className="fas fa-info-circle me-2"></i>
                            <strong>{topluPlanlaFormData.taksitSayisi}</strong> taksit &times;&nbsp;
                            <strong>
                                {new Intl.NumberFormat('tr-TR').format(
                                    Math.round((parseFloat(parseMoneyInput(topluPlanlaFormData.toplamTutar)) / parseInt(topluPlanlaFormData.taksitSayisi)) * 100) / 100
                                )} ₺
                            </strong> = toplam&nbsp;
                            <strong>{topluPlanlaFormData.toplamTutar} ₺</strong> oluşturulacak.
                            {parseInt(topluPlanlaFormData.aralikAy) > 1 && ` Her taksit ${topluPlanlaFormData.aralikAy} ayda bir.`}
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowTopluPlanlaModal(false)}>{t('btn.iptal', 'İptal')}</Button>
                    <Button variant="success" onClick={handleSaveTopluPlanla} disabled={saving}>
                        {saving ? <><i className="fas fa-spinner fa-spin me-1"></i>{t('sozlesmeDetay.olusturuluyor', 'Oluşturuluyor...')}</> : <><i className="fas fa-check me-1"></i>{t('sozlesmeDetay.taksitleriOlustur', 'Taksitleri Oluştur')}</>}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Ödeme Yap Modal */}
            <Modal show={showOdemeYapModal} onHide={() => { setShowOdemeYapModal(false); setOdemeYapTaksit(null); }}>
                <Modal.Header closeButton>
                    <Modal.Title><i className="fas fa-file-invoice-dollar me-2" style={{ color: '#059669' }}></i>{t('cari.taksitOdemesiYap', 'Taksit Ödemesi Yap')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {odemeYapTaksit && (
                        <>
                            <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', padding: '0.875rem 1rem', marginBottom: '1.25rem', fontSize: '0.875rem', color: '#166534' }}>
                                <strong>{odemeYapTaksit.aciklama}</strong> &mdash; {formatCurrency(odemeYapTaksit.miktar)} TL
                            </div>
                            <Form.Group className="mb-3">
                                <Form.Label>{t('cari.odemeTarihi', 'Ödeme Tarihi')} *</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="gg.aa.yyyy"
                                    value={odemeYapTarihDisplay}
                                    onChange={e => {
                                        const masked = maskTRDate(e.target.value);
                                        setOdemeYapTarihDisplay(masked);
                                        const iso = fromTRDate(masked);
                                        if (iso) setOdemeYapFormData(prev => ({ ...prev, tarih: iso }));
                                    }}
                                    maxLength={10}
                                />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>{t('rapor.kasaBankaHesabi', 'Kasa / Banka')} *</Form.Label>
                                <Form.Select
                                    value={odemeYapFormData.kasaID}
                                    onChange={e => setOdemeYapFormData(prev => ({ ...prev, kasaID: e.target.value }))}
                                >
                                    <option value="">— Seçin —</option>
                                    {kasalar.map(k => (
                                        <option key={k.kasaID} value={String(k.kasaID)}>{k.kasaAdi}</option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => { setShowOdemeYapModal(false); setOdemeYapTaksit(null); }}>{t('btn.iptal', 'İptal')}</Button>
                    <Button variant="success" onClick={handleSaveOdemeYap} disabled={saving || !odemeYapFormData.kasaID}>
                        {saving ? <><i className="fas fa-spinner fa-spin me-1"></i>{t('msg.kaydediliyor', 'Kaydediliyor...')}</> : <><i className="fas fa-check me-1"></i>{t('cari.odeyiOnayla', 'Ödemeyi Onayla')}</>}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Ayrıntılar Modal */}
            <Modal show={showAyrintilarModal} onHide={() => setShowAyrintilarModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>{t('cari.ayrintilar', 'Cari Ayrıntıları')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {cari && <dl className="row">
                        <dt className="col-sm-4">{t('label.telefon', 'Telefon')} 2</dt>
                        <dd className="col-sm-8">{cari.telefon2 ? formatPhoneNumber(cari.telefon2) : '-'}</dd>

                        <dt className="col-sm-4">{t('label.adres', 'Adres')}</dt>
                        <dd className="col-sm-8">{cari.adres || '-'}</dd>

                        <dt className="col-sm-4">{t('label.tc', 'TC')}</dt>
                        <dd className="col-sm-8">{cari.tc || '-'}</dd>

                        <dt className="col-sm-4">{t('label.vergiDairesi', 'Vergi Dairesi')}</dt>
                        <dd className="col-sm-8">{cari.cariVD || '-'}</dd>

                        <dt className="col-sm-4">{t('label.vergiNo', 'Vergi No')}</dt>
                        <dd className="col-sm-8">{cari.cariVN || '-'}</dd>

                        <dt className="col-sm-4">{t('label.takipNo', 'Takip No')}</dt>
                        <dd className="col-sm-8">{cari.takipNo}</dd>

                        <dt className="col-sm-4">{t('label.eklemeTarihi', 'Eklenme Tarihi')}</dt>
                        <dd className="col-sm-8">{formatDate(cari.eklenmeTarihi)}</dd>

                        <dt className="col-sm-4">{t('label.durum', 'Durum')}</dt>
                        <dd className="col-sm-8">
                            <span className={`badge badge-${cari.aktifMi ? 'success' : 'danger'}`}>
                                {cari.aktifMi ? t('durum.aktif', 'Aktif') : t('durum.pasif', 'Pasif')}
                            </span>
                        </dd>
                    </dl>}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowAyrintilarModal(false)}>
                        {t('btn.kapat', 'Kapat')}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Makbuz + Eşleştirme Modal */}
            <Modal show={showMakbuzModal} onHide={() => setShowMakbuzModal(false)} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <span style={{ width: 30, height: 30, borderRadius: 8, background: makbuzModu === 'tahsilat' ? '#dcfce7' : '#fee2e2', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                                <i className={`fas fa-arrow-${makbuzModu === 'tahsilat' ? 'down' : 'up'}`} style={{ color: makbuzModu === 'tahsilat' ? '#16a34a' : '#dc2626', fontSize: 12 }}></i>
                            </span>
                            {makbuzModu === 'tahsilat' ? t('sozlesmeDetay.tahsilatMakbuzu', 'Tahsilat Makbuzu') : t('cari.odemeMakbuzu', 'Ödeme Makbuzu')}
                            <small style={{ fontSize: 12, color: '#94a3b8', fontWeight: 400 }}>
                                {makbuzStep === 1 ? `— ${t('cari.adim1MakbuzFormu', 'Adım 1: Makbuz Formu')}` : `— ${t('cari.adim2FaturaEslestirme', 'Adım 2: Fatura Eşleştirme')}`}
                            </small>
                        </div>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {makbuzStep === 1 && (
                        <div>
                            <div style={{ padding: '8px 12px', background: '#f8fafc', borderRadius: 9, border: '1px solid #f1f5f9', fontSize: 12.5, color: '#475569', marginBottom: 16 }}>
                                <strong>{cari?.adiSoyadi}</strong> için bağımsız {makbuzModu === 'tahsilat' ? 'tahsilat' : 'ödeme'} makbuzu oluşturun.
                                Kasa anında güncellenir; ardından açık faturalara eşleştirebilirsiniz.
                            </div>
                            <Row>
                                <Col md={12} className="mb-3">
                                    <Form.Label style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Tutar (₺) *</Form.Label>
                                    <Form.Control type="text" placeholder="0" style={{ fontSize: 16, fontWeight: 700, textAlign: 'right' }}
                                        value={makbuzCDFormData.miktar}
                                        onChange={e => setMakbuzCDFormData(prev => ({ ...prev, miktar: formatMoneyInput(e.target.value) }))}
                                    />
                                </Col>
                            </Row>
                            <Row>
                                <Col md={6} className="mb-3">
                                    <Form.Label style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Tarih *</Form.Label>
                                    <TrDateInput className="form-control" value={makbuzCDFormData.tarih}
                                        onChange={e => setMakbuzCDFormData(prev => ({ ...prev, tarih: e.target.value }))}
                                    />
                                </Col>
                                <Col md={6} className="mb-3">
                                    <Form.Label style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Ödeme Türü</Form.Label>
                                    <Form.Select value={makbuzCDFormData.odemeTuru}
                                        onChange={e => setMakbuzCDFormData(prev => ({ ...prev, odemeTuru: e.target.value }))}>
                                        <option value="Nakit">Nakit</option>
                                        <option value="Çek">{t('odeme.cek')}</option>
                                        <option value="Havale/EFT">Havale/EFT</option>
                                        <option value="Kredi Kartı">{t('odeme.krediKarti')}</option>
                                    </Form.Select>
                                </Col>
                            </Row>
                            <Row>
                                <Col md={12} className="mb-3">
                                    <Form.Label style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Kasa *</Form.Label>
                                    <Form.Select value={makbuzCDFormData.kasaID}
                                        onChange={e => setMakbuzCDFormData(prev => ({ ...prev, kasaID: e.target.value }))}>
                                        <option value="">Kasa seçin...</option>
                                        {kasalar.map(k => (
                                            <option key={k.kasaID} value={k.kasaID}>{k.kasaAdi} ({k.kasaTuru === 'Nakit' ? '💵' : '🏦'})</option>
                                        ))}
                                    </Form.Select>
                                </Col>
                            </Row>
                            <Row>
                                <Col md={12} className="mb-1">
                                    <Form.Label style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.4px' }}>{t('label.aciklama')}</Form.Label>
                                    <Form.Control as="textarea" rows={2} placeholder={t('label.ara')}
                                        value={makbuzCDFormData.aciklama}
                                        onChange={e => setMakbuzCDFormData(prev => ({ ...prev, aciklama: e.target.value }))}
                                    />
                                </Col>
                            </Row>
                        </div>
                    )}
                    {makbuzStep === 2 && (() => {
                        const makbuzTutar = parseFloat(parseMoneyInput(makbuzCDFormData.miktar)) || 0;
                        const dagitilanToplam = acikFaturalar.reduce((sum, f) => {
                            const key = `${f.tip}-${f.id}`;
                            return sum + (parseFloat(parseMoneyInput(eslesmeTutarlari[key] || '')) || 0);
                        }, 0);
                        const kalanDagitimsiz = makbuzTutar - dagitilanToplam;
                        return (
                            <div>
                                <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
                                    <div style={{ flex: 1, background: '#f0fdf4', border: '1.5px solid #bbf7d0', borderRadius: 10, padding: '10px 14px' }}>
                                        <div style={{ fontSize: 10.5, fontWeight: 700, color: '#15803d', textTransform: 'uppercase' }}>✓ Makbuz Kaydedildi</div>
                                        <div style={{ fontSize: 18, fontWeight: 800, color: '#166534', marginTop: 4 }}>{makbuzTutar.toLocaleString('tr-TR')} ₺</div>
                                        <div style={{ fontSize: 11, color: '#4ade80', marginTop: 2 }}>{makbuzCDFormData.odemeTuru} · {makbuzCDFormData.tarih}</div>
                                    </div>
                                    <div style={{ flex: 1, background: kalanDagitimsiz < 0 ? '#fef2f2' : kalanDagitimsiz === 0 ? '#f0fdf4' : '#fff7ed', border: `1.5px solid ${kalanDagitimsiz < 0 ? '#fecaca' : kalanDagitimsiz === 0 ? '#bbf7d0' : '#fed7aa'}`, borderRadius: 10, padding: '10px 14px' }}>
                                        <div style={{ fontSize: 10.5, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>Dağıtılmayan Bakiye</div>
                                        <div style={{ fontSize: 18, fontWeight: 800, color: kalanDagitimsiz < 0 ? '#dc2626' : kalanDagitimsiz === 0 ? '#16a34a' : '#c05621', marginTop: 4 }}>{kalanDagitimsiz.toLocaleString('tr-TR')} ₺</div>
                                        <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{kalanDagitimsiz < 0 ? '⚠ Tutar aşıldı' : kalanDagitimsiz === 0 ? '✓ Tümü dağıtıldı' : 'Eşleştirilmedi'}</div>
                                    </div>
                                </div>
                                {acikFaturalar.length === 0 ? (
                                    <div style={{ textAlign: 'center', padding: '28px', color: '#94a3b8', fontSize: 13, background: '#f8fafc', borderRadius: 10, border: '1.5px solid #f1f5f9' }}>
                                        <i className="fas fa-check-circle" style={{ fontSize: 24, display: 'block', marginBottom: 8, color: '#22c55e' }}></i>
                                        Bu cariye ait açık fatura / sözleşme bulunamadı.<br />
                                        <span style={{ fontSize: 11, color: '#cbd5e1' }}>Makbuz kaydedildi, eşleştirmesiz devam edebilirsiniz.</span>
                                    </div>
                                ) : (
                                    <>
                                        <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>
                                            Açık Faturalar / Sözleşmeler ({acikFaturalar.length})
                                        </div>
                                        <div style={{ maxHeight: 300, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6 }}>
                                            {acikFaturalar.map(fatura => {
                                                const key = `${fatura.tip}-${fatura.id}`;
                                                const girilmis = eslesmeTutarlari[key] || '';
                                                const girilmisNum = parseFloat(parseMoneyInput(girilmis)) || 0;
                                                return (
                                                    <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 10, border: `1.5px solid ${girilmisNum > 0 ? '#bbf7d0' : '#f1f5f9'}`, background: girilmisNum > 0 ? '#f0fdf4' : '#fff' }}>
                                                        <div style={{ flex: 1, minWidth: 0 }}>
                                                            <div style={{ fontSize: 12.5, fontWeight: 600, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{fatura.aciklama}</div>
                                                            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2, display: 'flex', gap: 8 }}>
                                                                <span>Toplam: {fatura.tutarToplam.toLocaleString('tr-TR')} ₺</span>
                                                                <span style={{ color: '#dc2626', fontWeight: 600 }}>Kalan: {fatura.kalanTutar.toLocaleString('tr-TR')} ₺</span>
                                                            </div>
                                                        </div>
                                                        <Form.Control type="text" style={{ width: 100, textAlign: 'right', fontWeight: 700, flexShrink: 0 }}
                                                            placeholder="0" value={girilmis}
                                                            onChange={e => setEslesmeTutarlari(prev => ({ ...prev, [key]: formatMoneyInput(e.target.value) }))}
                                                        />
                                                        <button title={t('btn.kapat')}
                                                            onClick={() => setEslesmeTutarlari(prev => ({ ...prev, [key]: formatMoneyInput(String(Math.round(fatura.kalanTutar))) }))}
                                                            style={{ width: 28, height: 28, borderRadius: 7, border: '1.5px solid #e2e8f0', background: '#f8fafc', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
                                                        >
                                                            <Check size={13} color="#64748b" />
                                                        </button>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </>
                                )}
                            </div>
                        );
                    })()}
                </Modal.Body>
                <Modal.Footer>
                    {makbuzStep === 2 ? (
                        <>
                            <Button variant="secondary" onClick={() => { setShowMakbuzModal(false); fetchOdemeler(); }}>
                                Eşleştirmesiz Kapat
                            </Button>
                            {acikFaturalar.length > 0 && (
                                <Button variant="success" onClick={handleEslesmeKaydetCD} disabled={eslesmeSaving}>
                                    {eslesmeSaving ? 'Kaydediliyor...' : 'Eşleştirmeleri Kaydet'}
                                </Button>
                            )}
                        </>
                    ) : (
                        <>
                            <Button variant="secondary" onClick={() => setShowMakbuzModal(false)}>İptal</Button>
                            <Button variant="primary" onClick={handleMakbuzFormSaveCD} disabled={makbuzCDSaving}>
                                {makbuzCDSaving ? 'Kaydediliyor...' : 'Makbuzu Kaydet ->'}
                            </Button>
                        </>
                    )}
                </Modal.Footer>
            </Modal>

            {iysModal && cari?.cariID && (
                <IysIzinModal
                    cariID={cari.cariID}
                    iletisimTipi={iysModal.iletisimTipi}
                    iletisimVerisi={iysModal.iletisimVerisi}
                    onClose={() => setIysModal(null)}
                    onSuccess={async () => {
                        setIysModal(null);
                        try {
                            const r = await axiosInstance.get(`/api/CariIysIzinleri/cari/${cari.cariID}`);
                            setIysIzinler(r.data);
                        } catch {}
                    }}
                />
            )}
            {showDegisiklikGecmisi && id && id !== 'yeni' && id !== '0' && (
                <CariDegisiklikGecmisi cariID={Number(id)} onClose={() => setShowDegisiklikGecmisi(false)} />
            )}
        </div>
    );
};

export default CariDetay;
